import React, { useCallback, useEffect, useRef, useState } from "react";
import { ListChecks, Scale, FileText, FileSpreadsheet, Receipt, Send, Eye, Loader2, RefreshCw, Pencil, CheckCircle2, Clock, AlertTriangle, Upload, BellRing, Banknote, History, Split, Download, Calculator, Lock, Check, Trash2, Printer, Gift, Mail, Smartphone, Globe, Monitor, Cog, UserRound, Ticket, CalendarClock } from "lucide-react";
import { api, argent, ouvrirPdf, telecharger } from "./api";
import { useConfirmation } from "./Confirmation";
import { CarteIcone, Section, Tableau, Cellule, Badge, Case, BarreSelection, BoutonSombre, Puces, Modale, ChampTexte, ChampChoix, Recherche, EnTeteMenu } from "./ui";
import ImportReleve from "./Encaissements";
import { SelecteurSaison, BandeauSaisonClose } from "./SelecteurSaison";
import EcartsPaiement, { AVerifierEcarts } from "./EcartsPaiement";
import VueCash, { ModaleEncaisser, ModaleEchelonnerCash } from "./Caisse";
import CartesDix from "./CartesDix";
import FacturesPrivees from "./FacturesPrivees";
import { useChantiers } from "../lib/useChantiers";
import { useProgression, quelquesNoms, raisonErreur } from "./Progression";

const PAQUET = 20;

export const STATUTS = {
  a_generer: { label: "À générer", ton: "bleu" },
  generee: { label: "À envoyer", ton: "primaire" },
  envoyee: { label: "En attente de paiement", ton: "ambre" },
  partielle: { label: "Partiel", ton: "ambre" },
  payee: { label: "Payée", ton: "vert" },
  especes: { label: "Cash / espèces", ton: "ambre" },
  non_facturee: { label: "Non facturée", ton: "bleu" },
  annulee: { label: "Annulée", ton: "bleu" },
  bloquee: { label: "À corriger", ton: "rouge" },
};

export const BadgeStatut = ({ statut }) => {
  const s = STATUTS[statut] || STATUTS.a_generer;
  return <Badge ton={s.ton === "primaire" ? "vert" : s.ton}>{s.label}</Badge>;
};

/* Ce que la console est en train de faire, dit en clair : une correction enchaîne un
   recalcul, une écriture et la fabrication d'un PDF — plusieurs secondes de silence. */
const LIBELLES_OCCUPE = {
  correction: "Correction en cours — la facture est recalculée puis refaite…",
  generation: "Fabrication de la facture en cours…",
  envoi: "Envoi de la facture en cours…",
  relance: "Relances en cours…",
  especes: "Encaissements en espèces en cours…",
  remise: "Remise dans « À générer » en cours…",
  echeance: "Mise à jour des échéances…",
  // Après le bilan d'un envoi ou d'une relance : la liste se recharge.
  actualisation: "Mise à jour de la liste…",
};

function BarreAvancement({ texte }) {
  return (
    <div className="fixed left-0 right-0 top-0 z-[70] pointer-events-none"
      data-testid="fact-avancement" role="status" aria-live="polite">
      <div className="h-[3px] overflow-hidden" style={{ background: "rgba(15,23,42,.08)" }}>
        <div className="h-full w-1/3 barre-avancement"
          style={{ background: "linear-gradient(90deg, transparent, #0F766E, transparent)" }} />
      </div>
      <div className="flex justify-center">
        <span className="mt-3 text-xs px-3.5 py-2 rounded-full shadow-lg flex items-center gap-2"
          style={{ background: "#0F172A", color: "#F8FAFC" }}>
          <Loader2 size={13} className="animate-spin" /> {texte}
        </span>
      </div>
    </div>
  );
}

const VUES = [
  { id: "a_generer", label: "À générer" },
  /* Une SEULE liste de corrections : factures bloquées, montants changés après coup et
     factures corrigées depuis leur envoi. Deux listes séparées (« à recalculer »,
     « corrigées, à refaire ») faisaient croire à deux chantiers et laissaient des
     factures dans les deux à la fois. Juste après « À générer » (Jen, 02.11.2026) : une
     facture à corriger est une facture à refaire. */
  { id: "bloquee", label: "À corriger" },
  { id: "generee", label: "Générées" },
  { id: "envoyee", label: "En attente de paiement" },
  /* Les seules familles à relancer aujourd'hui, décidées par le SERVEUR (relances.py) :
     en retard de paiement, ni échelonnées à jour, ni en espèces, ni relancées il y a peu.
     L'alerte menait à « En attente de paiement » : 103 lignes pour 4 à relancer. */
  { id: "a_relancer", label: "À relancer" },
  /* « Cash / espèces » n'est plus ici (20.10.2026) : ce n'est pas un état de facture mais
     un autre circuit, le comptoir — cartes 10 cours et workshops compris, qui ne sont
     jamais des factures. Il a son écran, à côté des parrainages (Caisse.jsx). */
  { id: "payee", label: "Payées" },
  { id: "non_facturee", label: "Non facturées" },
  /* Tous les paiements échelonnés d'un coup : l'école devait sinon deviner lesquels,
     écran par écran, pour savoir qui a un accord en cours. */
  { id: "echelonnes", label: "Paiements échelonnés" },
  { id: "ecarts", label: "Écarts de paiement" },
  { id: "tous", label: "Toutes" },
];
/* Le nom d'une liste de travail, dit à l'école après une correction (« Elle attend dans … »).
   Cash / espèces est un écran à part, pas une vue : sans lui, une facture validée au
   comptoir était annoncée « dans À générer ». */
const nomListe = (id) => (id === "especes" ? "Cash / espèces"
  : (VUES.find((v) => v.id === id) || {}).label || "");

/* « À relancer » : c'est le SERVEUR qui décide, facture par facture (console/relances.py),
   et l'écran ne fait que l'afficher. Un paiement échelonné à jour, les espèces, une facture
   à corriger, une famille relancée il y a peu ou pas encore échue n'est jamais à relancer —
   sans filtre à penser à cocher (école, 09.10.2026 : « si l'école oublie de cocher « écarter
   les échelonnés », on envoie un rappel à quelqu'un qui est à jour »). */
const aRelancer = (f) => !!f.relance?.due;
/* Pas de rappel prévu aujourd'hui, mais l'école peut l'envoyer quand même à CETTE famille,
   en le voulant (facture perdue, accord à rappeler…) : jamais pour une famille payée, en
   espèces, à corriger ou déjà relancée aujourd'hui. */
const relanceALaMain = (f) => !f.relance?.due && !!f.relance?.forcable;
/* Ce que la ligne dit de la relance : les factures parties et pas soldées seulement (une
   facture payée ou pas encore envoyée a déjà son statut). */
const MOTIFS_MUETS = ["payee", "pas_envoyee", "annulee"];
const motifVisible = (f) => !!f.relance?.badge && !MOTIFS_MUETS.includes(f.relance.code);
/* « Renvoyer la facture » (« je ne l'ai pas reçue », « je l'ai perdue ») : une facture partie
   et pas soldée — la même règle que le serveur (console/renvoi_facture.py). */
const RENVOYABLES = ["pas_echue", "echelonne_a_jour", "relancee_recemment", "report", "en_retard", "partiel", "mensualite"];
const renvoyable = (f) => f.liste === "envoyee" && !!(f.numero || f.pdf_numero)
  && RENVOYABLES.includes(f.relance?.code);

/* « 11 échelonné(s) à jour · 4 pas encore échue(s) » : pourquoi les autres factures en
   attente ne reçoivent pas de rappel aujourd'hui. */
const pourquoiPas = (factures) => {
  const par = {};
  factures.forEach((f) => {
    const g = f.relance?.groupe || "autre raison";
    par[g] = (par[g] || 0) + 1;
  });
  return Object.entries(par).sort((a, b) => b[1] - a[1]).map(([g, n]) => `${n} ${g}`);
};

/* La liste de travail d'une facture de stage : payée en partie, elle attend le reste avec
   les factures « En attente de paiement ». */
const listeDuStage = (l) => (l.statut === "partielle" ? "envoyee" : (l.statut || "a_generer"));

/* Les vues où une action de lot existe vraiment. Ailleurs, les cases à cocher ne servaient
   à rien et laissaient croire qu'une action était possible. */
/* Les écrans à part, rangés à côté des genres de factures : ni cartes de factures, ni filtres
   de la liste au-dessus d'eux. */
// Cartes 10 cours : à côté des parrainages (Jen, 31.10.2026 : « pas de sens dans Élèves »).
// Cours privés : les factures d'annulation tardive (Jen, 01.11.2026), là où le chantier est ouvert.
const ECRANS_A_PART = ["parrainages", "especes", "cartes10", "prives"];

const VUES_AVEC_LOT = ["a_generer", "generee", "envoyee", "a_relancer", "especes", "tous"];

/* Titulaire du bulletin de versement, en clair. Le nom du compte bancaire
   (« VANEY NOEMIE ») reste celui imprimé sur la facture. */
const LIBELLE_BV = { noemie: "BV Noémie" };

const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");

const enNombre = (v) => (v === "" || v === null ? 0 : Number(String(v).replace(",", ".")) || 0);

/* « − 1'196.00 CHF » : une ligne de décompte, au centime et avec son signe, écrite comme
   sur la facture PDF (le prorata d'un stage tombe rarement sur un franc rond). */
const chfSigne = (v) => {
  const [entier, centimes] = Math.abs(v || 0).toFixed(2).split(".");
  return `${v < -0.005 ? "− " : ""}${entier.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${centimes} CHF`;
};

/* Ce qui demande l'attention de l'école, en haut de la page : sinon personne n'allait
   ouvrir « À corriger » ni « Cash / espèces » dans la liste des vues. */
const AlertesFacturation = ({ base, nbEspeces, onAller, onReleve }) => {
  const nb = (filtre) => base.filter(filtre).length;
  const alertes = [
    /* Une seule ligne : « à recalculer », « bloquée » et « corrigée depuis l'envoi »
       atterrissent dans la même vue « À corriger ». Plusieurs alertes pour un même geste
       faisaient croire à plusieurs chantiers. */
    { id: "bloquee", nb: nb((f) => f.liste === "bloquee"), vue: "bloquee",
      ton: "rouge", texte: (n) => `${n} facture(s) à corriger` },
    /* Le même nombre que la puce « Cash / espèces » et qu'À traiter : la liste commune du
       serveur (factures en espèces, stages, cartes 10 cours, workshops, saisons terminées). */
    { id: "especes", nb: nbEspeces, vue: "especes", ton: "ambre",
      texte: (n) => `${n} paiement(s) en espèces à confirmer` },
    /* La liste de travail (`liste`) est la seule vérité : une facture prête mais pas
       partie peut porter un autre statut (acompte déjà reçu, par exemple) et se compter
       quand même dans « Générées, à envoyer ». En lisant `statut`, cette alerte annonçait
       7 factures là où l'onglet en montrait 14. */
    { id: "generee", nb: nb((f) => f.liste === "generee"), vue: "generee", ton: "ambre",
      texte: (n) => `${n} facture(s) générée(s) qui attendent d'être envoyées` },
    { id: "relance", nb: nb(aRelancer), vue: "a_relancer",
      ton: "ambre", texte: (n) => `${n} famille(s) en retard de paiement, à relancer` },
  ].filter((a) => a.nb > 0);
  if (!alertes.length) return null;
  return (
    <div className="rounded-2xl px-5 py-4" data-testid="facturation-alertes"
      style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#92400E" }}>
        <AlertTriangle size={16} style={{ color: "#B45309" }} /> À ton attention
      </div>
      {/* Une puce par point : on lit la liste d'un coup d'œil au lieu d'un bloc de liens. */}
      <ul className="mt-2 space-y-1 list-disc pl-5 marker:text-[#B45309]">
        {alertes.map((a) => (
          <li key={a.id}>
            <button className="text-sm underline decoration-dotted text-left"
              data-testid={`facturation-alerte-${a.id}`}
              style={{ color: a.ton === "rouge" ? "#B91C1C" : "#92400E" }}
              onClick={() => onAller(a.vue)}>
              {a.texte(a.nb)}
            </button>
          </li>
        ))}
        <li>
          <button className="text-sm underline decoration-dotted" data-testid="facturation-alerte-releve"
            style={{ color: "#92400E" }} onClick={onReleve}>
            Importer le relevé bancaire pour pointer les paiements
          </button>
        </li>
      </ul>
    </div>
  );
};

/* Les parrainages du semestre : qui a parrainé qui, pour combien, où le bon est passé et
   où en est la facture du parrain. Le montant affiché est TOUJOURS celui réellement
   appliqué (25 CHF quand le bon est réduit) : l'école s'en sert pour répondre aux familles. */
const FILTRES_PARRAINAGE = [
  { valeur: "tous", label: "Tous" },
  { valeur: "applique", label: "Appliqué automatiquement" },
  { valeur: "deja_deduit", label: "Déjà déduit" },
  { valeur: "prevu", label: "Sur une facture à envoyer" },
  { valeur: "a_deduire", label: "À déduire" },
  { valeur: "reporte", label: "Reporté" },
  { valeur: "a_clarifier", label: "À clarifier" },
  { valeur: "ecarte", label: "Écarté" },
];

const TON_ETAT = {
  applique: "vert", deja_deduit: "vert", prevu: "ambre", a_deduire: "ambre",
  reporte: "ambre", a_clarifier: "rouge", ecarte: "bleu",
};

function Parrainages({ semestre, saison }) {
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState("tous");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [occupe, setOccupe] = useState("");
  const charger = useCallback((vider = true) => {
    if (vider) setData(null);
    api(`/facturation/parrainages?semestre=${semestre}${saison ? `&saison=${saison}` : ""}`).then(setData).catch((e) => setErr(e.message));
  }, [semestre, saison]);
  useEffect(() => { charger(); }, [charger]);
  const basculerCadeau = async (l) => {
    setOccupe(l.parrainage_id);
    try {
      await api(`/parrainages/${encodeURIComponent(l.parrainage_id)}/cadeau`,
        { method: "POST", body: JSON.stringify({ remis: !l.cadeau_remis }) });
      charger(false);
    } finally { setOccupe(""); }
  };
  if (err) return <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>;
  if (!data) return <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Lecture des parrainages…</p>;

  const cherche = q.trim().toLowerCase();
  const nb = (e) => data.lignes.filter((l) => l.etat === e).length;
  const lignes = data.lignes.filter((l) => (etat === "tous" || l.etat === etat)
    && (!cherche || `${l.parrain} ${l.filleul} ${l.client} ${l.facture}`.toLowerCase().includes(cherche)));

  return (
    <div className="space-y-4" data-testid="liste-parrainages">
      {/* Les trois états d'un parrainage reconnu s'additionnent : déjà déduit + à déduire
          + reporté = reconnus. Les écartés sont à part, ils ne comptent pas. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[["Parrainages reconnus", data.nb_reconnus,
           `${data.nb_appliques} déduits · ${data.nb_en_attente} à déduire · ${data.nb_reportes} reportés`],
          // Déduit = sur une facture ENVOYÉE (règle de l'école) ; avant l'envoi, le bon
          // compte « à déduire », même s'il est déjà sur la facture.
          ["Déjà déduits (factures envoyées)", data.nb_appliques, argent(data.montant_applique)],
          ["À déduire", data.nb_en_attente, `${argent(data.montant_en_attente)}${data.nb_prevus
            ? ` · dont ${data.nb_prevus} sur une facture à envoyer` : ""}`],
          ["Reportés au semestre suivant", data.nb_reportes, argent(data.montant_reporte)],
          ["Écartés (hors compte)", data.nb_ecartes, data.nb_a_clarifier
            ? `${data.nb_a_clarifier} à clarifier dans « À traiter »` : "0 à clarifier"]]
          .map(([titre, valeur, detail]) => (
            <div key={titre} className="rounded-xl border p-3 bg-white" style={{ borderColor: "var(--border)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{titre}</div>
              <div className="font-display text-lg mt-0.5">{valeur}</div>
              {detail && <div className="text-[11px] text-[var(--muted)]">{detail}</div>}
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Puces testid="parrainages-filtre" valeur={etat}
          options={FILTRES_PARRAINAGE.map((f) => ({ ...f,
            nb: f.valeur === "tous" ? data.lignes.length : nb(f.valeur) }))}
          onChange={setEtat} />
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[var(--muted)]" data-testid="parrainages-cadeaux-compteur">
            <Gift size={12} className="inline mr-1" style={{ color: "var(--primary)" }} />
            {data.nb_cadeaux_remis} cadeau(x) remis sur {data.nb_reconnus}
          </span>
          <button className="btn-ghost" data-testid="parrainages-imprimer"
            onClick={() => ouvrirPdf("/parrainages/liste/pdf", "Parrainages à imprimer")}>
            <Printer size={13} /> Imprimer
          </button>
          <Recherche valeur={q} onChange={setQ} largeur={260} testid="parrainages-recherche"
            placeholder="Chercher un parrain, un filleul…" />
        </div>
      </div>

      <Tableau
        testid="tableau-parrainages"
        fixe
        entetes={[{ label: "Parrain", largeur: "18%" }, { label: "Filleul", largeur: "16%" },
          { label: "Semestre", largeur: "9%" },
          { label: "Montant appliqué", droite: true, largeur: "12%" },
          { label: "État", largeur: "17%" }, { label: "Facture du parrain", largeur: "17%" },
          { label: "Cadeau reçu", largeur: "11%" }]}
        lignes={lignes.map((l, i) => ({ ...l, parrainage_id: l.id, id: `${l.id}-${i}` }))}
        vide="Aucun parrainage dans cette vue."
        rendu={(l) => (
          <>
            <Cellule fort>
              {l.parrain || <span className="text-[var(--muted)]">non reconnu</span>}
              {l.parrain_ecrit && l.parrain_ecrit !== l.parrain && (
                <div className="text-xs font-normal text-[var(--muted)]">écrit : « {l.parrain_ecrit} »</div>
              )}
              {l.a_la_main && <div className="text-[11px] font-normal text-[var(--muted)]">attribué à la main</div>}
            </Cellule>
            <Cellule>{l.filleul}</Cellule>
            <Cellule muet>Semestre {l.semestre_num}</Cellule>
            <Cellule droite fort>
              {l.montant ? argent(l.montant) : "—"}
              {l.reduit && l.montant > 0 && (
                <div className="text-[11px] font-normal" style={{ color: "#B45309" }}>
                  réduit ({argent(l.montant_plein)} plein)
                </div>
              )}
            </Cellule>
            <Cellule>
              <Badge ton={TON_ETAT[l.etat] || "bleu"}>{l.etat_label}</Badge>
              {l.motif && <div className="text-[11px] mt-1" style={{ color: l.a_verifier ? "#B45309" : "var(--muted)" }}>{l.motif}</div>}
            </Cellule>
            <Cellule muet>
              {l.facture ? (
                <>
                  <div className="font-semibold text-[var(--text)]">{l.facture}</div>
                  <div className="text-[11px]">{l.facture_statut_label}</div>
                </>
              ) : l.etat === "a_deduire" ? "sur la prochaine facture du parrain"
                : l.etat === "a_clarifier" ? "en attente de ta décision" : "—"}
              {l.client && <div className="text-[11px]">{l.client}</div>}
            </Cellule>
            {/* Le petit cadeau remis au filleul : coché ici, pour ne pas l'offrir deux fois. */}
            <Cellule>
              <button data-testid={`parrainage-cadeau-${l.parrainage_id}`} disabled={occupe === l.parrainage_id}
                onClick={() => basculerCadeau(l)}
                className="px-3 py-1 rounded-full text-xs font-semibold border transition-colors"
                style={l.cadeau_remis
                  ? { background: "#0F1B2D", color: "#fff", borderColor: "#0F1B2D" }
                  : { background: "#fff", color: "var(--muted)", borderColor: "var(--border)" }}>
                {occupe === l.parrainage_id ? "…" : l.cadeau_remis ? "✓ Oui" : "Non"}
              </button>
              {l.cadeau_remis && l.cadeau_le && (
                <div className="text-[11px] text-[var(--muted)] mt-1">le {l.cadeau_le}</div>
              )}
            </Cellule>
          </>
        )}
      />
      <p className="text-[11px] text-[var(--muted)]">{data.regle}</p>
    </div>
  );
}


/* Les bons de parrainage sont déduits du total, mais ils ne se voyaient que dans le décompte
   du PDF : l'école croyait donc le parrainage oublié. On l'annonce sur la ligne.
   - déduits : soit par la console, soit déjà passés à la main dans le classeur ;
   - reportés : la facture est déjà partie, le bon ira sur le semestre suivant. */
const MentionParrainage = ({ f }) => {
  const deduits = [...(f.parrainages || []), ...(f.parrainages_neutralises || [])];
  const reportes = f.parrainages_reportes || [];
  if (!deduits.length && !reportes.length) return null;
  const somme = (l) => l.reduce((s, b) => s + Number(b.montant || 0), 0);
  return (
    <div className="text-[11px] font-normal mt-0.5" data-testid="fact-parrainage-mention">
      {deduits.length > 0 && (
        <div style={{ color: "#047857" }} title={deduits.map((b) => `Parrainage de ${b.filleul}`).join(" · ")}>
          parrainage −{somme(deduits)} CHF{deduits.length > 1 ? ` (${deduits.length} bons)` : ""}
        </div>
      )}
      {reportes.length > 0 && (
        <div style={{ color: "#B45309" }}>
          parrainage −{somme(reportes)} CHF à créditer au semestre suivant
        </div>
      )}
    </div>
  );
};

/* « [saison::]semestre::clé » : une facture d'un autre semestre (ou d'une autre saison). */
const CIBLE_SEMESTRE = /^(?:(\d{4}-\d{4})::)?([12])::(.+)$/;

export default function Facturation({ onChangement, cible, onAller }) {
  const chantiers = useChantiers();
  const [semestre, setSemestre] = useState(0);
  const [saison, setSaison] = useState("");
  const [data, setData] = useState(null);
  const [vue, setVue] = useState("a_generer");
  /* Cash / espèces : la liste commune avec À traiter, encaissé compris (`tout=true`) ; l'écran
     du cash la filtre lui-même (à encaisser, payé en partie, encaissé…). */
  const [especes, setEspeces] = useState(null);
  /* « Seulement les échéances dépassées » et « Écarter les échelonnés à jour » ont disparu
     (09.10.2026) : le serveur ne propose plus que les familles en retard. Restent deux
     filtres pour affiner : la date d'envoi de la facture et le dernier rappel. */
  const [envoiDu, setEnvoiDu] = useState("");
  const [envoiAu, setEnvoiAu] = useState("");
  /* Qui a déjà été relancé, et quand : l'école veut voir d'un coup d'œil les familles
     jamais relancées, puis celles dont le dernier rappel date. */
  const [relanceFiltre, setRelanceFiltre] = useState("");
  const [choisis, setChoisis] = useState([]);
  const [modale, setModale] = useState(null);
  const [modaleStage, setModaleStage] = useState(null);
  const [echelon, setEchelon] = useState(null);
  const [versions, setVersions] = useState(null);
  const [q, setQ] = useState("");
  const [releve, setReleve] = useState(false);
  const [occupe, setOccupe] = useState("");
  const { demander, informer } = useConfirmation();
  /* Générer, envoyer, relancer, encaisser : pendant le travail, une fenêtre grise toute la
     console et suit l'avancement ; à la fin, elle donne le bilan (Progression.jsx). */
  const travail = useProgression();
  const [extrait, setExtrait] = useState(false);
  const [calculs, setCalculs] = useState(false);
  const [genre, setGenre] = useState("tous");
  const [stages, setStages] = useState({ items: [] });
  const [dernierReleve, setDernierReleve] = useState(null);
  const [err, setErr] = useState("");

  const charger = useCallback(async (s, sais) => {
    const params = new URLSearchParams();
    if (s) params.set("semestre", String(s));
    if (sais) params.set("saison", sais);
    const suite = params.toString();
    const [d, st, rel, esp] = await Promise.all([
      api(`/facturation${suite ? `?${suite}` : ""}`),
      api("/stages").catch(() => ({ items: [] })),
      api("/releves/dernier").catch(() => null),
      api(`/facturation/especes?${suite ? `${suite}&` : ""}tout=true`).catch(() => null),
    ]);
    setData(d);
    setStages(st);
    setDernierReleve(rel);
    setEspeces(esp);
    setSemestre(d.semestre);
    setSaison(d.saison || "");
  }, []);

  useEffect(() => { charger(0, "").catch((e) => setErr(e.message)); }, [charger]);
  // « especes » n'est pas une famille : c'est la demande d'ouvrir le filtre Cash, qui
  // arrive depuis les paiements en espèces à confirmer de l'écran À traiter.
  // « 2::mail@x.ch » vient d'une facture à corriger d'un AUTRE semestre : sans le
  // basculement de semestre, la famille était introuvable à l'arrivée. « 2026-2027::2::… »
  // (facture d'une saison passée jamais envoyée) ouvre aussi la bonne saison.
  const viseAutreSemestre = CIBLE_SEMESTRE.exec(cible || "");
  const cibleFamille = viseAutreSemestre ? viseAutreSemestre[3]
    : (cible && !["especes", "ecarts", "cartes10", "prives"].includes(cible) ? cible : "");

  useEffect(() => {
    if (["especes", "ecarts", "cartes10", "prives"].includes(cible)) setVue(cible);
    const vise = CIBLE_SEMESTRE.exec(cible || "");
    if (vise) charger(Number(vise[2]), vise[1] || saison).catch((e) => setErr(e.message));
  }, [cible, charger]);

  /* Arriver depuis « À traiter » ouvre la LISTE de la facture visée — « À corriger » pour
     une correction — et pas l'onglet « Toutes » : l'école atterrissait sur 231 lignes sans
     comprendre pourquoi. La famille visée est surlignée, les autres factures de la même
     liste restent visibles (c'est le chantier qu'elle vient traiter). */
  const vueDeja = useRef("");
  useEffect(() => {
    if (!cibleFamille || !data || vueDeja.current === cible) return;
    /* Une facture de stage (« stage::adresse::stage », bouton « Ouvrir dans Facturation »
       de la fiche élève) s'ouvre dans la puce « Stages », sur sa liste. */
    const stage = (stages.items || []).find((x) => `stage::${x.cle}` === cibleFamille);
    const f = stage ? { liste: listeDuStage(stage) }
      : (data.items || []).find((x) => x.cle === cibleFamille);
    if (!f) return;
    vueDeja.current = cible;
    if (stage) setGenre("stage");
    setVue(VUES.some((v) => v.id === f.liste) || f.liste === "especes" ? f.liste : "tous");
  }, [cible, cibleFamille, data, stages]);

  /* « Modifier » ou « Échelonner » depuis Cash / espèces sur la facture d'un AUTRE semestre :
     son semestre se charge d'abord, puis la fenêtre s'ouvre. */
  const [aOuvrir, setAOuvrir] = useState(null);
  useEffect(() => {
    if (!aOuvrir || !data) return;
    const f = (data.items || []).find((x) => x.cle === aOuvrir.cle);
    if (!f) return;
    setAOuvrir(null);
    if (aOuvrir.action === "echelonner") setEchelon(f); else setModale(f);
  }, [aOuvrir, data]);
  /* « Encaisser » depuis une ligne de facture, hors de la vue cash (Toutes, recherche) : la
     même fenêtre que dans Cash / espèces, tout ou une partie. */
  const [encaisserF, setEncaisserF] = useState(null);
  /* Écarts de paiement : « Prix spécial convenu » ouvre la fenêtre des montants (ou du
     stage) avec le rabais proposé ; « Paiement en plusieurs fois » d'un stage. */
  const [prixSpecial, setPrixSpecial] = useState(false);
  const [echelonStage, setEchelonStage] = useState(null);

  if (err && !data) return <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>;
  if (!data) return <p className="text-[var(--muted)]">Chargement…</p>;

  const cash = (f) => !!(f.especes || f.especes_annoncees || f.especes_a_confirmer
    || /cash|esp/i.test(f.mode_paiement || ""));

  /* Une facture de stage devient une ligne comme les autres : même liste, mêmes 4 cases,
     mêmes boutons. Seules les routes changent (`/stages/...`), d'où le champ `genre`. */
  const versLigne = (l) => ({
    ...l,
    genre: "stage",
    cle: `stage::${l.cle}`,
    cle_stage: l.cle,
    numero: l.numero || "",
    pdf: !!l.numero,
    pdf_numero: l.numero || "",
    pdf_version: 1,
    eleves: (l.enfants || []).map((e) => e.eleve),
    detail: (l.enfants || []).map((e) => ({ eleve: e.eleve, cours: `${l.stage} — ${e.eleve}` })),
    crediteur: "",
    especes_a_confirmer: !!l.especes_a_confirmer,
    bloquee: l.statut === "bloquee",
    /* Un stage suit les mêmes listes que les cours : sans cette clé, une facture de stage
       n'apparaissait dans aucun onglet. */
    liste: listeDuStage(l),
    postes: [],
    a_recalculer: false,
    annulee: false,
    echelonnement_nb: 0,
  });

  const lignesStages = (stages.items || []).map(versLigne);
  const lignesCours = data.items.map((f) => ({ ...f, genre: "cours" }));
  /* Les inscriptions annulées arrivent dans la réponse pour que la recherche les retrouve
     (une famille qui a annulé peut devoir une taxe) : elles ne comptent dans aucune liste
     de travail ni dans aucun compteur (03.10.2026). */
  const tout = genre === "cours" ? lignesCours
    : genre === "stage" ? lignesStages
    : [...lignesCours, ...lignesStages];
  const base = tout.filter((f) => f.statut !== "annulee");
  /* « Cash / espèces » : la liste commune du serveur (/facturation/especes, la même
     qu'À traiter), jusqu'au semestre affiché. Le bouton compte ce qui attend au comptoir :
     factures en espèces de ce semestre (et stages), celles d'un semestre précédent ou d'une
     saison terminée, cartes 10 cours et workshops pas encore encaissés. Avant, elle comptait
     les seules factures, payées comprises (« 16 dans Facturation et 30 dans À traiter »).
     Écran à part depuis le 20.10.2026 : le genre de factures choisi (cours, stages) ne
     change plus ce nombre. */
  const especesVues = especes?.items || [];
  const especesAttente = especesVues.filter((x) => x.a_confirmer !== false);
  const attente = new Set(especesAttente.map((x) => (x.genre === "facture"
    ? `${x.saison}::${x.semestre}::${x.cle}` : x.ref)));
  const aConfirmer = (f) => attente.has(f.genre === "stage" ? `stage::${f.cle_stage}`
    : `${f.saison || saison}::${f.semestre || semestre}::${f.cle}`);
  const nbEspeces = especesAttente.length;
  /* Filtre par date d'envoi de la facture : une facture sans date d'envoi connue reste
     visible, sinon elle disparaîtrait sans qu'on comprenne pourquoi. */
  const dansFenetre = (f) => {
    const le = String(f.envoyee_le || "").slice(0, 10);
    if (!le) return !envoiDu && !envoiAu;
    return (!envoiDu || le >= envoiDu) && (!envoiAu || le <= envoiAu);
  };
  const fenetreActive = !!(envoiDu || envoiAu);
  /* Filtre par DERNIER RAPPEL : « jamais relancées » (une première campagne) ou « déjà
     relancées » (un 2e rappel). Le délai entre deux rappels, lui, est tenu par la console :
     une famille relancée il y a peu n'est plus à relancer (Réglages → Paramètres). */
  const passeRelance = (f) => {
    if (!relanceFiltre) return true;
    const le = String(f.relance_envoyee_le || "").slice(0, 10);
    return relanceFiltre === "jamais" ? !le : !!le;
  };
  const echelonne = (f) => (f.echelonnement_nb || 0) > 0;
  /* La liste de travail d'une facture est calculée par la console (champ `liste`) : une
     facture et une seule liste, et aucun état ne peut n'être affiché nulle part. Le
     repli sur « À corriger » est le filet : une facture dont l'état serait inconnu se
     voit, au lieu de n'être trouvable qu'en cherchant son nom. */
  const LISTES = [...VUES.map((v) => v.id), "especes"];
  const listeDe = (f) => (LISTES.includes(f.liste) ? f.liste : "bloquee");
  const compte = (id) => (id === "especes" ? nbEspeces
    : id === "a_relancer" ? base.filter(aRelancer).length
    : id === "echelonnes" ? base.filter(echelonne).length
    : ["historique", "ecarts", "parrainages"].includes(id) ? undefined
    : id === "tous" ? base.length
    : base.filter((f) => listeDe(f) === id).length);
  const somme = (filtre) => base.filter(filtre).reduce((t, f) => t + (f.total || 0), 0);
  const cherche = q.trim().toLowerCase();
  // Une recherche par nom porte sur TOUTES les factures : pas besoin de trouver le bon
  // statut avant de chercher quelqu'un.
  const items = (cherche ? tout : base).filter((f) => (cherche
    ? `${f.client} ${f.email} ${f.numero} ${(f.numeros || []).join(" ")} ${(f.eleves || []).join(" ")}`.toLowerCase().includes(cherche)
    : ((vue === "tous" ? true : vue === "especes" ? aConfirmer(f)
        : vue === "envoyee" ? (listeDe(f) === "envoyee" && dansFenetre(f) && passeRelance(f))
        : vue === "a_relancer" ? (aRelancer(f) && dansFenetre(f) && passeRelance(f))
        : vue === "echelonnes" ? echelonne(f)
        : ["parrainages", "ecarts"].includes(vue) ? false : listeDe(f) === vue))));
  /* « Générées, à envoyer » suit l'ordre de FABRICATION, la dernière en haut : l'école
     vient de générer un lot et veut le retrouver tel quel, pas éparpillé dans l'alphabet.
     Partout ailleurs, l'ordre alphabétique reste le bon (on cherche une famille). */
  const visibles = vue === "generee" && !cherche
    ? [...items].sort((a, b) => String(b.pdf_genere_le || "").localeCompare(String(a.pdf_genere_le || "")))
    /* En attente de paiement et échéances dépassées : la plus vieille facture d'abord,
       c'est celle qui attend depuis le plus longtemps. */
    : ["envoyee", "a_relancer"].includes(vue) && !cherche
    ? [...items].sort((a, b) => String(a.echeance || "9999").localeCompare(String(b.echeance || "9999")))
    : items;
  /* Les impayés : se cochent les familles EN RETARD et celles que l'école peut relancer
     « quand même » (pas encore échue, échelonnée à jour, relancée il y a peu, paiement
     différé — école, 26.10.2026 : « il peut tous les sélectionner s'il veut »). Le rappel
     à une famille pas en retard se confirme d'abord (relancerSelection), et le serveur
     revérifie chacune. Jamais : payée, espèces, à corriger, à vérifier. */
  const vueRelances = ["envoyee", "a_relancer"].includes(vue) && !cherche;
  const relancable = (f) => aRelancer(f) || relanceALaMain(f);
  const selectionnable = (f) => (["envoyee", "a_relancer"].includes(vue) ? relancable(f) : !f.bloquee);
  const selectionnables = VUES_AVEC_LOT.includes(vue) ? items.filter(selectionnable) : [];
  const tous = selectionnables.length > 0 && selectionnables.every((f) => choisis.includes(f.cle));
  /* Le tableau de bord des relances : qui part aujourd'hui, et pourquoi les autres non. Il
     suit les filtres d'envoi et de dernier rappel, mais pas l'onglet : « À relancer »
     rappelle combien de factures en attente restent sans rappel. */
  const enSuivi = vueRelances
    ? base.filter((f) => listeDe(f) === "envoyee" && dansFenetre(f) && passeRelance(f)) : [];
  const enRetard = enSuivi.filter(aRelancer);
  const sansRappel = enSuivi.filter((f) => !aRelancer(f));

  const generer = async (refs, refaire = false, accepterEcart = false) => {
    setOccupe("generation");
    setErr("");
    // Les factures de stage suivent leur propre route : la liste est commune, pas le circuit.
    const clesStages = refs.filter((r) => String(r).startsWith("stage::")).map((r) => r.slice(7));
    let restants = refs.filter((r) => !String(r).startsWith("stage::")), produits = [], refus = [];
    /* C'est le NAVIGATEUR qui enchaîne les paquets de PAQUET factures : fermer la page au
       milieu arrête la génération au paquet en cours. La fenêtre de progression bloque la
       console, retient la fermeture de la page et compte les factures paquet par paquet. */
    const nom = (cle) => tout.find((f) => f.cle === cle)?.client || String(cle).replace(/^stage::/, "");
    const total = refs.length;
    const parPaquets = Math.ceil(restants.length / PAQUET) + (clesStages.length ? 1 : 0) > 1;
    let etape = "fabrication";
    travail.demarrer({
      titre: refaire ? (total > 1 ? "Nouvelle version des factures" : "Nouvelle version de la facture")
        : (total > 1 ? "Génération des factures" : "Génération de la facture"),
      total, faits: parPaquets ? 0 : null, unite: ["facture traitée", "factures traitées"],
      attente: total > 1 ? `${total} factures en cours de fabrication`
        : "Fabrication du PDF et contrôle du QR-code…",
      detail: `${parPaquets ? "En cours" : "Pour"} : ${quelquesNoms(
        (clesStages.length ? refs.filter((r) => String(r).startsWith("stage::")) : restants.slice(0, PAQUET)).map(nom))}`,
    });
    try {
      if (clesStages.length) {
        const rs = await api("/stages/generer", { method: "POST", body: JSON.stringify({ cles: clesStages }) });
        produits = [...produits, ...rs.produites];
        refus = [...refus, ...rs.refusees];
        if (parPaquets) travail.avancer({ faits: total - restants.length });
      }
      while (restants.length) {
        if (parPaquets) travail.avancer({ detail: `En cours : ${quelquesNoms(restants.slice(0, PAQUET).map(nom))}` });
        const r = await api("/facturation/generer", {
          method: "POST",
          body: JSON.stringify({ refs: restants, semestre, saison, limite: PAQUET, refaire,
            ...(accepterEcart ? { accepter_ecart: true } : {}) }),
        });
        produits = [...produits, ...r.produites];
        refus = [...refus, ...r.refusees];
        restants = r.restants;
        if (parPaquets) travail.avancer({ faits: total - restants.length });
      }
      etape = "liste";
      travail.avancer({ faits: null, attente: "Mise à jour de la liste…", detail: "" });
      setChoisis([]);
      await charger(semestre, saison);
      onChangement?.();
      // On ne change de vue que si quelque chose a été produit : sinon l'école se
      // retrouvait devant une liste « Générées » où sa facture n'était pas, et croyait
      // l'avoir perdue. Elle reste là où elle est, avec la raison du refus.
      if (produits.length) setVue("generee");
      await travail.conclure({
        titre: produits.length ? `${produits.length} facture(s) générée(s)`
          : "Aucune facture n'a pu être générée",
        ton: refus.length ? "attention" : "succes",
        texte: refus.length
          ? `${refus.length} facture(s) refusée(s) — voici pourquoi. Aucun PDF n'a été `
            + "fabriqué ; les montants déjà enregistrés, eux, sont conservés."
          : produits.length
            ? "Code QR relu et vérifié dans chaque PDF. Elles attendent leur envoi."
            : "La console n'a pas retrouvé cette facture dans le semestre et la saison "
              + "affichés. Recharge la page, puis réessaie.",
        reussis: { libelle: ["générée", "générées"], noms: produits.map((p) => p.client) },
        erreurs: { libelle: ["refusée", "refusées"],
          items: refus.map((x) => ({ nom: x.client, motif: x.motif })) },
      });
    } catch (e) {
      setErr(`Génération interrompue : ${e.message}`);
      // Rien ne se perd : ce qui est fait, refusé et pas encore traité reste sous les yeux.
      await travail.conclure({
        titre: "Génération interrompue", ton: "danger",
        texte: `${raisonErreur(e)}\n${etape === "liste"
          ? "Les factures ont été traitées, mais la liste n'a pas pu être rechargée : recharge la page."
          : restants.length
            ? `${restants.length} facture(s) pas traitée(s) : coche-les à nouveau et relance « Générer ».`
            : ""}`.trim(),
        reussis: { libelle: ["générée", "générées"], noms: produits.map((p) => p.client) },
        erreurs: { libelle: ["refusée", "refusées"],
          items: refus.map((x) => ({ nom: x.client, motif: x.motif })) },
        ecartees: { libelle: ["pas traitée", "pas traitées"],
          items: restants.map((c) => ({ nom: nom(c), motif: "" })) },
      });
    }
    setOccupe("");
  };

  const remettreAGenerer = async (factures) => {
    const refs = factures.filter((f) => f.genre !== "stage").map((f) => f.cle);
    if (!refs.length) return;
    setOccupe("remise");
    setErr("");
    // Un seul appel au serveur, qui traite tout le lot : la fenêtre bloque la console le temps
    // de la réponse et donne le bilan.
    travail.demarrer({
      titre: "Remise dans « À générer »",
      attente: `${refs.length} facture(s) en cours`,
      detail: `Pour : ${quelquesNoms(factures.filter((f) => f.genre !== "stage").map((f) => f.client))}`,
    });
    try {
      const r = await api("/facturation/remettre-a-generer", {
        method: "POST", body: JSON.stringify({ refs, semestre, saison }),
      });
      setChoisis([]);
      await charger(semestre, saison);
      onChangement?.();
      if (r.faites.length) setVue("a_generer");
      await travail.conclure({
        titre: r.faites.length
          ? `${r.faites.length} facture(s) remise(s) dans « À générer »`
          : "Aucune facture remise dans « À générer »",
        ton: r.refusees.length ? "attention" : "succes",
        texte: "Génère-les, vérifie le montant, puis renvoie-les à la famille.",
        reussis: { libelle: ["remise", "remises"], noms: r.faites.map((x) => x.client) },
        erreurs: { libelle: ["refusée", "refusées"],
          items: r.refusees.map((x) => ({ nom: x.client, motif: x.motif })) },
      });
    } catch (e) {
      setErr(`Remise dans « À générer » interrompue : ${e.message}`);
      await travail.conclure({ titre: "Remise dans « À générer » interrompue", ton: "danger",
        texte: raisonErreur(e) });
    }
    setOccupe("");
  };

  /* Les rappels de paiement. Par défaut, SEULES les familles en retard partent (le serveur
     l'a décidé, et il le revérifie à l'envoi) : une sélection qui contiendrait une famille
     à jour la laisse de côté. `voulu` : « Relancer quand même », après confirmation, sur
     une ligne (relancerQuandMeme) ou sur une sélection (relancerSelection). `forcer` : le
     relevé bancaire n'est pas à jour, l'école relance en connaissance de cause. */
  const relancer = async (factures, forcer = false, voulu = false) => {
    const partent = voulu ? factures : factures.filter(aRelancer);
    if (!partent.length) {
      await travail.conclure({ titre: "Aucune famille à relancer", ton: "attention",
        texte: "Personne dans la sélection n'est en retard de paiement aujourd'hui.",
        points: pourquoiPas(factures) });
      return;
    }
    const refs = partent.filter((f) => f.genre !== "stage").flatMap((f) => (f.postes || []).map((p) => p.id));
    const clesStages = partent.filter((f) => f.genre === "stage").map((f) => f.cle_stage);
    if (!refs.length && !clesStages.length) return;
    setOccupe("relance");
    setErr("");
    const envois = [], ignorees = [], sansPiece = [], rates = [];
    let avertissement = "", lance = false;
    try {
      const etatReleve = await api("/releves/dernier");
      if (!etatReleve.valide && !forcer) {
        // On demande le relevé d'abord (pour ne pas relancer une famille qui a payé),
        // mais la relance reste possible : la modale propose « Relancer quand même ».
        setReleve({ apres: partent, voulu, etat: etatReleve });
        setOccupe("");
        return;
      }
      const delai = data.items.find((x) => x.delai_paiement_jours)?.delai_paiement_jours || 30;
      const laissees = factures.length - partent.length;
      /* La confirmation dit ce que le rappel réclamera. Un rappel voulu sur une seule ligne
         a déjà eu sa propre question (relancerQuandMeme) : on ne la repose pas. */
      if (!voulu && !await demander({
        titre: "Envoyer les rappels de paiement",
        ton: data.mode_reel ? "attention" : "info",
        texte: data.mode_reel
          ? `Un rappel va partir à ${partent.length} famille(s) en retard de paiement.`
          : `${partent.length} rappel(s) — MODE TEST : aucun e-mail n'est parti, ni aux familles ni ailleurs.`,
        points: [`Délai de paiement : ${delai} jours.`,
          "La dernière facture envoyée est jointe au rappel : la famille n'a rien à rechercher.",
          "Un seul e-mail par famille.",
          ...(partent.some((f) => f.relance?.code === "mensualite")
            ? ["Paiement échelonné : le rappel ne réclame que la mensualité en retard (montant et date), pas le solde."]
            : []),
          ...(partent.some((f) => f.relance?.code === "partiel")
            ? ["Paiement partiel : le rappel dit ce qui est déjà arrivé et ne réclame que le reste."]
            : []),
          ...(laissees > 0
            ? [`${laissees} famille(s) de la sélection ne sont pas en retard (${pourquoiPas(
              factures.filter((f) => !aRelancer(f))).join(", ")}) : elles ne reçoivent rien.`]
            : []),
          ...(etatReleve.valide ? [] : [`⚠ ${etatReleve.pourquoi || "relevé bancaire non importé"}`])],
        valider: "Envoyer les rappels",
      })) {
        setOccupe("");
        return;
      }
      /* Les rappels partent par paquets de cinq FAMILLES : l'école voit avancer le compteur
         au lieu d'attendre devant un écran figé, et un incident n'emporte pas le lot. Le
         découpage suit les familles, jamais leurs lignes : sans ça, une famille à cheval
         sur deux paquets recevait deux e-mails. C'est le navigateur qui enchaîne les
         paquets : la fenêtre de progression bloque la console et retient la page. */
      const familles = partent.filter((f) => f.genre !== "stage");
      const total = familles.length + clesStages.length;
      const parPaquets = Math.ceil(familles.length / 5) + (clesStages.length ? 1 : 0) > 1;
      lance = true;
      travail.demarrer({
        titre: total > 1 ? "Envoi des rappels de paiement" : "Envoi du rappel de paiement",
        total, faits: parPaquets ? 0 : null, unite: ["famille traitée", "familles traitées"],
        attente: total > 1 ? `${total} rappels en cours d'envoi` : "Rappel en cours d'envoi",
        detail: `${parPaquets ? "En cours" : "Pour"} : ${quelquesNoms(
          (familles.length ? familles.slice(0, 5) : partent).map((f) => f.client))}`,
      });
      for (let i = 0; i < familles.length; i += 5) {
        const lot = familles.slice(i, i + 5);
        const paquet = lot.flatMap((f) => (f.postes || []).map((p) => p.id));
        if (parPaquets) travail.avancer({ detail: `En cours : ${quelquesNoms(lot.map((f) => f.client))}` });
        try {
          /* Le serveur revérifie chaque famille : une famille devenue à jour entre-temps
             (virement du jour, accord pris) est écartée et la raison revient dans le bilan. */
          const r = await api("/notifications/impayes/relancer", {
            method: "POST",
            body: JSON.stringify({ refs: paquet, forcer: true, ...(voulu ? { forcer_rappel: true, quand_meme_selection: true } : {}) }),
          });
          envois.push(...r.envois);
          ignorees.push(...(r.ignorees || []));
          sansPiece.push(...(r.sans_facture_jointe || []));
          avertissement = r.avertissement || avertissement;
        } catch (e) {
          /* Un paquet qui échoue (coupure réseau, e-mail refusé) n'arrête pas la campagne :
             les familles suivantes partent, et l'école voit lesquelles reprendre. Reprendre
             la campagne ne fait pas de doublon : le serveur refuse deux rappels le même jour. */
          rates.push(...lot.map((f) => ({ nom: f.client,
            motif: `${raisonErreur(e)} — à relancer` })));
        }
        if (parPaquets) travail.avancer({ faits: Math.min(i + 5, familles.length) });
      }
      if (clesStages.length) {
        if (parPaquets) travail.avancer({ detail: "En cours : les factures de stage" });
        const rs = await api("/stages/relancer", { method: "POST",
          body: JSON.stringify({ cles: clesStages, ...(voulu ? { forcer_rappel: true, quand_meme_selection: true } : {}) }) });
        envois.push(...rs.envois);
        ignorees.push(...(rs.ignorees || []));
        if (parPaquets) travail.avancer({ faits: total });
      }
      /* « 3 ignorées : à jour » — les familles que le serveur a écartées, comptées par raison. */
      const parRaison = {};
      ignorees.forEach((x) => { const g = x.groupe || x.motif; parRaison[g] = (parRaison[g] || 0) + 1; });
      await travail.conclure({
        titre: `${envois.length} rappel(s) envoyé(s)`,
        ton: (sansPiece.length || rates.length) ? "attention" : "succes",
        texte: data.mode_reel ? "Les familles concernées ont reçu leur rappel, facture jointe."
          : "MODE TEST : rien n'est parti aux familles.",
        reussis: { libelle: ["rappel envoyé", "rappels envoyés"], noms: envois.map((x) => x.client) },
        // Paquet en panne (réseau) : à reprendre. Familles écartées par le serveur (à jour,
        // espèces, déjà relancée aujourd'hui…) : la raison est dite, rien à refaire.
        erreurs: { libelle: ["en erreur", "en erreur"], items: rates },
        ecartees: { libelle: ["sans rappel", "sans rappel"],
          items: ignorees.map((x) => ({ nom: x.client || x.cle || x.ref, motif: x.motif })) },
        points: [...(ignorees.length
            ? [`${ignorees.length} ignorée(s) par la console : ${Object.entries(parRaison)
              .map(([g, n]) => `${n} ${g}`).join(", ")}.`]
            : []),
          ...(avertissement ? [`Relevé bancaire : ${avertissement}`] : []),
          ...(rates.length
            ? ["Les familles en erreur peuvent être relancées tout de suite : aucun doublon "
               + "possible, le serveur refuse deux rappels le même jour."]
            : []),
          ...(sansPiece.length
            ? [`⚠ ${sansPiece.length} rappel(s) partis SANS facture jointe (aucun PDF `
               + `archivé) : ${sansPiece.slice(0, 5).join(", ")}`]
            : [])],
      });
      lance = false; // bilan lu : un échec du rechargement de la liste n'est plus qu'un bandeau
      setOccupe("actualisation");
      setChoisis([]);
      await charger(semestre, saison);
      onChangement?.();
    } catch (e) {
      setErr(`Relance interrompue : ${e.message}`);
      if (lance) {
        await travail.conclure({
          titre: "Relance interrompue", ton: "danger",
          texte: `${raisonErreur(e)}\nLes rappels déjà partis sont comptés ci-dessous ; les autres `
            + "peuvent être relancés : aucun doublon possible le même jour.",
          reussis: { libelle: ["rappel envoyé", "rappels envoyés"], noms: envois.map((x) => x.client) },
          erreurs: { libelle: ["en erreur", "en erreur"], items: rates },
          ecartees: { libelle: ["sans rappel", "sans rappel"],
            items: ignorees.map((x) => ({ nom: x.client || x.cle || x.ref, motif: x.motif })) },
        });
      }
    }
    setOccupe("");
  };

  /* « Relancer quand même » : une famille que la console ne relancerait pas aujourd'hui
     (pas encore échue, échelonnée à jour, relancée il y a peu, paiement différé). L'école
     le veut pour CETTE famille : on lui dit pourquoi elle n'était pas prévue et ce que le
     rappel va réclamer, puis elle confirme. */
  const relancerQuandMeme = async (f) => {
    const r = f.relance || {};
    const quoi = {
      pas_echue: `Le rappel dira « à régler d'ici le ${jourFr(r.echeance)} », sans parler de retard.`,
      echelonne_a_jour: `Le rappel annonce la prochaine mensualité : ${argent(r.montant)} à régler d'ici le ${jourFr(r.prochaine || r.echeance)}.`,
      relancee_recemment: `Elle a déjà reçu un rappel le ${jourFr(f.relance_envoyee_le)} ; celui-ci réclamera ${argent(r.montant)}.`,
      report: `Un paiement différé lui a été accordé jusqu'au ${jourFr(r.echeance)}.`,
    }[r.code];
    if (!await demander({
      titre: `Relancer ${f.client} quand même ?`,
      ton: "attention",
      texte: `La console ne prévoit pas de rappel aujourd'hui : ${r.motif}.`,
      points: [...(quoi ? [quoi] : []),
        "La dernière facture envoyée est jointe au rappel.",
        ...(data.mode_reel ? [] : ["MODE TEST : aucun e-mail ne partira."])],
      valider: "Envoyer ce rappel",
    })) return;
    await relancer([f], false, true);
  };

  /* Une sélection qui mêle familles en retard et familles relancées « quand même » : la
     fenêtre compte celles qui ne sont pas en retard et dit ce que leur rappel réclamera,
     puis tout part en une campagne (le serveur n'accepte « quand même » que pour une
     famille pas échue, échelonnée à jour, relancée il y a peu ou en paiement différé). */
  const relancerSelection = async (dues, quandMeme) => {
    const codes = new Set(quandMeme.map((f) => f.relance?.code));
    if (!await demander({
      titre: "Relancer aussi des familles qui ne sont pas en retard ?",
      ton: "attention",
      texte: `${dues.length} famille(s) en retard de paiement et ${quandMeme.length} qui ne le sont pas `
        + `aujourd'hui (${pourquoiPas(quandMeme).join(", ")}) : le rappel part quand même à toutes.`,
      points: [
        ...(codes.has("pas_echue") ? ["Pas encore échue : le rappel dit « à régler d'ici le … », sans parler de retard."] : []),
        ...(codes.has("echelonne_a_jour") ? ["Paiement échelonné à jour : le rappel annonce la prochaine mensualité."] : []),
        ...(codes.has("relancee_recemment") ? ["Relancée il y a peu : elle reçoit un nouveau rappel."] : []),
        ...(codes.has("report") ? ["Paiement différé : le rappel part malgré le délai accordé."] : []),
        "Payées, en espèces, à corriger ou à vérifier ne reçoivent jamais de rappel.",
        "Un seul e-mail par famille, la dernière facture envoyée jointe.",
        ...(data.mode_reel ? [] : ["MODE TEST : aucun e-mail ne partira."])],
      valider: `Envoyer ${dues.length + quandMeme.length} rappel(s)`,
    })) return;
    await relancer([...dues, ...quandMeme], false, true);
  };

  /* « Renvoyer la facture » : le dernier PDF reçu par la famille (même numéro, même QR),
     avec un court e-mail « voici une copie de votre facture » — pas un rappel. Sa date
     d'envoi et son échéance ne bougent pas. */
  const renvoyerFacture = async (f) => {
    const numero = f.numero || f.pdf_numero;
    if (!await demander({
      titre: `Renvoyer la facture de ${f.client} ?`,
      texte: `La famille reçoit à nouveau sa facture n° ${numero} (le PDF déjà envoyé, même QR), avec un court message « voici une copie de votre facture ». Ce n'est pas un rappel.`,
      points: ["Pour une famille qui ne l'a pas reçue ou l'a perdue.",
        "Sa date d'envoi, son échéance et ses rappels ne changent pas.",
        ...(data.mode_reel ? [] : ["MODE TEST : aucun e-mail ne partira."])],
      valider: "Renvoyer la facture",
    })) return;
    setOccupe("renvoi");
    setErr("");
    try {
      const r = await api("/facturation/renvoyer", { method: "POST", body: JSON.stringify({ numero }) });
      await travail.conclure({ titre: "Facture renvoyée", ton: "succes", texte: r.message });
      await charger(semestre, saison);
      onChangement?.();
    } catch (e) {
      await travail.conclure({ titre: "Facture pas renvoyée", ton: "attention", texte: raisonErreur(e) });
    }
    setOccupe("");
  };

  /* « Valider la facture corrigée ». Pour une famille au comptoir, la carte propose les deux
     vrais choix (école, 02.11.2026 : « Valider » tentait une facture QR que la console
     refuse, et la facture restait « À corriger ») : `mode` « especes » = « Elle paie au
     comptoir » (le montant s'encaisse dans Cash / espèces, sans facture QR) ; « qr » = « Elle
     paie par virement » (facture QR refaite, dans « Générées, à envoyer »). Le serveur dit où
     la facture se trouve ensuite, et pourquoi si elle n'a pas pu être refaite. */
  const accepterCorrection = async (f, mode = "") => {
    const c = f.correction;
    if (!c && !mode) return;
    // Les mêmes chiffres que la carte (après les bons de parrainage).
    const t = totauxCorrection(f);
    const chiffres = `${t.recu ? "Reçue par la famille" : "Facture actuelle"} : ${argent(t.avant)}\n`;
    const causes = c?.causes || [];
    const reste = Math.max(Math.round((t.nouveau - (f.recu || 0)) * 100) / 100, 0);
    if (!await demander(mode === "especes" ? {
      titre: `Elle paie au comptoir — ${f.client}`,
      texte: `${chiffres}${reste > 0.02 ? "À encaisser au comptoir" : "Réglée au comptoir"} : ${argent(t.nouveau)}`,
      points: [...causes, "Aucune facture QR n'est refaite ni envoyée.",
        reste > 0.02 ? `Elle passe dans Cash / espèces : ${argent(reste)} à encaisser.`
          : "Elle passe dans « Payées ».",
        ...(t.recu ? [`La facture de ${argent(t.avant)} reçue par la famille reste dans l'historique.`] : [])],
      valider: `Valider ${argent(t.nouveau)}`,
    } : mode === "qr" ? {
      titre: `Elle paie par virement — ${f.client}`,
      texte: `${chiffres}Nouvelle facture QR : ${argent(t.nouveau)}`,
      points: [...causes, "Le mode de paiement passe à « Facture QR (virement) ».",
        "La facture est refaite (même numéro, nouveau QR-code) et attend dans « Générées, à envoyer ».",
        "Rien ne part sans toi : relis-la, puis envoie-la."],
      valider: "Passer en facture QR",
    } : {
      titre: `Corriger la facture de ${f.client}`,
      texte: `${chiffres}Facture corrigée : ${argent(t.nouveau)}`,
      points: [...causes, "Le même prix est réaligné sur les deux semestres de la saison.",
        "La facture est refaite ; l'ancienne version reste dans l'historique."],
      valider: "Appliquer la correction",
    })) return;
    setOccupe("correction");
    setErr("");
    // Recalcul, écriture et nouveau PDF en un seul appel : plusieurs secondes où rien ne
    // doit toucher à cette famille.
    travail.demarrer({
      titre: "Correction de la facture", detail: `Pour : ${f.client}`,
      attente: mode === "especes" ? "Validation du montant à encaisser au comptoir…"
        : "Recalcul, puis nouvelle facture et nouveau QR-code…",
    });
    try {
      const r = await api("/facturation/corrections/appliquer", {
        method: "POST", body: JSON.stringify({ cles: [f.cle], semestre, saison, ...(mode ? { mode } : {}) }),
      });
      const info = r.corrigees[0];
      const ou = nomListe(info.liste);
      const autres = (r.autres_semestres || []).filter((a) => Math.abs(a.total_apres - a.total_avant) > 0.02);
      // La refabrication du PDF et le retour dans « À envoyer » sont faits côté serveur
      // par la validation elle-même : plus besoin de redemander une génération ici (ça
      // créait une version de facture en double). Cash / espèces se relit aussi : la
      // facture validée au comptoir doit y être tout de suite.
      const suite = `semestre=${semestre}${saison ? `&saison=${saison}` : ""}`;
      const [d, esp] = await Promise.all([api(`/facturation?${suite}`),
        api(`/facturation/especes?${suite}&tout=true`).catch(() => null)]);
      setData(d);
      if (esp) setEspeces(esp);
      onChangement?.();
      const encore = (d.items || []).find((x) => x.cle === f.cle && x.liste === "bloquee");
      const semestresAutres = autres.map((a) => `Le semestre ${a.semestre} a été réaligné en même temps : `
        + `${argent(a.total_avant)} → ${argent(a.total_apres)} (sa facture n'est pas encore refaite).`);
      // Jamais un succès affiché sur une facture qui n'a pas bougé : la raison, et quoi faire.
      if (info.refus || encore) {
        await travail.conclure({
          titre: info.refus ? "Correction enregistrée — la facture n'a pas pu être refaite"
            : "Correction enregistrée — la facture reste à corriger",
          ton: "attention",
          texte: `${f.client} — ${info.refus || encore?.a_corriger_pourquoi || encore?.motif || "un contrôle bloque encore la facture"}`,
          points: [`Elle attend dans « ${ou || "À corriger"} » : règle ce qui bloque (« Corriger à la main »), puis valide de nouveau.`],
        });
        if (ou) setVue(info.liste);
      } else if (info.comptoir) {
        // Au comptoir : aucune facture QR, la facture attend l'argent dans Cash / espèces.
        await travail.conclure({
          titre: info.liste === "payee" ? "Validée — déjà réglée au comptoir" : "Validée — à encaisser au comptoir",
          ton: "succes",
          texte: `${argent(t.avant)} → ${argent(t.nouveau)}.`,
          points: ["Aucune facture QR n'a été refaite ni envoyée.",
            info.liste === "payee" ? "Elle est dans « Payées »."
              : "Elle attend dans Cash / espèces : « Encaisser » quand la famille paie.",
            ...semestresAutres],
        });
        setVue(info.liste === "payee" ? "payee" : "especes");
      } else {
        // La facture corrigée repasse par « Générées, à envoyer » : on la relit avant
        // qu'elle ne reparte à la famille — aucun envoi automatique.
        await travail.conclure({
          titre: "Correction appliquée",
          ton: "succes",
          texte: `${argent(t.avant)} → ${argent(t.nouveau)}.`,
          points: [info.a_renvoyer || info.liste === "generee"
            ? "La facture a été refaite avec un nouveau QR-code et remise dans « Générées, à envoyer »."
            : `La facture attend dans « ${ou || "À générer"} ».`,
            ...(mode === "qr" ? ["La famille paie désormais par facture QR (virement)."] : []),
            ...semestresAutres,
            info.a_renvoyer || (info.liste === "generee" && t.recu)
              ? "Le montant a changé : relis-la, puis renvoie-la à la famille quand tu veux."
              : info.deja_envoyee
                ? "Elle avait déjà été envoyée et le montant ne change pas : rien à renvoyer."
                : info.liste === "generee" ? "Elle attend son envoi dans « Générées, à envoyer »."
                  : "Génère-la, relis-la, puis envoie-la à la famille."],
        });
        setVue(ou ? info.liste : "generee");
      }
    } catch (e) {
      setErr(`Correction interrompue : ${e.message}`);
      await travail.conclure({ titre: "Correction interrompue", ton: "danger",
        texte: `${f.client} — ${raisonErreur(e)}` });
    }
    setOccupe("");
  };

  /* « Corriger à la main » depuis « À corriger » : l'école part de la facture corrigée de la
     carte et y ajoute son geste (rabais, ajustement). Après l'enregistrement, la console
     valide donc la correction — sous-total réaligné sur les cours suivis, ce qui vient d'être
     saisi gardé — et refait le PDF. Sans ça, la génération refusait « le sous-total annoncé
     ne correspond plus aux cours » juste après la correction (école, 09.10.2026). */
  const validerApresCorrectionMain = async (cle, client) => {
    setOccupe("correction");
    setErr("");
    travail.demarrer({
      titre: "Correction de la facture", detail: `Pour : ${client}`,
      attente: "Ta correction est enregistrée : recalcul, puis nouvelle facture et nouveau QR-code…",
    });
    try {
      const r = await api("/facturation/corrections/appliquer", {
        method: "POST", body: JSON.stringify({ cles: [cle], semestre, saison }),
      });
      const info = (r.corrigees || [])[0] || {};
      const suite = `semestre=${semestre}${saison ? `&saison=${saison}` : ""}`;
      const [d, esp] = await Promise.all([api(`/facturation?${suite}`),
        api(`/facturation/especes?${suite}&tout=true`).catch(() => null)]);
      setData(d);
      if (esp) setEspeces(esp);
      onChangement?.();
      const encore = (d.items || []).find((x) => x.cle === cle && x.liste === "bloquee");
      /* Le serveur a refait le PDF (`regeneree`) ou dit pourquoi il n'a pas pu (`refus`) :
         la facture attend alors dans la liste qu'il nomme, avec sa raison. Une famille au
         comptoir (mode « Cash » gardé dans la fenêtre) n'a pas de facture QR : son montant
         est validé, à encaisser dans Cash / espèces (`comptoir`). */
      const ou = nomListe(info.liste);
      await travail.conclure(info.refus ? {
        titre: "Correction enregistrée — la facture n'a pas pu être refaite", ton: "attention",
        texte: `${client} — ${info.refus}`,
        points: [`Elle attend dans « ${ou || "À générer"} » : règle ce qui bloque, puis valide de nouveau.`],
      } : encore ? {
        titre: "Correction enregistrée — la facture reste à corriger", ton: "attention",
        texte: `${client} — ${encore.a_corriger_pourquoi || encore.motif || "un contrôle bloque encore la facture"}`,
      } : info.comptoir ? {
        titre: info.liste === "payee" ? "Correction validée — déjà réglée au comptoir"
          : "Correction validée — à encaisser au comptoir", ton: "succes",
        texte: (() => {
          const refaite = (d.items || []).find((x) => x.cle === cle);
          return refaite ? `Nouveau total : ${argent(refaite.total)}.` : "";
        })(),
        points: ["La famille paie au comptoir : aucune facture QR n'a été refaite ni envoyée.",
          info.liste === "payee" ? "Elle est dans « Payées »."
            : "Elle attend dans Cash / espèces : « Encaisser » quand la famille paie."],
      } : {
        titre: "Correction appliquée", ton: "succes",
        // Le total de la facture REFAITE, bons de parrainage et bons cadeaux déduits —
        // pas celui des seules lignes (qui annonçait 470 pour une facture à 420).
        texte: (() => {
          const refaite = (d.items || []).find((x) => x.cle === cle);
          const total = refaite ? refaite.total : info.total_apres;
          return total != null ? `Nouveau total : ${argent(total)}.` : "";
        })(),
        points: [info.liste === "generee"
          ? "La facture a été refaite (même numéro, nouveau QR-code) et attend dans « Générées, à envoyer »."
          : `La facture attend dans « ${ou || "À générer"} ».`,
          "Relis-la, puis envoie-la à la famille quand tu veux."],
      });
      if (info.refus || !encore) setVue(ou ? info.liste : "generee");
    } catch (e) {
      setErr(`Correction interrompue : ${e.message}`);
      await travail.conclure({ titre: "Correction interrompue", ton: "danger",
        texte: `${client} — ${raisonErreur(e)}` });
    }
    setOccupe("");
  };

  // « Elle est déjà partie » vit désormais dans la modale « Modifier » (cas particuliers) :
  // à côté de « Générer », le bouton se cliquait par erreur.


  // Supprimer la version recalculée portait à confusion (« si on refait la facture, on
  // l'envoie ») : l'action a été retirée de l'écran. Le nettoyage reste possible en
  // régénérant, et l'historique garde toutes les versions.


  /* La puce, la liste et l'alerte se relisent après un encaissement fait dans un bloc
     (cartes 10 cours, workshops) : la liste commune a changé, À traiter aussi. */
  const rafraichirEspeces = () => {
    const p = new URLSearchParams();
    if (semestre) p.set("semestre", String(semestre));
    if (saison) p.set("saison", saison);
    p.set("tout", "true");
    api(`/facturation/especes?${p}`).then(setEspeces).catch(() => {});
    onChangement?.();
  };

  /* Cash / espèces › « Modifier » et « Échelonner » d'une facture de cours ou de stage :
     les fenêtres habituelles (montants et mode de paiement, prorata et rabais du stage,
     paiement en plusieurs fois). Une facture d'un autre semestre ouvre d'abord son semestre. */
  const ouvrirDepuisCash = (it, action) => {
    if (it.genre === "stage") {
      const f = lignesStages.find((x) => x.cle_stage === it.cle);
      if (f) setModaleStage(f);
      return;
    }
    const ici = (it.saison || "") === (saison || data.saison || "") && Number(it.semestre) === Number(semestre);
    const f = ici && lignesCours.find((x) => x.cle === it.cle);
    if (f) {
      if (action === "echelonner") setEchelon(f); else setModale(f);
      return;
    }
    setAOuvrir({ cle: it.cle, action });
    charger(it.semestre, it.saison).catch((e) => setErr(e.message));
  };
  /* La facture fabriquée (et ses versions) d'une famille au comptoir : pour voir ce qui est
     parti par mégarde. */
  const voirFactureDepuisCash = (it) => {
    const f = lignesCours.find((x) => x.cle === it.cle);
    if (f && (f.pdf_numero || f.numero || f.pdf_archive)) setVersions(f);
    else informer({ titre: "Aucune facture fabriquée",
      texte: "Cette famille n'a jamais eu de facture QR : rien n'a pu partir par e-mail." });
  };
  /* Une ligne du tableau des factures dans la forme de la liste du cash, pour la fenêtre
     « Encaisser ». */
  const versCash = (f) => ({
    ref: f.genre === "stage" ? `stage::${f.cle_stage}` : (f.postes || []).map((p) => p.id).join(","),
    genre: f.genre === "stage" ? "stage" : "facture", titre: f.client,
    quoi: f.genre === "stage" ? f.stage : (f.detail || []).map((d) => d.cours).join(", "),
    total: f.total || 0, recu: f.recu || 0, reste: Math.max(Math.round(((f.total || 0) - (f.recu || 0)) * 100) / 100, 0),
    echelonnement: f.echelonnement_nb ? { nb: f.echelonnement_nb, recus: f.echelonnement_recus || 0,
      part: f.echelonnement_part || 0, prochaine: f.echelonnement_prochaine || "" } : null,
  });

  const confirmerEspeces = async (factures) => {
    /* UNE route pour tout, celle d'À traiter : les lignes d'une facture de cours, et
       « stage::<clé> » pour une facture de stage (encaissée en entier, un acompte déjà reçu
       compris). Une ligne de la liste commune (autre semestre) porte déjà sa référence. */
    const refs = factures.flatMap((f) => (f.ref ? [f.ref]
      : f.genre === "stage" ? [`stage::${f.cle_stage}`] : (f.postes || []).map((p) => p.id)));
    if (!refs.length) return;
    if (!await demander({
      titre: "Encaissement en espèces",
      texte: `${factures.length} ligne(s) vont passer en PAYÉE.`,
      points: ["À ne valider que si l'argent est bien arrivé au comptoir.",
        "Le solde tombe à zéro et plus aucun rappel ne partira."],
      valider: "Oui, c'est encaissé",
    })) return;
    setOccupe("especes");
    setErr("");
    let nb = 0, montant = 0, bilanLu = false;
    travail.demarrer({
      titre: "Encaissement en espèces",
      attente: `${factures.length} facture(s) passent en payée`,
      detail: `Pour : ${quelquesNoms(factures.map((f) => f.client || f.titre))}`,
    });
    try {
      const r = await api("/facturation/especes/confirmer", { method: "POST", body: JSON.stringify({ ids: refs }) });
      nb += r.confirmes.length;
      montant += r.total;
      await travail.conclure({
        titre: `${nb} ligne(s) passée(s) en payée`, ton: "succes",
        texte: `${argent(montant)} encaissés en espèces.`,
      });
      bilanLu = true;
      setOccupe("actualisation");
      setChoisis([]);
      await charger(semestre, saison);
      onChangement?.();
    } catch (e) {
      setErr(`Confirmation interrompue : ${e.message}`);
      if (!bilanLu) {
        await travail.conclure({
          titre: "Encaissement interrompu", ton: "danger",
          texte: `${raisonErreur(e)}\n${nb ? `${nb} ligne(s) déjà passée(s) en payée (${argent(montant)}). ` : ""}`
            + "Recharge la page et vérifie la vue « Cash / espèces » avant de recommencer.",
        });
      }
    }
    setOccupe("");
  };

  const annulerPaiement = async (f) => {
    if (!await demander({
      titre: `Annuler le paiement de ${f.client}`,
      ton: "danger",
      texte: "Le montant encaissé est remis à zéro et la facture repasse en attente de paiement.",
      valider: "Annuler le paiement",
      annuler: "Ne rien changer",
    })) return;
    setOccupe("especes");
    setErr("");
    // Une écriture PAR LIGNE depuis le navigateur : interrompue au milieu, la facture
    // resterait à moitié payée. La fenêtre bloque la console le temps de ces écritures.
    travail.demarrer({ titre: "Annulation du paiement", detail: `Pour : ${f.client}`,
      attente: "Le montant encaissé est remis à zéro…" });
    try {
      if (f.genre === "stage") {
        // Une ligne de stage n'a pas de postes (versLigne) : la boucle ci-dessous ne
        // faisait rien et le paiement restait. Le stage s'annule par son propre
        // encaissement : montant 0 = encaissement remis à zéro.
        await api("/stages/paiement", {
          method: "POST",
          body: JSON.stringify({ cle: f.cle_stage, montant: 0 }),
        });
      } else {
        for (const p of f.postes) {
          await api(`/facturation/poste/${encodeURIComponent(p.id)}`, {
            method: "PATCH",
            body: JSON.stringify({ champs: { montant_recu: 0, date_paiement: "" } }),
          });
        }
      }
      await charger(semestre, saison);
      onChangement?.();
      travail.arreter();
    } catch (e) {
      setErr(`Annulation interrompue : ${e.message}`);
      await travail.conclure({ titre: "Annulation interrompue", ton: "danger",
        texte: `${f.client} — ${raisonErreur(e)}\nLe paiement n'est peut-être annulé qu'en partie : `
          + "recharge la page et vérifie la facture avant de recommencer." });
    }
    setOccupe("");
  };

  // Encaissement d'une facture de stage : la banque (ou le cash via « Payé en espèces »).
  const encaisserStage = async (f) => {
    if (!await demander({
      titre: `Encaissement — ${f.client}`,
      texte: `${argent(f.solde)} reçus pour ${f.stage}.`,
      points: ["La facture passe en payée et plus aucun rappel ne partira."],
      valider: "Oui, c'est encaissé",
    })) return;
    setOccupe("especes");
    travail.demarrer({ titre: "Encaissement du stage", detail: `Pour : ${f.client}`,
      attente: `${argent(f.solde)} reçus — la facture passe en payée…` });
    try {
      await api("/stages/paiement", {
        method: "POST",
        body: JSON.stringify({ cle: f.cle_stage, montant: f.solde, mode: "Virement" }),
      });
      await charger(semestre, saison);
      onChangement?.();
      travail.arreter();
    } catch (e) {
      setErr(`Encaissement interrompu : ${e.message}`);
      await travail.conclure({ titre: "Encaissement interrompu", ton: "danger",
        texte: `${f.client} — ${raisonErreur(e)}` });
    }
    setOccupe("");
  };

  const envoyer = async (factures) => {
    const lot = factures.filter((f) => f.pdf);
    const numeros = lot.filter((f) => f.genre !== "stage").map((f) => f.pdf_numero);
    const clesStages = lot.filter((f) => f.genre === "stage").map((f) => f.cle_stage);
    if (!lot.length) return;
    if (!await demander({
      titre: `Envoyer ${lot.length} facture(s)`,
      ton: data.mode_reel ? "attention" : "info",
      texte: data.mode_reel
        ? "Les factures partent AUX FAMILLES, avec leur QR-facture."
        : "MODE TEST : aucun e-mail n'est parti, ni aux familles ni ailleurs.",
      valider: "Envoyer",
    })) return;
    setOccupe("envoi");
    /* Le SERVEUR envoie tout le lot en un seul appel, facture par facture : fermer la page
       ne l'arrête pas, mais l'école perdait le bilan (qui n'est pas parti, et pourquoi) et
       pouvait toucher, pendant ce temps, à une facture en train de partir. La fenêtre
       bloque la console jusqu'au bilan. */
    const envois = [], echecs = [], attestations = [];
    let bilanLu = false;
    // Le serveur répond par numéro de facture : on remet le nom de la famille en face.
    const nomDe = (e) => {
      const f = lot.find((x) => (e.cle && x.cle_stage === e.cle) || (e.numero && x.pdf_numero === e.numero));
      const nom = e.client || f?.client || e.numero || e.cle || "";
      return f && f.genre !== "stage" && e.numero ? `${nom} (${e.numero})` : nom;
    };
    travail.demarrer({
      titre: lot.length > 1 ? "Envoi des factures" : "Envoi de la facture",
      total: lot.length,
      attente: lot.length > 1 ? `${lot.length} factures en cours d'envoi` : "Facture en cours d'envoi",
      detail: `Pour : ${quelquesNoms(lot.map((f) => f.client))}`,
    });
    try {
      if (numeros.length) {
        const r = await api("/facturation/envoyer", { method: "POST", body: JSON.stringify({ refs: numeros }) });
        envois.push(...r.envois);
        echecs.push(...r.echecs);
        /* L'attestation reprend les montants de la facture : celles qui étaient déjà
           parties avec un autre montant sont refaites tout de suite, et attendent dans
           « À traiter » qu'on décide de les envoyer. */
        attestations.push(...(r.attestations_pretes || []));
      }
      if (clesStages.length) {
        if (numeros.length) {
          travail.avancer({ attente: `${clesStages.length} facture(s) de stage en cours d'envoi` });
        }
        const rs = await api("/stages/envoyer", { method: "POST", body: JSON.stringify({ cles: clesStages }) });
        envois.push(...rs.envois);
        echecs.push(...(rs.echecs || []).map((e) => ({ numero: e.cle || "", motif: e.motif,
          cle: e.cle || "", client: e.client || "" })));
      }
      await travail.conclure({
        titre: `${envois.length} facture(s) envoyée(s)`,
        ton: echecs.length ? "attention" : "succes",
        texte: data.mode_reel ? "Les familles ont reçu leur facture." : "MODE TEST : rien n'est parti aux familles.",
        reussis: { libelle: ["envoyée", "envoyées"], noms: envois.map((x) => x.client) },
        erreurs: { libelle: ["en erreur", "en erreur"],
          items: echecs.map((e) => ({ nom: nomDe(e), motif: e.motif })) },
        points: [
          ...attestations.filter((a) => !a.echec).map((a) => `Nouvelle attestation prête pour ${a.eleve} — ${argent(a.montant)} au lieu de ${argent(a.montant_precedent)} : à envoyer depuis « À traiter »`),
          ...attestations.filter((a) => a.echec).map((a) => `Attestation à refaire à la main : ${a.eleve || a.ref} — ${a.echec}`),
        ],
      });
      bilanLu = true;
      setOccupe("actualisation");
      setChoisis([]);
      await charger(semestre, saison);
      onChangement?.();
      setVue("envoyee");
    } catch (e) {
      setErr(`Envoi interrompu : ${e.message}`);
      if (!bilanLu) {
        await travail.conclure({
          titre: "Envoi interrompu", ton: "danger",
          texte: `${raisonErreur(e)}\nUne partie des factures est peut-être partie avant l'incident. `
            + "Recharge la page : celles qui sont parties sont dans « En attente de paiement ». "
            + "Renvoyer les autres ne fait pas de doublon : une facture déjà partie est refusée.",
          reussis: { libelle: ["envoyée", "envoyées"], noms: envois.map((x) => x.client) },
          erreurs: { libelle: ["en erreur", "en erreur"],
            items: echecs.map((x) => ({ nom: nomDe(x), motif: x.motif })) },
        });
      }
    }
    setOccupe("");
  };

  return (
    <div className="space-y-7" data-testid="console-facturation">
      {/* Un recalcul + un PDF prennent plusieurs secondes : sans repère, l'école croyait
          que son clic n'avait pas pris et rien ne bougeait jusqu'au message final. */}
      {/* Générer, envoyer, relancer, encaisser : la fenêtre de progression grise toute la
          console jusqu'au bilan. La petite pastille ne reste que pour les vérifications
          éclair (relevé bancaire) et le rechargement de la liste après le bilan. */}
      {travail.fenetre}
      {occupe && !travail.actif && <BarreAvancement texte={LIBELLES_OCCUPE[occupe] || "Traitement en cours…"} />}
      <EnTeteMenu ton="argent" Icon={Receipt} contexte={`QR-facture suisse · ${data.etiquette || data.semestre_label || `semestre ${semestre}`}`} titre="Facturation"
        testid="facturation-entete"
        points={[
          [Pencil, "1 · Corriger", "Les montants, si besoin"],
          [FileText, "2 · Générer", "Une facture par famille, puis relire le PDF"],
          [Send, "3 · Envoyer", "Par e-mail, avec la QR-facture"],
          [ListChecks, "4 · Suivre", "Paiements et rappels"],
        ]}>
        <SelecteurSaison saison={saison}
          onChange={async (s) => {
            if (!s || s === saison) return;
            const precedent = saison;
            setChoisis([]);
            setErr("");
            setSaison(s);
            try {
              await charger(semestre, s);
            } catch (e) {
              // Une saison sans facturation (année à venir) ne doit pas faire disparaître
              // l'écran : on explique et on revient sur la saison précédente.
              setErr(`${e.message} — retour à la saison ${String(precedent).replace("-", "/")}.`);
              setSaison(precedent);
              await charger(semestre, precedent).catch(() => {});
            }
          }} />
        <Puces testid="facturation-semestre" valeur={String(semestre)}
          options={[{ valeur: "1", label: "Semestre 1", nb: semestre === 1 ? "en cours" : "voir" },
                    { valeur: "2", label: "Semestre 2", nb: semestre === 2 ? "en cours" : "voir" }]}
          onChange={(s) => { setChoisis([]); charger(Number(s), saison); }} />
        {/* Les outils, à droite : ils ne changent pas ce que montre la liste. */}
        <span className="flex flex-wrap items-center gap-2 md:ml-auto">
          <button className="btn-ghost" data-testid="fact-regles-calcul" onClick={() => setCalculs(true)}>
            <Calculator size={14} /> Comment c'est calculé
          </button>
          <button className="btn-ghost" data-testid="fact-extrait-impots" onClick={() => setExtrait(true)}>
            <FileSpreadsheet size={14} /> Extrait pour les impôts
          </button>
          <button className="btn-ghost" data-testid="fact-importer-releve" onClick={() => setReleve(true)}>
            <Upload size={14} /> Importer un relevé bancaire
          </button>
        </span>
      </EnTeteMenu>

      {err && (
        <div className="rounded-2xl px-5 py-4 text-sm" data-testid="facturation-erreur-saison"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
          {err}
        </div>
      )}

      {data.lecture_seule && <BandeauSaisonClose etiquette={data.etiquette} />}

      {/* Saison ou semestre sans aucune facture (début de saison) : l'écran reste là, avec
          les stages et l'import d'un relevé, au lieu d'un simple message d'erreur. */}
      {data.vide && (
        <div className="rounded-2xl px-5 py-4 text-sm" data-testid="facturation-vide"
          style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
          Aucune facture de cours pour {data.etiquette || "ce semestre"} pour l'instant : elles
          apparaîtront ici avec les inscriptions. Les stages et l'import d'un relevé restent disponibles.
        </div>
      )}

      {!data.mode_reel && (
        <div className="rounded-2xl px-5 py-4 flex items-center gap-3" data-testid="facturation-mode-test"
          style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <AlertTriangle size={18} style={{ color: "#B45309" }} />
          <div className="text-sm" style={{ color: "#92400E" }}>
            <strong>MODE TEST</strong> — aucun e-mail ne part : ni aux familles, ni sur une adresse de test.
          </div>
        </div>
      )}

      <AlertesFacturation base={base} nbEspeces={nbEspeces} onAller={(v) => { setVue(v); setChoisis([]); }}
        onReleve={() => setReleve(true)} />

      {/* Les parrainages et le cash ne sont pas des états de facture : ce sont des écrans à
          part, rangés à côté des genres de factures plutôt que parmi les filtres de la liste
          (19 et 20.10.2026). */}
      <div className="flex flex-wrap items-center gap-3" data-testid="fact-source">
        <Puces testid="fact-type" valeur={ECRANS_A_PART.includes(vue) ? "" : genre}
          options={[{ valeur: "tous", label: "Toutes les factures", nb: lignesCours.length + lignesStages.length },
                    { valeur: "cours", label: "Cours semestriels", nb: lignesCours.length },
                    { valeur: "stage", label: "Stages", nb: lignesStages.length }]}
          onChange={(v) => { setGenre(v); setChoisis([]); if (ECRANS_A_PART.includes(vue)) setVue("a_generer"); }} />
        <span className="hidden sm:block w-px h-6" style={{ background: "var(--border)" }} />
        {[{ id: "especes", Icon: Banknote, label: "Cash / espèces", nb: nbEspeces, testid: "fact-vue-cash" },
          { id: "parrainages", Icon: Gift, label: "Parrainages", testid: "fact-vue-parrainages" },
          { id: "cartes10", Icon: Ticket, label: "Cartes 10 cours", testid: "fact-vue-cartes10" },
          ...(chantiers.cours_prives ? [{ id: "prives", Icon: CalendarClock, label: "Cours privés", testid: "fact-vue-prives" }] : [])].map((b) => (
          <button key={b.id} type="button" data-testid={b.testid}
            onClick={() => { setVue(b.id); setChoisis([]); setQ(""); }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs transition-transform hover:-translate-y-0.5"
            style={vue === b.id
              ? { background: "var(--primary)", color: "#04202A", border: "1px solid var(--primary)", fontWeight: 600 }
              : { background: "#fff", color: "var(--muted)", border: "1px solid var(--border)" }}>
            <b.Icon size={13} /> {b.label}
            {b.nb ? <span className="ml-0.5 px-1.5 rounded-full text-[10px] font-semibold"
              style={{ background: "#FEF3C7", color: "#92400E" }}>{b.nb}</span> : null}
          </button>
        ))}
        {!ECRANS_A_PART.includes(vue) && (
          <span className="text-xs text-[var(--muted)]">
            Cours et stages suivent le même circuit (générer → envoyer → encaisser → relancer).
            Les stages ont leur propre numérotation (F-…ST-…) et n'entrent pas dans les totaux du semestre.
          </span>
        )}
      </div>

      <>
      {!ECRANS_A_PART.includes(vue) && (<>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Une facture à corriger est une facture à refaire (Jen, 02.11.2026 : « quand ils
            arrivent dans Facturation, ils voient qu'il y a 4 factures à corriger et générer ») :
            la carte les compte toutes et ouvre d'abord « À corriger » s'il y en a. */}
        <CarteIcone titre={compte("bloquee") ? "À générer ou corriger" : "À générer"}
          valeur={compte("a_generer") + compte("bloquee")}
          detail={compte("bloquee")
            ? <>{compte("a_generer")} à générer ·{" "}
                <strong style={{ color: "#B91C1C" }} data-testid="fact-pretes-a-corriger">{compte("bloquee")} à corriger</strong></>
            : argent(somme((f) => f.statut === "a_generer"))}
          Icon={FileText} teinte="bleu" onClick={() => setVue(compte("bloquee") ? "bloquee" : "a_generer")}
          testid="fact-pretes" />
        <CarteIcone titre="Générées, à envoyer" valeur={compte("generee")}
          detail={argent(somme((f) => f.statut === "generee"))}
          Icon={Send} teinte="violet" onClick={() => setVue("generee")} testid="fact-generees" />
        {/* On annonce le montant FACTURÉ et, séparément, ce qui reste dû : un acompte déjà
            reçu rendait ces chiffres incomparables avec le tableau de bord (02.10.2026). */}
        <CarteIcone titre="En attente de paiement" valeur={compte("envoyee") + compte("partielle")}
          detail={`${argent(somme((f) => ["envoyee", "partielle"].includes(f.statut)))} facturés · ${argent(
            base.filter((f) => ["envoyee", "partielle"].includes(f.statut))
              .reduce((t, f) => t + Math.max((f.total || 0) - (f.recu || 0), 0), 0))} restent dus`}
          Icon={Clock} teinte="ambre" onClick={() => setVue("envoyee")} testid="fact-attente" />
        <CarteIcone titre="Payées" valeur={compte("payee")}
          detail={`${argent(base.filter((f) => f.statut === "payee")
            .reduce((t, f) => t + (f.recu || 0), 0))} encaissés · ${argent(
            base.reduce((t, f) => t + (f.recu || 0), 0))} au total avec les acomptes`}
          Icon={CheckCircle2} teinte="vert" onClick={() => setVue("payee")} testid="fact-payees" />
      </div>

      {/* Deux comptes, deux relevés : afficher une seule date laissait croire que tout
          était pointé alors qu'un des deux comptes était en retard. */}
      <p className="text-xs text-[var(--muted)]" data-testid="fact-date-releve">
        {(dernierReleve?.comptes || []).length
          ? <>
              Montants reçus connus jusqu'au :{" "}
              {dernierReleve.comptes.map((c, k) => (
                <span key={c.cle}>
                  {k ? " · " : ""}{c.libelle}{" "}
                  <strong>{c.date_releve ? jourFr(c.date_releve) : "aucun relevé"}</strong>
                </span>
              ))}
              .{!dernierReleve.valide && " Un des deux relevés n'est plus à jour : importe-le avant de relancer."}
            </>
          : dernierReleve?.present
            ? "Le dernier relevé bancaire importé ne porte aucune date lisible : impossible de dire jusqu'à quand les paiements sont connus."
            : "Aucun relevé bancaire importé : les montants reçus ne sont pas encore pointés."}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Puces testid="fact-vues" valeur={vue}
          options={VUES.map((v) => ({ valeur: v.id, label: v.label, nb: compte(v.id) }))}
          onChange={(v) => { setVue(v); setChoisis([]); }} />
        {/* `flex-wrap` : sur téléphone, la recherche passe sous le bouton du suivi cash au
            lieu de déborder de l'écran. */}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <Recherche valeur={q} onChange={setQ} largeur={300} testid="fact-recherche"
            placeholder="Chercher une facture : famille, élève, n°…" />
          {cherche && (
            <span className="text-xs font-semibold" style={{ color: "var(--primary)" }} data-testid="fact-recherche-active">
              {items.length} résultat(s) sur toutes les factures
            </span>
          )}
        </div>
      </div>
      </>)}

      {/* Les relances en un coup d'œil : combien de familles sont en retard (le serveur l'a
          décidé), un seul bouton pour leur écrire, et pourquoi les autres factures en attente
          ne reçoivent rien aujourd'hui. Plus de filtre à penser à cocher (09.10.2026). */}
      {vueRelances && (
        <div className="rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid="fact-relances-panneau"
          style={{ background: enRetard.length ? "#FEF2F2" : "#F0FDF4",
            border: `1px solid ${enRetard.length ? "#FECACA" : "#BBF7D0"}` }}>
          <div className="min-w-0 flex-1" style={{ minWidth: 220 }}>
            <div className="text-sm font-semibold flex items-center gap-2"
              style={{ color: enRetard.length ? "#991B1B" : "#166534" }} data-testid="fact-relances-compte">
              <BellRing size={15} />
              {enRetard.length
                ? `${enRetard.length} famille(s) en retard de paiement à relancer`
                : "Aucune famille à relancer aujourd'hui"}
            </div>
            {sansRappel.length > 0 && (
              <div className="text-xs mt-1 text-[var(--text)]" data-testid="fact-relances-exclues">
                {sansRappel.length} en attente sans rappel aujourd'hui, exclue(s) d'office :{" "}
                {pourquoiPas(sansRappel).join(" · ")}
              </div>
            )}
            <div className="text-[11px] mt-1 text-[var(--muted)]">
              La console choisit : échéance passée (ou mensualité d'un paiement échelonné pas
              reçue), ni espèces, ni facture à corriger, ni relancée depuis moins
              de {data.items.find((x) => x.delai_entre_relances_jours)?.delai_entre_relances_jours || 14} jours.
              {dernierReleve && !dernierReleve.valide && (
                <strong style={{ color: "#B45309" }}> Relevé bancaire pas à jour : importe-le avant de relancer.</strong>
              )}
            </div>
          </div>
          {enRetard.length > 0 && (
            <BoutonSombre testid="fact-relancer-en-retard" disabled={occupe === "relance"}
              onClick={() => relancer(enRetard)}>
              {occupe === "relance" ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              {enRetard.length > 1 ? `Relancer les ${enRetard.length} familles en retard` : "Relancer la famille en retard"}
            </BoutonSombre>
          )}
        </div>
      )}

      {/* Affiner une campagne : jamais relancées (premier rappel) ou déjà relancées (2e
          rappel), et la date d'envoi de la facture. Le reste — échéance, échelonnés à jour,
          espèces, délai entre deux rappels — la console le fait seule. */}
      {vueRelances && (
        <div className="flex flex-wrap items-center gap-2 -mt-1" data-testid="fact-filtre-relance">
          {/* Les deux listes des impayés côte à côte (école, 26.10.2026) : les familles en
              retard seulement, ou toutes celles qui attendent un paiement. */}
          <span className="text-xs font-semibold text-[var(--muted)]">Afficher</span>
          {[{ id: "a_relancer", label: "À relancer" }, { id: "envoyee", label: "Toutes en attente" }]
            .map(({ id, label }) => (
              <button key={id} className="btn-ghost text-xs" data-testid={`fact-portee-${id}`}
                onClick={() => { setVue(id); setChoisis([]); }}
                style={vue === id ? { background: "#0F172A", color: "#F8FAFC" } : undefined}>
                {label} ({compte(id)})
              </button>
            ))}
          <span className="text-xs font-semibold text-[var(--muted)] ml-2">Affiner</span>
          {[{ id: "jamais", label: "Jamais relancées" }, { id: "deja", label: "Déjà relancées" }]
            .map(({ id, label }) => {
              const actif = relanceFiltre === id;
              const nb = base.filter((f) => (vue === "a_relancer" ? aRelancer(f) : listeDe(f) === "envoyee"))
                .filter((f) => (id === "jamais" ? !f.relance_envoyee_le : !!f.relance_envoyee_le)).length;
              return (
                <button key={id} className="btn-ghost text-xs" data-testid={`fact-relance-${id}`}
                  onClick={() => { setRelanceFiltre(actif ? "" : id); setChoisis([]); }}
                  style={actif ? { background: "#0F172A", color: "#F8FAFC" } : undefined}>
                  {label} ({nb})
                </button>
              );
            })}
          <span className="text-xs text-[var(--muted)] ml-1" data-testid="fact-filtre-envoi">factures envoyées du</span>
          <input type="date" value={envoiDu} data-testid="fact-envoi-du" aria-label="Envoyées depuis le"
            onChange={(e) => { setEnvoiDu(e.target.value); setChoisis([]); }}
            className="text-xs rounded-full border px-2 py-1 bg-white"
            style={{ borderColor: "var(--border)" }} />
          <span className="text-xs text-[var(--muted)]">au</span>
          <input type="date" value={envoiAu} data-testid="fact-envoi-au" aria-label="Envoyées jusqu'au"
            onChange={(e) => { setEnvoiAu(e.target.value); setChoisis([]); }}
            className="text-xs rounded-full border px-2 py-1 bg-white"
            style={{ borderColor: "var(--border)" }} />
          {(fenetreActive || relanceFiltre) && (
            <>
              <span className="text-xs font-semibold" style={{ color: "var(--primary)" }}
                data-testid="fact-envoi-compte">
                {items.length} facture(s)
              </span>
              <button className="btn-ghost text-xs" data-testid="fact-filtres-effacer"
                onClick={() => { setEnvoiDu(""); setEnvoiAu(""); setRelanceFiltre(""); setChoisis([]); }}>
                Tout afficher
              </button>
            </>
          )}
        </div>
      )}

      {vue === "prives" ? <FacturesPrivees /> : vue === "cartes10" ? <CartesDix integre onAller={onAller} /> : vue === "especes" ? (
        /* Cash / espèces : une seule liste pour tout ce qui se paie au comptoir, avec les
           mêmes colonnes et les mêmes boutons (Caisse.jsx, 20.10.2026). */
        <VueCash liste={especes} occupe={occupe}
          genreDepart={genre === "cours" ? "facture" : genre === "stage" ? "stage" : "tous"}
          onEncaisserLot={(lot) => confirmerEspeces(lot)}
          onOuvrir={ouvrirDepuisCash} onVoirFacture={voirFactureDepuisCash}
          onVoirCartes={() => setVue("cartes10")}
          onChange={async () => { await charger(semestre, saison); onChangement?.(); }} />
      ) : (
      <Section titre={cherche ? `Recherche : ${items.length} facture(s)` : (VUES.find((v) => v.id === vue)?.label || (vue === "parrainages" ? "Parrainages" : ""))} testid="section-factures"
        aide={vue === "parrainages"
          ? "Qui a parrainé qui, le montant réellement appliqué, et où en est la facture du parrain. Un bon non déduit part sur la facture du semestre suivant ; les cas douteux sont dans « À clarifier » et remontent dans « À traiter »."
          : vue === "bloquee"
            ? "Tout ce qui demande une correction, en une seule liste : les factures bloquées (il manque une information pour les faire partir), celles dont les montants ont changé après coup — changement de cours, désinscription, cours ajouté — et celles déjà envoyées ou payées dont le montant n'est plus le bon. Chaque ligne dit pourquoi elle est là."
            : vue === "ecarts"
              ? "Les versements du relevé bancaire qui ne couvraient pas exactement la facture : montant supérieur ou paiement partiel, avec l'état actuel de la facture."
              : vue === "echelonnes"
                ? "Toutes les familles avec un paiement en plusieurs fois : combien de versements sont arrivés, la part mensuelle et l'échéance. Tant que les mensualités échues sont payées, aucun rappel ne part ; une mensualité en retard, elle, repart en relance."
                : vueRelances
                  ? "Chaque ligne dit si un rappel est dû aujourd'hui, et sinon pourquoi. « À relancer » montre les familles en retard, « Toutes en attente » toutes celles qui doivent encore payer. Les familles en retard et celles qu'on peut relancer « quand même » (pas encore échues, échelonnées à jour, relancées il y a peu) ont une case à cocher : la console demande confirmation avant d'écrire à une famille qui n'est pas en retard. Jamais de rappel à une facture payée, en espèces, à corriger ou à vérifier."
                  : "Le décompte de chaque famille est recomposé : il redonne toujours son total."}>
        {selectionnables.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-1">
            <Case cochee={tous} onChange={(v) => setChoisis(v ? selectionnables.map((f) => f.cle) : [])}
              testid="fact-tout-selectionner" titre="Tout sélectionner" />
            <span className="text-xs text-[var(--muted)]" data-testid="fact-tout-selectionner-libelle">
              {["envoyee", "a_relancer"].includes(vue)
                ? (() => {
                  const quandMeme = selectionnables.filter(relanceALaMain).length;
                  const retard = selectionnables.length - quandMeme;
                  return quandMeme
                    ? `Tout sélectionner : ${selectionnables.length} famille(s) — ${retard} en retard, ${quandMeme} à relancer « quand même »`
                    : `Tout sélectionner : les ${selectionnables.length} famille(s) en retard`;
                })()
                : `Tout sélectionner (${selectionnables.length})`}
            </span>
            {["envoyee", "a_relancer"].includes(vue) && items.length > selectionnables.length && (
              <span className="text-xs" style={{ color: "#1D4ED8" }} data-testid="fact-exclues-selection">
                {items.length - selectionnables.length} jamais relancée(s) : {pourquoiPas(
                  items.filter((f) => !selectionnable(f))).join(" · ")}
              </span>
            )}
          </div>
        )}

        {choisis.length > 0 && (
          <BarreSelection nb={choisis.length} testid="fact-barre">
            <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
              {argent(items.filter((f) => choisis.includes(f.cle)).reduce((t, f) => t + f.total, 0))}
            </span>
            {["a_generer", "tous", "non_facturee"].includes(vue) && (
              <BoutonSombre onClick={() => generer(choisis)} disabled={occupe === "generation"} testid="fact-generer">
                {occupe === "generation" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {occupe === "generation" ? "Génération…" : "Générer les factures"}
              </BoutonSombre>
            )}
            {["generee", "tous"].includes(vue) && (
              <BoutonSombre onClick={() => envoyer(items.filter((f) => choisis.includes(f.cle) && f.pdf))}
                disabled={occupe === "envoi"} testid="fact-envoyer-lot">
                <Send size={14} /> Envoyer
              </BoutonSombre>
            )}
            {["especes", "tous"].includes(vue) && (
              <BoutonSombre
                onClick={() => confirmerEspeces(items.filter((f) => choisis.includes(f.cle) && f.especes_a_confirmer))}
                disabled={occupe === "especes"} testid="fact-confirmer-especes">
                {occupe === "especes" ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                Passer en payées (espèces)
              </BoutonSombre>
            )}
            {vue === "tous" && (
              <BoutonSombre
                onClick={() => remettreAGenerer(items.filter((f) => choisis.includes(f.cle)
                  && f.version_perimee))}
                disabled={occupe === "remise"} testid="fact-remettre-a-generer">
                {occupe === "remise" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Remettre dans « À générer »
              </BoutonSombre>
            )}
            {["envoyee", "a_relancer", "tous"].includes(vue) && (() => {
              /* Les familles en retard de la sélection partent ; dans les impayés, celles
                 cochées « quand même » aussi, après confirmation (le serveur revérifie). */
              const choisies = items.filter((f) => choisis.includes(f.cle));
              const lot = choisies.filter(aRelancer);
              const quandMeme = vueRelances ? choisies.filter(relanceALaMain) : [];
              const nb = lot.length + quandMeme.length;
              return (
                <BoutonSombre onClick={() => (quandMeme.length ? relancerSelection(lot, quandMeme) : relancer(lot))}
                  disabled={occupe === "relance" || !nb} testid="fact-relancer-lot">
                  {occupe === "relance" ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
                  {!nb ? "Aucune famille à relancer"
                    : quandMeme.length ? `Relancer ${nb} famille(s) (${quandMeme.length} pas en retard)`
                    : `Relancer ${nb} famille(s) en retard`}
                </BoutonSombre>
              );
            })()}
          </BarreSelection>
        )}

        {vue === "parrainages" ? (
          <Parrainages semestre={semestre} saison={saison} />
        ) : vue === "ecarts" ? (
          <div className="space-y-5">
          <AVerifierEcarts
            items={[...lignesCours, ...lignesStages].filter((f) => f.ecart_a_verifier && f.statut !== "annulee")}
            onEchelonner={(f) => (f.genre === "stage"
              ? setEchelonStage({ ref: `stage::${f.cle_stage}`, genre: "stage", titre: f.client, quoi: f.stage,
                total: f.total || 0, recu: f.recu || 0,
                reste: Math.max(Math.round(((f.total || 0) - (f.recu || 0)) * 100) / 100, 0), echelonnement: null })
              : setEchelon(f))}
            onPrixSpecial={(f) => { setPrixSpecial(true); if (f.genre === "stage") setModaleStage(f); else setModale(f); }}
            onIlManque={async (f) => {
              if (!await demander({
                titre: `Il manque de l'argent — ${f.client}`,
                texte: `${argent(f.recu)} reçus sur ${argent(f.total)} : il reste ${argent((f.total || 0) - (f.recu || 0))}.`,
                points: ["La facture reprend le circuit normal : le prochain rappel réclamera le reste.",
                  "Si c'était un paiement en plusieurs fois ou un prix spécial, choisis plutôt ces boutons."],
                valider: "Oui, il manque de l'argent",
              })) return;
              try {
                await api("/facturation/ecarts/il-manque", { method: "POST", body: JSON.stringify({
                  ref: f.genre === "stage" ? `stage::${f.cle_stage}` : (f.postes || []).map((p) => p.id).join(",") }) });
                await charger(semestre, saison);
                onChangement?.();
              } catch (e) {
                setErr(e.message);
              }
            }} />
          <EcartsPaiement
            onEchelonner={(e) => {
              // Une famille qui verse moins que prévu a souvent obtenu un arrangement :
              // on ouvre l'échelonnement sans la chercher à la main.
              const f = base.find((x) => x.numero && x.numero === e.numero)
                || base.find((x) => x.client === (e.client || e.payeur));
              if (f) setEchelon(f);
              else {
                setQ(e.client || e.payeur || e.numero || "");
                setVue("tous");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            onOuvrirFamille={(e) => {
            // On cherche par NOM : le numéro écrit sur le virement peut être celui d'une
            // seconde demande de la famille, alors que la facture n'en affiche qu'un.
            setQ(e.client || e.payeur || e.numero || "");
            setVue("tous");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }} />
          </div>
        ) : vue === "bloquee" && !cherche ? (
          <div className="space-y-4" data-testid="liste-corrections">
            {items.length === 0 && <p className="text-sm text-[var(--muted)]">Aucune facture à corriger.</p>}
            {items.map((f) => (
              <CarteCorrection key={f.cle} facture={f} occupe={occupe}
                onAccepter={() => accepterCorrection(f)} onModifier={() => setModale(f)}
                onRefaire={() => generer([f.cle], true)}
                onChoisir={(mode) => accepterCorrection(f, mode)} />
            ))}
          </div>
        ) : (
        <Tableau
          testid="tableau-factures"
          fixe
          entetes={["", { label: "Famille", largeur: "21%" }, { label: "Cours", largeur: "19%" },
            { label: "Total", droite: true, largeur: "8%" }, { label: "Reçu", droite: true, largeur: "6%" },
            { label: `Échéance${items.find((f) => f.delai_paiement_jours) ? ` (${items.find((f) => f.delai_paiement_jours).delai_paiement_jours} j)` : ""}`, largeur: "10%" },
            { label: "Statut", largeur: "12%" }, { label: "", largeur: "20%" }]}
          lignes={visibles.map((f) => ({ ...f, id: f.cle }))}
          surligne={(f) => !!cibleFamille && f.cle === cibleFamille}
          vide="Aucune facture dans cette vue."
          rendu={(f) => (
            <>
              <Cellule>
                {selectionnable(f) ? (
                  <Case cochee={choisis.includes(f.cle)} testid="fact-case"
                    onChange={(v) => setChoisis(v ? [...choisis, f.cle] : choisis.filter((x) => x !== f.cle))} />
                ) : vueRelances && f.relance ? (
                  /* Pas de case : cette famille n'est pas à relancer aujourd'hui. */
                  <span className="text-[var(--muted)] text-xs" title={`Pas de rappel aujourd'hui : ${f.relance.motif}`}
                    data-testid="fact-case-absente">–</span>
                ) : null}
              </Cellule>
              <Cellule fort>
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  {f.client}
                  {f.genre === "stage" && <Badge ton="violet">Stage</Badge>}
                  {/* Pourquoi un rappel est dû — ou pourquoi pas : la décision du serveur, en
                      quelques mots, à côté du nom (visible aussi sur téléphone, où le tableau
                      glisse) : « en retard de 12 j », « échelonné à jour — prochaine le
                      05.12 », « relancée il y a 3 j », « espèces — au comptoir ». */}
                  {motifVisible(f) && (
                    <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-lg border leading-snug"
                      title={f.relance.motif}
                      data-testid={aRelancer(f) ? "fact-relance-due" : "fact-relance-motif"}
                      style={aRelancer(f)
                        ? { background: "#FEF2F2", borderColor: "#FECACA", color: "#B91C1C" }
                        : { background: "#F1F5F9", borderColor: "#E2E8F0", color: "#475569" }}>
                      {aRelancer(f) ? "À relancer · " : ""}{f.relance.badge}
                    </span>
                  )}
                  {/* Corrigée après un envoi : la famille a déjà l'ancienne version (cas Piras). */}
                  {f.renvoi && <span data-testid="fact-a-renvoyer"><Badge ton="ambre">Corrigée — à renvoyer</Badge></span>}
                </span>
                <div className="text-xs font-normal text-[var(--muted)]">
                  {(f.eleves || []).length > 0 && f.genre !== "stage" && (
                    <span className="block" data-testid="fact-eleves">{f.eleves.length > 1 ? "Élèves" : "Élève"} : {f.eleves.join(", ")}</span>
                  )}
                  {f.renvoi && (
                    <span className="block" style={{ color: "#B45309" }} data-testid="fact-renvoi">
                      La famille a reçu la version d'avant{f.renvoi.total ? ` (${argent(f.renvoi.total)})` : ""}{f.renvoi.le ? ` le ${jourFr(f.renvoi.le)}` : ""} : celle-ci la remplace, à renvoyer.
                    </span>
                  )}
                  {f.genre === "stage" ? "Stage" : `Semestre ${f.semestre || semestre}`} · {f.email}{(f.emails_secondaires || []).length > 0 ? ` (aussi ${f.emails_secondaires.join(", ")})` : ""} · n° {f.numero || "à créer"}{f.pdf_version > 1 ? ` · version ${f.pdf_version}` : ""}{f.motif ? ` · ${f.motif}` : ""}
                  {/* L'ordre de la liste « À envoyer » est celui de la fabrication : on dit
                      donc quand chaque facture a été faite, sinon l'ordre paraît arbitraire. */}
                  {/* Les colonnes disent déjà l'envoi, l'échéance et le dernier rappel. */}
                  {vue === "generee" && f.pdf_genere_le && (
                    <span data-testid="fact-genere-le"> · générée le {jourFr(f.pdf_genere_le)}</span>
                  )}
                  {/* En recherche, on dit dans QUELLE liste la facture se trouve : c'est
                      la question qu'on se pose en la trouvant par son nom. */}
                  {cherche && (
                    <span data-testid="fact-liste">
                      {f.statut === "annulee"
                        ? " · annulée — hors des listes, visible par la recherche"
                        : <> · liste «&nbsp;
                          {VUES.find((v) => v.id === listeDe(f))?.label
                            || (listeDe(f) === "especes" ? "Cash / espèces" : "À corriger")}&nbsp;»</>}
                    </span>
                  )}
                </div>
              </Cellule>
              <Cellule muet>
                {f.genre === "stage"
                  ? `${f.stage}${f.dates ? ` · ${f.dates}` : ""}`
                  : (f.detail.map((d) => d.cours).join(", ") || "—")}
                {/* Prorata, rabais, ajustement d'un stage : ce que « Modifier » a noté, ligne
                    par ligne comme sur la facture — le total seul ne disait pas pourquoi. */}
                {f.genre === "stage" && (f.corrections || []).length > 0 && (
                  <div className="text-[11px] mt-1" data-testid="fact-stage-corrections">
                    {f.corrections.map((c) => (
                      <span key={c.genre} className="block">{c.libelle} · {chfSigne(c.montant)}</span>
                    ))}
                  </div>
                )}
                {/* Bulletin de versement d'un autre compte que l'école : sous le cours,
                    et jamais de pastille vide (les stages n'ont pas de compte). */}
                {f.compte && f.compte !== "ecole" && f.crediteur && (
                  <div className="mt-1" data-testid="fact-badge-bv">
                    <Badge ton="ambre">{LIBELLE_BV[f.compte] || `BV ${f.crediteur}`}</Badge>
                  </div>
                )}
                {["non_facturee", "especes"].includes(f.statut) && f.raison && (
                  <div className="text-[11px] mt-1" style={{ color: "#B45309" }} data-testid="fact-raison">
                    Pourquoi : {f.raison}
                  </div>
                )}
              </Cellule>
              <Cellule droite fort>
                {argent(f.total)}
                <MentionParrainage f={f} />
                {/* L'impayé d'une saison terminée reste dans son année (il n'est pas ajouté à
                    cette facture) : l'école doit le voir avant de facturer celle-ci. */}
                {(f.dette_saisons_passees || 0) > 0.02 && (
                  <div className="text-[11px] font-normal mt-1" style={{ color: "#B91C1C" }}
                    data-testid="fact-dette-saisons-passees"
                    title={f.dette_saisons_passees_detail || ""}>
                    + {argent(f.dette_saisons_passees)} dus d'une saison passée
                  </div>
                )}
              </Cellule>
              <Cellule droite muet>{f.recu ? argent(f.recu) : "—"}</Cellule>
              <Cellule>
                {f.echeance ? (
                  <span data-testid="fact-echeance"
                    style={{ color: aRelancer(f) ? "#B91C1C" : "var(--muted)", fontSize: 12.5 }}>
                    {jourFr(f.echeance)}
                    {/* Corrigée puis renvoyée : l'échéance reste celle de la première facture. */}
                    {f.premier_envoi_le && (
                      <span className="block text-[10.5px] text-[var(--muted)]" data-testid="fact-echeance-premiere"
                        title={`Première facture envoyée le ${jourFr(f.premier_envoi_le)}, version corrigée renvoyée le ${jourFr(f.envoyee_le)}`}>
                        première facture
                      </span>
                    )}
                  </span>
                ) : <span className="text-[var(--muted)] text-xs">pas encore envoyée</span>}
              </Cellule>
              {/* Le statut et les boutons ont besoin de leur place : sans largeur minimale,
                  le badge « En attente de paiement » se retrouvait par-dessus « Relancer ». */}
              <Cellule largeur={150}>
                <BadgeStatut statut={f.statut} />
                {/* Le dernier rappel, en clair et sur toutes les vues : l'école venait
                    d'écrire à une famille sans aucun moyen de le voir ensuite. Et une
                    facture partie mais jamais relancée le dit aussi. */}
                {f.relance_envoyee_le ? (
                  <div className="text-[11.5px] mt-1 font-semibold" style={{ color: "#B45309" }}
                    data-testid="fact-dernier-rappel">
                    {f.relances_nb > 1 ? `${f.relances_nb} rappels · dernier le ` : "rappel envoyé le "}
                    {jourFr(f.relance_envoyee_le)}
                  </div>
                ) : f.envoyee_le && (f.total || 0) - (f.recu || 0) > 0.02 ? (
                  <div className="text-[11.5px] mt-1 text-[var(--muted)]"
                    data-testid="fact-jamais-relancee">
                    jamais relancée
                  </div>
                ) : null}
                {/* Le statut « bloquée » dit déjà « À corriger ». Ici, ce sont les factures
                    DÉJÀ parties (ou payées) dont un cours a changé : l'écart se rembourse
                    ou se crédite, ce n'est pas le même geste qu'une facture pas encore émise. */}
                {f.a_recalculer && f.statut !== "bloquee" && (
                  <div className="text-xs mt-1 font-semibold" style={{ color: "#B91C1C" }}
                    data-testid="fact-a-recalculer-badge">
                    à corriger — {["payee", "partielle"].includes(f.statut)
                      ? "déjà payée, écart à rembourser ou créditer"
                      : "un cours a changé après l'envoi"}
                  </div>
                )}
                {/* La famille a reçu un autre montant que celui d'aujourd'hui : cette
                    facture doit être refaite, sinon elle réglera l'ancien chiffre. */}
                {f.version_perimee && (
                  <div className="text-xs mt-1 font-semibold" style={{ color: "#B91C1C" }}
                    data-testid="fact-version-perimee">
                    corrigée depuis l'envoi — la famille a reçu {argent(f.total_version_envoyee)}
                  </div>
                )}
                {/* Facture déjà reçue par la famille : son montant est gelé. Le calcul
                    d'aujourd'hui donnerait autre chose (taxe recomptée, barème affiné),
                    mais ce n'est pas une correction à faire — juste une note discrète. */}
                {f.gelee && (
                  <div className="text-xs mt-1 text-[var(--muted)]" data-testid="fact-montant-gele">
                    montant gelé à {argent(f.total)} (reçu par la famille) · le calcul
                    d'aujourd'hui donnerait {argent(f.total_calcule)}
                    {/* L'école peut décider d'appliquer le calcul d'aujourd'hui (une réduction
                        accordée par erreur, par ex.) : nouvelle version, à envoyer ensuite. */}
                    {f.genre !== "stage" && (
                      <button className="block underline mt-0.5 text-left" data-testid="fact-refaire-calcul"
                        disabled={!!occupe}
                        onClick={async () => {
                          if (await demander({
                            titre: `Refaire la facture de ${f.client} à ${argent(f.total_calcule)} ?`,
                            texte: `La famille a reçu ${argent(f.total)}. La nouvelle version suit le calcul d'aujourd'hui.`,
                            points: ["L'ancienne version est archivée avec sa date d'envoi (bouton « Historique »).",
                              "La nouvelle passe dans « à envoyer » : la famille la reçoit quand tu l'envoies (avec le prochain rappel, par exemple).",
                              "L'échéance reste celle de la première facture."],
                            valider: `Refaire à ${argent(f.total_calcule)}`,
                          })) generer([f.cle], true, true);
                        }}>
                        Refaire la facture à {argent(f.total_calcule)}
                      </button>
                    )}
                  </div>
                )}
                {/* Une déduction qui risque d'être comptée deux fois : l'école doit le voir
                    sur la ligne, pas seulement en ouvrant les montants. */}
                {(f.avertissements || []).map((a, k) => (
                  <div key={k} className="text-xs mt-1" style={{ color: "#B45309" }}
                    data-testid="fact-avertissement">⚠ {a}</div>
                ))}
                {f.envoyee_le && <div className="text-xs text-[var(--muted)] mt-1">envoyée le {jourFr(f.envoyee_le)}</div>}
                {f.especes_a_confirmer && (
                  <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="fact-especes-badge">
                    espèces à confirmer
                  </div>
                )}
                {/* La carte 10 cours ne figure PAS sur cette facture : elle se règle sur
                    place et se suit dans la vue « Cash / espèces ». On le dit ici pour que
                    l'école ne cherche pas un montant manquant. */}
                {f.carte_10 && (
                  <div className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="fact-carte10-badge">
                    + carte 10 cours hors facture : {argent(f.cartes_10_total)} à encaisser sur place
                    {(f.cartes_10 || []).some((c) => c.taxe > 0) && " (taxe d'inscription comprise)"}
                  </div>
                )}
                {f.echelonnement_nb > 0 && (
                  /* Pas de `whitespace-nowrap` : la phrase dépassait de la colonne et
                     passait SOUS les boutons d'action (signalé par l'école le 02.10.2026). */
                  <div className="text-xs mt-1" data-testid="fact-echelonnement-badge"
                    style={{ color: f.echelonnement_en_retard ? "#B91C1C" : "#1D4ED8" }}>
                    Échelonné {f.echelonnement_nb}× · {f.echelonnement_recus}/{f.echelonnement_nb} reçu(s)
                    {" · "}{argent(f.echelonnement_part)}/mois
                    {/* « à jour · prochaine le … » ou « mensualité du … en retard » : c'est le
                        badge de relance juste au-dessus qui le dit, une seule fois. */}
                    {!motifVisible(f) && (f.echelonnement_en_retard ? (
                      <div className="font-semibold" data-testid="fact-echelonnement-retard">
                        mensualité du {jourFr(f.echelonnement_echeance_due)} pas reçue
                      </div>
                    ) : f.echelonnement_prochaine ? (
                      <div className="text-[var(--muted)]">
                        à jour · prochaine le {jourFr(f.echelonnement_prochaine)}
                      </div>
                    ) : null)}
                  </div>
                )}
              </Cellule>
              <Cellule largeur={230}>
                <ActionsFacture actions={[
                  { id: "fact-modifier", Icon: Pencil, label: "Modifier",
                    onClick: () => (f.genre === "stage" ? setModaleStage(f) : setModale(f)) },
                  /* Générer une seule facture depuis sa ligne : il fallait la cocher puis
                     descendre à la barre de sélection, et après une correction l'école ne
                     trouvait plus comment la fabriquer. */
                  /* Générer une seule facture depuis sa ligne : il fallait la cocher puis
                     descendre à la barre de sélection, et après une correction l'école ne
                     trouvait plus comment la fabriquer. Ces boutons suivent la LISTE de la
                     facture (et non son statut de paiement) : une facture jamais envoyée
                     qui portait un acompte n'avait plus aucun bouton pour partir. */
                  f.liste === "a_generer" && {
                    id: "fact-generer-une", Icon: FileText, label: "Générer", primaire: true,
                    onClick: () => generer([f.cle]), disabled: occupe === "generation" },
                  f.liste === "generee" && { id: "fact-envoyer-une", Icon: Send, label: "Envoyer",
                    primaire: true, onClick: () => envoyer([f]), disabled: occupe === "envoi" },
                  f.especes_a_confirmer && { id: "fact-especes-une", Icon: Banknote,
                    label: "Encaisser", primaire: true,
                    onClick: () => setEncaisserF(versCash(f)), disabled: occupe === "especes" },
                  /* « Relancer » pour une famille en retard ; « Relancer quand même… » (avec
                     confirmation) pour une famille que la console ne relancerait pas
                     aujourd'hui. Payée, espèces, à corriger : aucun bouton. */
                  f.liste === "envoyee" && aRelancer(f) && {
                    id: "fact-relancer-une", Icon: BellRing, label: "Relancer", primaire: true,
                    onClick: () => relancer([f]), disabled: occupe === "relance" },
                  f.liste === "envoyee" && relanceALaMain(f) && {
                    id: "fact-relancer-quand-meme", Icon: BellRing, label: "Relancer quand même…",
                    title: `Pas de rappel prévu aujourd'hui : ${f.relance.motif}`,
                    onClick: () => relancerQuandMeme(f), disabled: occupe === "relance" },
                  /* « Je ne l'ai pas reçue » : la même facture, renvoyée telle quelle. */
                  renvoyable(f) && {
                    id: "fact-renvoyer", Icon: Mail, label: "Renvoyer la facture",
                    title: "La même facture (même numéro, même QR), avec un court message — pas un rappel",
                    onClick: () => renvoyerFacture(f), disabled: occupe === "renvoi" },
                  /* La facture recalculée À L'INSTANT : c'est LA nouvelle facture, celle
                     qui portera les corrections. Avant, ce bouton n'existait que si un PDF
                     avait déjà été fabriqué — impossible de vérifier avant de générer. */
                  f.genre !== "stage" && !cash(f) && { id: "fact-apercu", Icon: Eye, label: "Facture à jour",
                    title: "La facture recalculée à l'instant, telle qu'elle partira — rien n'est enregistré",
                    onClick: () => ouvrirPdf(
                      `/facturation/apercu-pdf?cle=${encodeURIComponent(f.cle)}`
                      + `&semestre=${f.semestre || semestre}&saison=${encodeURIComponent(f.saison || saison || "")}`,
                      `Facture à jour — ${f.client}`) },
                  /* Les stages n'ont pas d'aperçu recalculé : leur PDF enregistré EST la
                     facture. Pour les cours, ce bouton faisait doublon avec « Facture à
                     jour » et avec la copie envoyée — d'où trois PDF pour une facture. */
                  f.pdf && f.genre === "stage" && { id: "fact-voir-pdf", Icon: FileText,
                    label: "Voir la facture",
                    onClick: () => ouvrirPdf(`/stages/pdf/${encodeURIComponent(f.pdf_numero)}`,
                      `Facture ${f.numero || f.pdf_numero} — ${f.client}`) },
                  f.copie_numero && { id: "fact-pdf-envoye", Icon: Eye,
                    label: f.copie_date
                      ? `Copie envoyée le ${jourFr(f.copie_date)}`
                      : "Copie envoyée à la famille",
                    alerte: Math.abs((f.copie_total || 0) - (f.total || 0)) > 0.02,
                    title: `Le PDF réellement reçu par la famille — ${argent(f.copie_total)}`
                      + (Math.abs((f.copie_total || 0) - (f.total || 0)) > 0.02
                        ? ` — le calcul d'aujourd'hui donne ${argent(f.total)}` : ""),
                    onClick: () => ouvrirPdf(
                      f.copie_source === "console"
                        ? `/facturation/pdf/${encodeURIComponent(f.copie_numero)}`
                        : f.copie_source === "version"
                          ? `/facturation/version/${encodeURIComponent(f.copie_numero)}/pdf`
                          : `/factures/archive/pdf?numero=${encodeURIComponent(f.copie_numero)}`,
                      `Facture envoyée ${f.numero || f.copie_numero} — ${f.client}`) },
                  /* L'historique s'ouvre aussi quand la seule facture précédente est
                     celle importée de l'ancien outil : c'est elle que l'école cherche. */
                  (f.pdf_version > 1 || f.pdf_archive) && {
                    id: "fact-historique", Icon: History, label: "Historique",
                    onClick: () => setVersions(f) },
                  f.genre === "stage" && f.solde > 0.02 && f.statut !== "a_generer" && {
                    id: "fact-encaisser-stage", Icon: Banknote, label: "Encaissé (banque)",
                    onClick: () => encaisserStage(f), disabled: occupe === "especes" },
                  cash(f) && f.recu > 0 && { id: "fact-especes-annuler", Icon: Banknote,
                    label: "Pas encore payé",
                    onClick: () => annulerPaiement(f), disabled: occupe === "especes" },
                  // Un paiement en espèces s'échelonne aussi : la famille apporte 2 ou 3
                  // enveloppes. Rien n'est envoyé par e-mail, seul le suivi change.
                  f.genre !== "stage" && (["envoyee", "partielle", "generee"].includes(f.statut)
                    || (cash(f) && f.total - f.recu > 0.02)) && {
                    id: "fact-echelonner", Icon: Split,
                    label: f.echelonnement_nb ? `Échelonné ${f.echelonnement_nb}×` : "Échelonner",
                    onClick: () => setEchelon(f) },
                  ["envoyee", "generee", "partielle", "payee"].includes(f.statut) && {
                    id: "fact-regenerer", Icon: RefreshCw, label: "Régénérer",
                    onClick: async () => {
                      if (await demander({
                        titre: `Refaire la facture de ${f.client}`,
                        texte: "Une nouvelle version est créée avec un nouveau QR-code.",
                        points: ["L'ancienne version est archivée avec sa date d'envoi (bouton « Historique »).",
                          "La date d'envoi est effacée : il faudra la renvoyer."],
                        valider: "Refaire la facture",
                      })) generer([f.cle], true);
                    } },
                ].filter(Boolean)} />
              </Cellule>
            </>
          )}
        />
        )}
      </Section>
      )}

      </>

      {modale && (
        <ModaleMontants facture={modale} prixSpecial={prixSpecial}
          onFermer={() => { setModale(null); setPrixSpecial(false); }}
          onOk={async (montantsChanges, { valider } = {}) => {
            const cle = modale.cle;
            setModale(null);
            setPrixSpecial(false);
            /* Une facture qu'on vient de corriger repart dans « Générées, à envoyer » et
               nulle part ailleurs — même partiellement payée : la console refait donc son
               PDF tout de suite. Si elle reste bloquée, la génération est refusée et dit
               pourquoi, et la facture reste dans « À corriger ». */
            // Ouverte depuis « À corriger » : enregistrer (ou « Valider la correction », rien
            // à changer) valide la correction avec ce que l'école vient de saisir, et le
            // serveur refait le PDF (voir validerApresCorrectionMain).
            if (modale.correction && (montantsChanges || valider)) {
              await validerApresCorrectionMain(cle, modale.client);
              return;
            }
            if (montantsChanges) { await generer([cle], true); return; }
            await charger(semestre, saison); onChangement?.();
          }} />
      )}

      {encaisserF && (
        <ModaleEncaisser it={encaisserF} onFermer={() => setEncaisserF(null)}
          onOk={async () => { setEncaisserF(null); await charger(semestre, saison); onChangement?.(); }} />
      )}

      {modaleStage && (
        <ModaleStage facture={modaleStage} prixSpecial={prixSpecial}
          onFermer={() => { setModaleStage(null); setPrixSpecial(false); }}
          onOk={async () => { setModaleStage(null); setPrixSpecial(false); await charger(semestre, saison); onChangement?.(); }} />
      )}

      {echelonStage && (
        <ModaleEchelonnerCash it={echelonStage} onFermer={() => setEchelonStage(null)}
          explication="Chaque versement arrive par le relevé bancaire ; tant que l'accord est tenu, aucun rappel ne part."
          onOk={async () => { setEchelonStage(null); await charger(semestre, saison); onChangement?.(); }} />
      )}

      {releve && (
        <ImportReleve releve={releve?.etat || null} onFermer={() => setReleve(false)}
          actionSecondaire={releve?.apres ? {
            label: "Relancer quand même sans charger de relevé",
            aide: "À utiliser seulement si tu sais déjà qui a payé : sinon une famille qui a payé hier recevra un rappel.",
            onClick: async () => {
              const { apres: suite, voulu } = releve;
              setReleve(false);
              await relancer(suite, true, voulu);
            },
          } : null}
          onTermine={async () => {
            const suite = releve?.apres;
            const voulu = releve?.voulu;
            setReleve(false);
            await charger(semestre, saison);
            onChangement?.();
            /* Le relevé vient d'être pointé : une famille qui a payé n'est plus en retard. La
               liste rechargée (et le serveur) décident ; on renvoie les mêmes familles. */
            if (suite) await relancer(suite, true, voulu);
          }} />
      )}

      {extrait && <ModaleExtrait onFermer={() => setExtrait(false)} />}
      {calculs && <ModaleCalculs semestre={semestre} onFermer={() => setCalculs(false)} />}

      {echelon && (
        <ModaleEchelonnement facture={echelon} onFermer={() => setEchelon(null)}
          onOk={async () => { setEchelon(null); await charger(semestre, saison); onChangement?.(); }} />
      )}

      {versions && (
        <ModaleVersions facture={versions} onFermer={() => setVersions(null)} />
      )}
    </div>
  );
}

/* Ce qui a changé : les six premières lignes, le reste sur demande. */
export function DetailJournal({ detail }) {
  const [tout, setTout] = useState(false);
  const lignes = detail || [];
  if (lignes.length === 0) return "—";
  const vues = tout ? lignes : lignes.slice(0, 6);
  return (
    <ul className="text-xs space-y-0.5" data-testid="historique-detail">
      {vues.map((d, k) => <li key={k}>{d}</li>)}
      {lignes.length > 6 && (
        <li>
          <button className="underline" onClick={() => setTout(!tout)}
            data-testid="historique-detail-tout">
            {tout ? "Réduire" : `Tout voir (${lignes.length - 6} de plus)`}
          </button>
        </li>
      )}
    </ul>
  );
}

/* D'où vient une action : la console, l'app installée, le site dans un navigateur, un lien
   reçu par e-mail, le formulaire du site, ou une tâche automatique (école, 12.10.2026 :
   une famille avait modifié ses fiches sans avoir installé l'app). */
const SOURCES_JOURNAL = {
  console: { Icon: Monitor, fond: "#F1F5F9", texte: "#475569" },
  app: { Icon: Smartphone, fond: "#EEF2FF", texte: "#4338CA" },
  web: { Icon: Globe, fond: "#E0F2FE", texte: "#0369A1" },
  lien: { Icon: Mail, fond: "#FEF3C7", texte: "#92400E" },
  formulaire: { Icon: FileText, fond: "#DCFCE7", texte: "#15803D" },
  auto: { Icon: Cog, fond: "#F5F3FF", texte: "#6D28D9" },
  espace: { Icon: UserRound, fond: "#F1F5F9", texte: "#475569" },
  espace_prof: { Icon: UserRound, fond: "#F1F5F9", texte: "#475569" },
};

/* La colonne « Qui » du journal : l'auteur en gras, son adresse en dessous (elle peut se
   couper : en un seul bloc, elle débordait sur la colonne voisine), puis la source. */
export function QuiJournal({ ligne }) {
  const s = SOURCES_JOURNAL[ligne.source];
  return (
    <div className="min-w-0">
      <div className="font-semibold leading-snug">{ligne.par}</div>
      {ligne.par_sous && (
        <div className="text-xs text-[var(--muted)] break-all mt-0.5">{ligne.par_sous}</div>
      )}
      {s && ligne.source_label && ligne.source_label !== ligne.par && (
        <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold leading-4"
          style={{ background: s.fond, color: s.texte }} data-testid="historique-source"
          title="D'où vient l'action">
          <s.Icon size={12} className="shrink-0" /> {ligne.source_label}
        </span>
      )}
    </div>
  );
}

/* Historique des actions de facturation : qui a fait quoi, quand. La console journalise
   déjà chaque écriture (facture générée, envoyée, corrigée, encaissement, relevé importé,
   e-mail type modifié) — ici on le donne à lire. */
export function Historique() {
  const [items, setItems] = useState(null);
  const [domaine, setDomaine] = useState("tout");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const cherche = q.trim();

  useEffect(() => {
    setItems(null);
    const t = setTimeout(() => {
      api(`/journal?limite=300${domaine === "tout" ? "" : `&domaine=${domaine}`}`
        + (cherche ? `&cherche=${encodeURIComponent(cherche)}` : ""))
        .then((d) => setItems(d.items)).catch((e) => setErr(e.message));
    }, cherche ? 350 : 0);
    return () => clearTimeout(t);
  }, [domaine, cherche]);

  if (err) return <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>;

  const quand = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso
      : d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Section titre={cherche && items ? `Historique — ${items.length} action(s) pour « ${cherche} »`
      : "Historique des actions"} testid="section-historique"
      aide="Tout ce qui a été fait, de la plus récente à la plus ancienne : factures, fiches élèves, planning, réglages. Chaque action est signée, et la pastille dit d'où elle vient : la console, l'app installée, le site web dans un navigateur, un lien reçu par e-mail, le formulaire du site, ou une tâche automatique. Tapez un nom d'élève ou de cours pour ne voir que ce qui le concerne."
      action={
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <Recherche valeur={q} onChange={setQ} largeur={240} testid="historique-recherche"
            placeholder="Un élève, un cours, une personne…" />
          <Puces testid="historique-domaine" valeur={domaine}
            options={[{ valeur: "tout", label: "Tout" }, { valeur: "reglages", label: "Réglages" },
              { valeur: "planning", label: "Planning" }, { valeur: "eleves", label: "Élèves & inscriptions" },
              { valeur: "facturation", label: "Facturation" }, { valeur: "comptes", label: "Comptes" }]}
            onChange={setDomaine} />
        </div>
      }>
      {!items ? <p className="text-sm text-[var(--muted)]">Chargement de l'historique…</p> : (
      <Tableau
        testid="tableau-historique"
        fixe
        entetes={[{ label: "Quand", largeur: "10%" }, { label: "Qui", largeur: "18%" },
          { label: "Quoi", largeur: "19%" }, { label: "Concerné", largeur: "21%" },
          { label: "Ce qui a changé", largeur: "32%" }]}
        vide={cherche ? `Aucune action ne concerne « ${cherche} ».`
          : "Aucune action enregistrée pour l'instant."}
        lignes={items.map((i, n) => ({ ...i, id: `${i.le}-${n}` }))}
        rendu={(i) => (
          <>
            <Cellule muet>{quand(i.le)}</Cellule>
            <Cellule><QuiJournal ligne={i} /></Cellule>
            <Cellule>
              <div className="leading-snug">{i.action}</div>
              <div className="text-xs text-[var(--muted)] mt-1">{i.quoi}</div>
            </Cellule>
            <Cellule>
              <div className="leading-snug break-words" data-testid="historique-concerne">{i.concerne || "—"}</div>
              {i.concerne_sous && (
                <div className="text-xs text-[var(--muted)] mt-1 break-all">{i.concerne_sous}</div>
              )}
            </Cellule>
            <Cellule muet><DetailJournal detail={i.detail} /></Cellule>
          </>
        )}
      />
      )}
    </Section>
  );
}

/* La facture est partie autrement (remise en main propre, ancien classeur) : elle sort de
   « à envoyer » sans qu'aucun e-mail ne quitte la console. Rangé dans « Modifier » parce
   qu'à côté de « Générer » le bouton se cliquait par erreur. */
function CasParticuliers({ facture, onOk }) {
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const [jour, setJour] = useState(new Date().toISOString().slice(0, 10));
  const [ouvert, setOuvert] = useState(false);
  if (!["a_generer", "generee"].includes(facture.statut)) return null;

  const marquer = async () => {
    setEnvoi(true);
    setErr("");
    try {
      if (facture.genre === "stage") {
        await api("/stages/deja-envoyee", {
          method: "POST",
          body: JSON.stringify({ cle: facture.cle_stage, date_envoi: jour }),
        });
      } else {
        await api("/facturation/deja-envoyee", {
          method: "POST",
          body: JSON.stringify({ cles: [facture.cle], date_envoi: jour,
            semestre: facture.semestre, saison: facture.saison }),
        });
      }
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  return (
    <div className="rounded-xl p-4 mb-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
      data-testid="cas-particuliers">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#92400E" }}>
        Cas particulier
      </div>
      {!ouvert ? (
        <>
          <p className="text-sm mt-1">Cette facture est déjà partie autrement (remise en main propre, envoyée hors de la console) ?</p>
          <button className="btn-ghost mt-2" data-testid="fact-deja-envoyee"
            onClick={() => setOuvert(true)}>
            <Check size={13} /> La marquer comme déjà envoyée
          </button>
        </>
      ) : (
        <>
          <p className="text-sm mt-1">
            Aucun e-mail ne partira : la facture sort de « à envoyer » et son délai de paiement
            part de la date indiquée.
          </p>
          <div className="mt-2 max-w-[220px]">
            <ChampTexte label="Date d'envoi" type="date" valeur={jour} onChange={setJour}
              testid="champ-date-deja-envoyee" />
          </div>
          {err && <p className="text-sm mb-2" style={{ color: "#B91C1C" }}>{err}</p>}
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => setOuvert(false)}>Annuler</button>
            <BoutonSombre onClick={marquer} disabled={envoi} testid="fact-deja-envoyee-valider">
              {envoi ? "Enregistrement…" : "Marquer comme envoyée"}
            </BoutonSombre>
          </div>
        </>
      )}
    </div>
  );
}

/* Corriger une facture de stage. Le prix vient du catalogue (tarif membre / non-membre) et
   ne se touche pas. Trois gestes, chacun SA ligne sur la facture — le prorata remplaçait
   l'ajustement, impossible d'accorder un rabais en plus (école, 09.10.2026 : « si on veut
   faire un rabais en plus on fait comment ? ») :
     1. le prorata par jour (jours suivis ÷ jours du stage), calculé par la console ;
     2. le rabais, saisi en POSITIF (« 50 » = 50 CHF de moins), motif facultatif ;
     3. replié : un supplément ou une autre correction (l'ancien « Ajustement », + ou −),
        ouvert d'office quand la facture en porte déjà un.
   Le décompte en bas est celui que la facture imprimera. */
const JOURS_STAGE = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
const jourStage = (j) => {
  const d = new Date(`${j}T12:00:00`);
  return `${JOURS_STAGE[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export function ModaleStage({ facture, onFermer, onOk, prixSpecial = false }) {
  const tarifs = facture.tarifs ?? ((facture.total || 0) - (facture.ajustement || 0));
  /* Les jours suivis, enfant par enfant (école, 09.10.2026 : « l'école devrait pouvoir
     indiquer quels jours ») : cochés ici, ils font le prorata ET la liste d'appel de chaque
     jour au téléphone. Les dates du stage illisibles : on garde les deux nombres. Une
     facture corrigée AVANT les jours (« 3 jours sur 5 » sans dire lesquels) garde son
     prorata tant qu'on ne coche rien. */
  const joursStage = facture.jours_dates || [];
  const enfants = facture.enfants || [];
  const [joursEnfants, setJoursEnfants] = useState(() => Object.fromEntries(enfants.map((e) => [
    e.inscription_id, (e.jours_choisis || []).length ? e.jours_choisis : joursStage])));
  const avecJours = enfants.some((e) => (e.jours_choisis || []).length) || (facture.prorata_enfants || []).length > 0;
  const [joursTouches, setJoursTouches] = useState(false);
  const parJours = joursStage.length > 0 && (avecJours || joursTouches || !facture.prorata);
  const basculerJour = (id, j) => {
    setJoursTouches(true);
    setJoursEnfants((x) => {
      const a = x[id] || [];
      return { ...x, [id]: a.includes(j) ? a.filter((y) => y !== j) : [...a, j].sort() };
    });
  };
  const prorataEnfants = parJours ? enfants.map((e) => {
    const n = (joursEnfants[e.inscription_id] || []).length;
    const m = n > 0 && n < joursStage.length ? -Math.round((e.montant || 0) * (1 - n / joursStage.length) * 100) / 100 : 0;
    return { id: e.inscription_id, eleve: e.eleve, n, montant: m };
  }) : [];
  const sansJour = prorataEnfants.some((x) => x.n === 0);
  /* Prorata par jour : l'enfant n'est venu que 3 jours sur 5, on coupe le prix d'autant
     (demandé par l'école le 02.10.2026). Le calcul se fait côté console ; ce qui a été
     saisi revient à la réouverture. */
  const [suivis, setSuivis] = useState(facture.jours_suivis ? String(facture.jours_suivis) : "");
  /* Les jours du stage sont lus dans ses dates (« Du 12 au 16 octobre 2026 » → 5) :
     l'école n'a plus qu'à dire combien l'enfant en a suivis. Le champ reste modifiable. */
  const [jours, setJours] = useState(String(facture.jours_du_stage || facture.jours_stage || ""));
  /* « Prix spécial convenu » (Écarts de paiement, 20.10.2026) : le rabais qui ramène la
     facture à ce que la famille a versé est proposé d'avance ; l'école l'ajuste. */
  const ecartPrix = prixSpecial ? Math.max(Math.round(((facture.total || 0) - (facture.recu || 0)) * 100) / 100, 0) : 0;
  const [rabais, setRabais] = useState(() => {
    const deja = Math.abs(facture.rabais || 0);
    return deja || ecartPrix ? String(Math.round((deja + ecartPrix) * 100) / 100) : "";
  });
  const [motifRabais, setMotifRabais] = useState(facture.motif_rabais || (prixSpecial ? "Prix spécial convenu" : ""));
  const [ajustement, setAjustement] = useState(facture.ajustement ? String(facture.ajustement) : "");
  const [motif, setMotif] = useState(facture.motif_ajustement || "");
  const [autre, setAutre] = useState(!!facture.ajustement);
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");

  const s = enNombre(suivis);
  const j = enNombre(jours);
  const prorata = parJours
    ? Math.round(prorataEnfants.reduce((t, x) => t + x.montant, 0) * 100) / 100
    : (j > 0 && s > 0 && s < j ? -Math.round(tarifs * (1 - s / j) * 100) / 100 : 0);
  const valRabais = -Math.abs(enNombre(rabais));
  const valAjustement = autre ? enNombre(ajustement) : 0;
  const total = Math.round((tarifs + prorata + valRabais + valAjustement) * 100) / 100;
  const lignes = [
    ...(parJours ? prorataEnfants.filter((x) => x.montant).map((x) => ({
      id: `prorata-${x.id}`, montant: x.montant,
      libelle: `Prorata${enfants.length > 1 ? ` ${x.eleve}` : ""} : ${x.n} jour(s) suivi(s) sur ${joursStage.length}`,
    })) : [prorata && { id: "prorata", libelle: `Prorata : ${s} jour(s) suivi(s) sur ${j}`, montant: prorata }]),
    valRabais && { id: "rabais", libelle: `Rabais${motifRabais.trim() ? ` — ${motifRabais.trim()}` : ""}`, montant: valRabais },
    valAjustement && { id: "ajustement", libelle: `Ajustement${motif.trim() ? ` — ${motif.trim()}` : ""}`, montant: valAjustement },
  ].filter(Boolean);
  const joursFaux = parJours ? sansJour : (s < 0 || j < 0 || (s > 0 && (j <= 0 || s > j)));
  // Le motif du rabais est facultatif (sans lui, la facture dit « Rabais ») : l'école ne
  // pouvait pas enregistrer et ne voyait pas pourquoi (09.10.2026).
  const ajustementSansMotif = !!valAjustement && !motif.trim();
  const bloque = joursFaux || ajustementSansMotif || total < 0;

  const enregistrer = async () => {
    setEnvoi(true);
    setErr("");
    try {
      await api("/stages/ajustement", {
        method: "POST",
        body: JSON.stringify({
          cle: facture.cle_stage, jours_suivis: s, jours_total: j,
          ...(parJours ? { jours: joursEnfants } : {}),
          rabais: Math.abs(enNombre(rabais)), motif_rabais: valRabais ? motifRabais : "",
          ajustement: valAjustement, motif: valAjustement ? motif : "",
        }),
      });
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  const bloc = { background: "#F8FAFC", border: "1px solid var(--border)" };
  const titreBloc = "text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2";
  /* Le mode de paiement du stage, dans les deux sens (20.10.2026) : au comptoir → le stage
     passe dans Cash / espèces ; facture QR → il repart dans le circuit des factures (À
     générer, envoi, rappels). Ce qui a déjà été reçu reste compté. */
  const changerMode = async () => {
    setEnvoi(true);
    setErr("");
    try {
      await api("/facturation/especes/mode", { method: "POST",
        body: JSON.stringify({ ref: `stage::${facture.cle_stage}`, mode: facture.especes ? "facture" : "cash" }) });
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  return (
    <Modale titre={`Facture de stage — ${facture.client}`} onFermer={onFermer} testid="modale-stage"
      sous_titre="Le prix du stage vient du catalogue et ne change pas : ici tu retires un prorata et/ou un rabais, chacun sa ligne sur la facture.">
      <div className="rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="modale-stage-comptoir"
        style={facture.especes ? { background: "#FFFBEB", border: "1px solid #FDE68A" } : bloc}>
        <span className="text-sm flex-1" style={{ color: facture.especes ? "#92400E" : "var(--text)", minWidth: 200 }}>
          Mode de paiement : <strong>{facture.especes ? "cash, au comptoir" : "facture QR"}</strong>
          {facture.especes ? " — aucune facture n'est envoyée." : ""}
        </span>
        <button className="btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }} disabled={envoi}
          onClick={changerMode} data-testid="modale-stage-sur-facture">
          {facture.especes ? "Passer sur facture QR" : "Passer au comptoir (cash)"}
        </button>
      </div>
      <div className="rounded-xl p-4 mb-4" style={bloc}>
        <div className="text-sm font-semibold">{facture.stage}</div>
        <div className="text-[11px] text-[var(--muted)] mt-0.5">{facture.dates}</div>
        <ul className="text-sm mt-2 space-y-1" data-testid="modale-stage-enfants">
          {(facture.enfants || []).map((e, k) => (
            <li key={k} className="flex justify-between gap-4">
              <span>{e.eleve}{e.membre ? " · membre" : ""}</span>
              <span className="text-[var(--muted)] text-right">{argent(e.montant)} — {e.regle}</span>
            </li>
          ))}
        </ul>
        <div className="text-sm mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
          Tarifs du catalogue : <strong>{argent(tarifs)}</strong>
        </div>
      </div>

      <div className="rounded-xl p-4 mb-4" style={bloc} data-testid="modale-stage-bloc-prorata">
        <div className={titreBloc}>1 · Prorata par jour — si l'enfant ne vient pas toute la semaine</div>
        {joursStage.length > 0 && !parJours && (
          <div className="text-xs mb-3 rounded-lg p-3" style={{ background: "#EFF6FF", color: "#1E3A8A" }} data-testid="modale-stage-prorata-ancien">
            Prorata enregistré sans dire les jours : {facture.jours_du_stage
              ? `${facture.jours_suivis} jour(s) suivi(s) sur ${facture.jours_du_stage}` : chfSigne(facture.prorata)}.
            Il reste tel quel ; pour la liste d'appel au téléphone, coche les jours de l'enfant.
            <button type="button" className="underline font-semibold ml-1" data-testid="modale-stage-cocher-jours"
              onClick={() => setJoursTouches(true)}>Cocher les jours</button>
          </div>
        )}
        {parJours && (
          <div className="space-y-3 mb-2" data-testid="modale-stage-jours">
            {enfants.map((e) => {
              const choisis = joursEnfants[e.inscription_id] || [];
              const x = prorataEnfants.find((y) => y.id === e.inscription_id) || {};
              return (
                <div key={e.inscription_id}>
                  <div className="text-sm flex flex-wrap justify-between gap-2">
                    <span className="font-semibold">{e.eleve}</span>
                    <span className="text-xs text-[var(--muted)]" data-testid="modale-stage-jours-suivis">
                      Jours suivis : <strong>{choisis.length}</strong> sur {joursStage.length}
                      {x.montant ? ` → ${chfSigne(x.montant)}` : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {joursStage.map((jj) => {
                      const oui = choisis.includes(jj);
                      return (
                        <button key={jj} type="button" aria-pressed={oui} data-testid="modale-stage-jour"
                          onClick={() => basculerJour(e.inscription_id, jj)}
                          className="text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"
                          style={oui ? { background: "#0F1B2D", color: "#fff", border: "1px solid #0F1B2D" }
                            : { background: "#fff", color: "#94A3B8", border: "1px dashed #CBD5E1", textDecoration: "line-through" }}>
                          {oui && <Check size={11} strokeWidth={3} />}{jourStage(jj)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-[11px] text-[var(--muted)]">
              Tous cochés = tout le stage. Les jours décochés sont retirés au prorata du tarif de l'enfant, et la
              liste d'appel du mode école ne le montre que les jours cochés.
            </p>
          </div>
        )}
        {!parJours && joursStage.length === 0 && <div className="grid sm:grid-cols-2 gap-x-3">
          <ChampTexte label="Jours suivis" valeur={suivis} onChange={setSuivis}
            testid="champ-stage-jours-suivis" aide="ex. 3 — vide si l'enfant fait tout le stage" />
          <ChampTexte label="Jours du stage" valeur={jours} onChange={setJours}
            testid="champ-stage-jours-total"
            aide={facture.jours_stage
              ? `compté depuis « ${facture.dates} » — corrige si besoin`
              : "ex. 5 — les dates du stage ne sont pas lisibles"} />
        </div>}
        {prorata !== 0 && (
          <p className="text-xs font-semibold -mt-1" style={{ color: "#1D4ED8" }}
            data-testid="modale-stage-prorata">
            {parJours
              ? `Prorata : ${chfSigne(prorata)} — jours non suivis, au tarif de chaque enfant.`
              : `Prorata : ${chfSigne(prorata)} — ${j - s} jour(s) non suivi(s) sur ${j}, calculé sur les tarifs.`}
          </p>
        )}
        {joursFaux && (
          <p className="text-xs font-semibold -mt-1" style={{ color: "#B91C1C" }}
            data-testid="modale-stage-jours-faux">
            {parJours ? "Coche au moins un jour par enfant (un enfant qui ne vient plus se passe en « Annulé » dans Liste → Stages)."
              : j <= 0 ? "Indique aussi le nombre de jours du stage."
              : "Plus de jours suivis que de jours de stage : vérifie les deux nombres."}
          </p>
        )}
      </div>

      <div className="rounded-xl p-4 mb-4" style={bloc} data-testid="modale-stage-bloc-rabais">
        <div className={titreBloc}>2 · Rabais — geste commercial, fratrie, arrangement</div>
        {ecartPrix > 0 && (
          <p className="text-xs mb-3 rounded-lg px-3 py-2" data-testid="modale-stage-prix-special"
            style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
            Prix spécial convenu : la famille a versé {argent(facture.recu)} sur {argent(facture.total)}.
            Le rabais proposé ramène la facture à ce montant — ajuste-le si le prix convenu est différent.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-x-3">
          <ChampTexte label="Rabais en CHF" valeur={rabais} onChange={setRabais}
            testid="champ-stage-rabais" aide="sans signe : 50 = 50 CHF de moins" />
          <ChampTexte label="Motif du rabais" valeur={motifRabais} onChange={setMotifRabais}
            testid="champ-stage-motif-rabais" aide="facultatif — imprimé sur la facture (sinon « Rabais »)" />
        </div>
      </div>

      {!autre ? (
        <button type="button" className="text-xs underline mb-4 text-[var(--muted)]"
          data-testid="modale-stage-autre-ouvrir" onClick={() => setAutre(true)}>
          Faire payer un supplément ou corriger une autre erreur ?
        </button>
      ) : (
        <div className="rounded-xl p-4 mb-4" style={bloc} data-testid="modale-stage-bloc-ajustement">
          <div className={titreBloc}>3 · Autre correction (rare) — supplément ou erreur</div>
          <div className="grid sm:grid-cols-2 gap-x-3">
            <ChampTexte label="Ajustement (+ ou −)" valeur={ajustement} onChange={setAjustement}
              testid="champ-stage-ajustement"
              aide="+30 = 30 CHF de plus · −30 = 30 CHF de moins" />
            <ChampTexte label="Motif de l'ajustement" valeur={motif} onChange={setMotif}
              testid="champ-stage-motif" aide="obligatoire — imprimé sur la facture" />
          </div>
          {ajustementSansMotif && (
            <p className="text-xs font-semibold -mt-1 mb-2" style={{ color: "#B45309" }}>
              Écris le motif de l'ajustement : la famille le lira sur sa facture.
            </p>
          )}
          <button type="button" className="text-xs underline text-[var(--muted)]"
            data-testid="modale-stage-autre-retirer"
            onClick={() => { setAutre(false); setAjustement(""); setMotif(""); }}>
            Retirer cette correction
          </button>
        </div>
      )}

      <div className="rounded-xl p-4 mb-4" style={{ background: "#fff", border: "1px solid var(--border)" }}
        data-testid="modale-stage-decompte">
        <div className={titreBloc}>Décompte imprimé sur la facture</div>
        <div className="text-sm space-y-1">
          {lignes.length > 0 && (
            <div className="flex justify-between gap-3">
              <span>Sous-total du stage</span>
              <span className="whitespace-nowrap">{chfSigne(tarifs)}</span>
            </div>
          )}
          {lignes.map((l) => (
            <div key={l.id} className="flex justify-between gap-3" data-testid={`modale-stage-ligne-${l.id}`}>
              <span className="min-w-0 break-words">{l.libelle}</span>
              <span className="whitespace-nowrap">{chfSigne(l.montant)}</span>
            </div>
          ))}
          <div className="flex justify-between gap-3 pt-2 mt-1 border-t font-semibold"
            style={{ borderColor: "var(--border)" }} data-testid="modale-stage-total">
            <span>Nouveau total</span>
            <span className="whitespace-nowrap" style={{ color: total < 0 ? "#B91C1C" : undefined }}>
              {chfSigne(total)}
            </span>
          </div>
          {facture.recu ? (
            <div className="text-xs text-[var(--muted)]">déjà reçu {argent(facture.recu)}</div>
          ) : null}
          {total < 0 && (
            <p className="text-xs font-semibold" style={{ color: "#B91C1C" }}>
              Le total ne peut pas être négatif : le prorata et le rabais dépassent les tarifs.
            </p>
          )}
        </div>
      </div>
      {facture.numero && (
        <p className="text-xs mb-4" style={{ color: "#B45309" }} data-testid="modale-stage-regenerer">
          La facture {facture.numero} est déjà fabriquée : à l'enregistrement, elle est refaite
          toute seule (même numéro){facture.envoyee_le
            ? " et repasse dans « à envoyer » pour que la famille reçoive le bon montant" : ""}.
        </p>
      )}
      <CasParticuliers facture={facture} onOk={onOk} />
      {err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        <BoutonSombre onClick={enregistrer} disabled={envoi || bloque} testid="modale-stage-enregistrer">
          {envoi ? "Enregistrement…" : "Enregistrer"}
        </BoutonSombre>
      </div>
    </Modale>
  );
}

/* Paiement en 2 ou 3 fois : la console fixe les échéances toute seule (échéance de la
   facture, puis un mois plus tard), aucun rappel tant que chaque versement arrive à temps
   (une mensualité échue et pas reçue repart en relance), et les encaissements des relevés
   cochent les versements reçus. Même écran depuis la fiche élève. */
export function ModaleEchelonnement({ facture, onFermer, onOk }) {
  const ids = (facture.postes || []).map((p) => p.id);
  const [nb, setNb] = useState(String(facture.echelonnement_nb || facture.ecart_suggestion_nb || 2));
  const [note, setNote] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const part = facture.total ? Math.round((facture.total / Number(nb)) * 100) / 100 : 0;

  const enregistrer = async (annuler = false) => {
    setEnvoi(true);
    setErr("");
    try {
      await api(`/facturation/echelonnement${annuler ? "/annuler" : ""}`, {
        method: "POST",
        body: JSON.stringify({ ids, nb: Number(nb), note }),
      });
      await onOk();
    } catch (e) {
      setErr(e.message);
    }
    setEnvoi(false);
  };

  return (
    <Modale titre={`Paiement en plusieurs fois — ${facture.client}`}
      sous_titre="Un versement par mois, échéances fixées automatiquement. Aucun rappel tant que chaque versement arrive à temps ; une mensualité manquée repart en relance."
      onFermer={onFermer} testid="modale-echelonnement">
      <p className="text-sm text-[var(--muted)] mb-4">
        Total de la facture : <strong>{argent(facture.total)}</strong>
        {facture.recu ? ` · déjà reçu ${argent(facture.recu)}` : ""}.
      </p>
      <ChampChoix label="Nombre de versements" valeur={nb} testid="echelon-nb"
        options={[{ valeur: "2", label: "2 fois" }, { valeur: "3", label: "3 fois" }]}
        onChange={setNb} />
      <p className="text-xs text-[var(--muted)] mb-4" data-testid="echelon-explication">
        Environ <strong>{argent(part)}</strong> par versement. Le premier tombe à l'échéance de la
        facture, les suivants de mois en mois. {facture.especes
          ? "Au comptoir : chaque enveloppe s'encaisse avec « Encaisser » (une partie) dans Cash / espèces."
          : "Chaque versement reçu se coche tout seul à l'import du relevé bancaire."}
      </p>
      {facture.echelonnement_nb > 0 && (facture.echelonnement_dates || []).length > 0 && (
        <p className="text-xs mb-4" style={{ color: "#1D4ED8" }} data-testid="echelon-dates">
          Échéances en cours : {facture.echelonnement_dates.map(jourFr).join(" · ")}
          {" "}— {facture.echelonnement_recus || 0}/{facture.echelonnement_nb} reçu(s).
        </p>
      )}
      <ChampTexte label="Ce qui a été convenu (facultatif)" valeur={note} onChange={setNote} testid="echelon-note" />
      {err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex items-center gap-2 mt-2">
        <BoutonSombre onClick={() => enregistrer(false)} disabled={envoi} testid="echelon-enregistrer">
          {envoi ? <Loader2 size={14} className="animate-spin" /> : <Split size={14} />} Enregistrer l'accord
        </BoutonSombre>
        {facture.echelonnement_nb > 0 && (
          <button className="btn-ghost" onClick={() => enregistrer(true)} disabled={envoi} data-testid="echelon-annuler">
            Annuler l'accord
          </button>
        )}
      </div>
    </Modale>
  );
}

/* Ce que la famille a reçu avant qu'on refasse sa facture. */
function ModaleVersions({ facture, onFermer }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api(`/facturation/versions/${encodeURIComponent(facture.pdf_numero || facture.numero)}`).then(setData).catch(() => setData({ versions: [] }));
  }, [facture.pdf_numero, facture.numero]);

  return (
    <Modale titre={`Historique — facture ${facture.numero || facture.pdf_numero}`}
      sous_titre="Chaque version précédente est conservée telle qu'elle est partie."
      onFermer={onFermer} testid="modale-versions">
      {!data && <p className="text-sm text-[var(--muted)]">Chargement…</p>}
      {data && (
        <div className="space-y-2" data-testid="liste-versions">
          {facture.pdf && (
            <div className="rounded-xl p-3 text-sm flex items-center gap-3" style={{ background: "#F0FDFA", border: "1px solid #99F6E4" }}>
              <strong>Version {facture.pdf_version} — actuelle</strong>
              <span className="text-[var(--muted)] text-xs">
                {argent(facture.total)}{facture.envoyee_le ? ` · envoyée le ${jourFr(facture.envoyee_le)}` : " · pas encore envoyée"}
              </span>
              <button className="btn-ghost ml-auto" data-testid="version-pdf-actuelle"
                onClick={() => ouvrirPdf(`/facturation/pdf/${encodeURIComponent(facture.pdf_numero)}`)}>
                <Eye size={13} /> PDF
              </button>
            </div>
          )}
          {data.versions.map((v) => (
            <div key={v.id} className="rounded-xl p-3 text-sm flex items-center gap-3"
              style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
              <strong>Version {v.version}</strong>
              <span className="text-[var(--muted)] text-xs">
                {argent(v.total)} · {v.envoyee_le ? `envoyée le ${jourFr(v.envoyee_le)}` : "jamais envoyée"}
                {" "}· remplacée le {jourFr(v.archive_le)}
              </span>
              <button className="btn-ghost ml-auto" data-testid="version-pdf"
                onClick={() => ouvrirPdf(`/facturation/version/${encodeURIComponent(v.id)}/pdf`)}>
                <Eye size={13} /> PDF
              </button>
            </div>
          ))}
          {data.archive && (
            <div className="rounded-xl p-3 text-sm flex items-center gap-3"
              style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
              data-testid="version-archive">
              <strong>Facture d'origine</strong>
              <span className="text-[var(--muted)] text-xs">
                {argent(data.archive.total)}
                {data.archive.envoyee_le ? ` · envoyée le ${jourFr(data.archive.envoyee_le)}` : ""}
                {" "}· celle que la famille avait reçue avant la console
              </span>
              <button className="btn-ghost ml-auto" data-testid="version-pdf-archive"
                onClick={() => ouvrirPdf(
                  `/factures/archive/pdf?numero=${encodeURIComponent(data.archive.numero)}`)}>
                <Eye size={13} /> PDF
              </button>
            </div>
          )}
          {data.versions.length === 0 && !data.archive && (
            <p className="text-sm text-[var(--muted)]">Cette facture n'a jamais été refaite.</p>
          )}
        </div>
      )}
    </Modale>
  );
}

/* Tout ce que la console calcule elle-même quand on génère les factures. Les montants
   affichés sont ceux des Réglages : ce n'est pas un texte figé. */
export function ModaleCalculs({ semestre, onFermer }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api(`/facturation/regles-de-calcul?semestre=${semestre}`).then(setData).catch((e) => setErr(e.message));
  }, [semestre]);

  return (
    <Modale large titre="Comment les montants sont calculés"
      sous_titre="À la génération d'une facture, la console refait tout ce calcul depuis les inscriptions."
      onFermer={onFermer} testid="modale-calculs">
      {err && <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>}
      {!data && !err && <p className="text-sm text-[var(--muted)]">Chargement…</p>}
      {data && (
        <>
          <div className="rounded-xl px-4 py-3 text-sm mb-4" style={{ background: "#EFF6FF", border: "1px solid #DBEAFE", color: "#1D4ED8" }}>
            Semestre {data.semestre} : <strong>{data.fenetre_semestre}</strong> ({data.nb_mois} mois facturés).
            C'est cette fenêtre qui décide du prorata.
          </div>
          <ol className="space-y-3" data-testid="liste-calculs">
            {data.etapes.map((e) => (
              <li key={e.titre} className="rounded-xl border p-4" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{e.titre}</span>
                  <Badge ton={e.auto ? "vert" : "ambre"}>{e.auto ? "automatique" : "décision de l'école"}</Badge>
                </div>
                <p className="text-sm mt-1">{e.quoi}</p>
                <p className="text-xs text-[var(--muted)] mt-1">{e.detail}</p>
              </li>
            ))}
          </ol>
          <p className="text-xs text-[var(--muted)] mt-4">
            Les montants viennent des Réglages : les modifier là change ce calcul, sans toucher au code.
          </p>
        </>
      )}
    </Modale>
  );
}

/* « Modifier la facture » — refaite le 09.10.2026 à la demande de l'école.

   L'ancienne fenêtre montrait des champs VIDES (« calculé : … » en petit dessous) pour
   tout ce que la console calcule, répétait taxe et ajustement dans le bloc de chaque
   inscription, et additionnait les colonnes du classeur (« Taxes et réductions : 40 CHF »
   pour une facture qui n'en comptait que 20). Surtout, « Réduction accumulation » et
   « Réduction famille » étaient saisissables alors que la console les recalcule TOUJOURS
   d'après les Réglages : ce qu'on y tapait n'avait aucun effet sur la facture.

   La fenêtre se lit maintenant de haut en bas comme la facture elle-même :
     1. La facture — les cours (un par ligne, avec leur prorata) puis le décompte calculé
        par la console, exactement celui qui s'imprimera, recalculé en direct.
     2. Ce que l'école peut décider — rabais, ajustement, taxe, paiement, notes : un seul
        champ par geste, pour toute la facture.
     3. Cas rares, replié — forcer le prix des cours ou le prorata d'une inscription.

   Rien ne change dans ce qui est enregistré : un champ que l'école ne touche pas renvoie
   la valeur déjà portée par la ligne (la console compare et ne voit aucun changement), et
   les réductions calculées ne sont jamais réécrites. */
export function ModaleMontants({ facture, onFermer, onOk, prixSpecial = false }) {
  const nombre = (v) => (v === "" || v === null || v === undefined ? 0
    : Number(String(v).replace(",", ".").replace("−", "-")) || 0);
  const originaux = Object.fromEntries((facture.postes || []).map((p) => [p.id, p]));
  const orig = (p) => originaux[p.id] || {};

  /* Le champ Prorata : vide = « la console calcule », un montant (0 compris) = décision de
     l'école. On ne renvoie le prorata QUE si l'école a touché le champ (cas Fernandes : le
     0 d'une ligne venue du site figeait le prorata à 0). Un prorata que la console a
     identifié comme périmé (le cours remplace un cours quitté) est retiré d'avance. */
  const [postes, setPostes] = useState(() => (facture.postes || []).map((p) => ({
    ...p,
    prorata: p.prorata_a_retirer ? ""
      : (p.prorata_manuel ? String(p.prorata ?? 0) : (Number(p.prorata) ? p.prorata : "")),
    // Le prix des cours n'est affiché dans le champ que s'il a été DÉCIDÉ par l'école ;
    // sinon le champ est vide et la valeur calculée est écrite à côté.
    total_cours: p.total_cours_manuel ? String(p.total_cours ?? "") : "",
    // La taxe que la facture compte vraiment pour cette inscription.
    taxe_admin: String(p.taxe_calculee ?? p.taxe_admin ?? 0),
  })));
  const [prorataTouche, setProrataTouche] = useState(() => Object.fromEntries(
    (facture.postes || []).filter((p) => p.prorata_a_retirer).map((p) => [p.id, true])));
  const [totalTouche, setTotalTouche] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const [avance, setAvance] = useState(() => (facture.postes || []).some((p) => p.prorata_a_retirer));
  // Encaissement bancaire : ouvert à la demande, avec un motif gardé dans l'historique.
  const [correction, setCorrection] = useState({});
  const [motifs, setMotifs] = useState({});

  const maj = (id, champs) => setPostes((liste) => liste.map((p) => (p.id === id ? { ...p, ...champs } : p)));
  const lignesDuPoste = (p) => (facture.detail || []).filter((d) => d.poste_id === p.id);
  const elevesDuPoste = (p) => [...new Set([...lignesDuPoste(p).map((l) => l.eleve),
    ...(p.cours_quittes || []).map((q) => q.eleve)].filter(Boolean))].join(" · ")
    || `Inscription ${p.demande || ""}`.trim();
  const tousQuittes = [...new Map(postes.flatMap((p) => p.cours_quittes || [])
    .map((q) => [`${q.eleve}::${q.cours}::${q.statut || ""}`, q])).values()];
  // Les inscriptions qui facturent quelque chose. Une demande qui n'a QUE des cartes 10
  // cours (hors facture) ou dont tous les cours sont annulés n'ouvre aucun champ.
  const avecCours = postes.filter((p) => lignesDuPoste(p).length);
  const toutAnnule = postes.length > 0 && avecCours.length === 0;
  const facturees = toutAnnule ? postes.slice(0, 1) : avecCours;
  const principal = facturees[0] || postes[0];

  /* Un geste = un champ. Rabais, ajustement et notes valent pour LA facture : ils sont lus
     sur la ligne qui les porte (ou la première inscription facturée) et réécrits sur elle
     seule. Une facture ancienne qui porte un ajustement sur PLUSIEURS inscriptions les
     garde séparés, dans « Cas rares », pour ne rien déplacer. */
  const porteurs = (champ, motif) => postes.filter((p) => Math.abs(nombre(orig(p)[champ])) > 0.005
    || String(orig(p)[motif] || "").trim());
  const porteurDe = (champ, motif) => {
    const avec = porteurs(champ, motif);
    return avec.length > 1 ? null : (avec[0] || principal);
  };
  const porteurAjustement = porteurDe("ajustement", "motif_ajustement");
  const porteurRabais = porteurDe("rabais", "motif_rabais");
  /* « Prix spécial convenu » (Écarts de paiement, 20.10.2026) : la famille a versé moins
     que la facture parce qu'un prix a été convenu. Le rabais qui ramène la facture à ce
     qu'elle a versé est proposé d'avance (en négatif) ; l'école l'ajuste si le prix
     convenu est différent. */
  const ecartPrix = prixSpecial ? Math.max(Math.round(((facture.total || 0) - (facture.recu || 0)) * 100) / 100, 0) : 0;
  const prixPropose = useRef(false);
  useEffect(() => {
    if (!ecartPrix || !porteurRabais || prixPropose.current) return;
    prixPropose.current = true;
    const o = originaux[porteurRabais.id] || {};
    maj(porteurRabais.id, { rabais: String(Math.round((nombre(o.rabais) - ecartPrix) * 100) / 100),
      motif_rabais: o.motif_rabais || "Prix spécial convenu" });
  }, [ecartPrix, porteurRabais]); // eslint-disable-line react-hooks/exhaustive-deps
  const porteursNotes = postes.filter((p) => String(orig(p).notes || "").trim());
  const porteurNotes = porteursNotes.length > 1 ? null : (porteursNotes[0] || principal);
  const modePaiement = (postes.find((p) => String(p.mode_paiement || "").trim()) || {}).mode_paiement || "";
  /* Un mode écrit fait foi ; sans mode écrit, une famille habituée du comptoir (mode repris
     d'un autre semestre) est en cash. */
  const cash = modePaiement ? /cash|esp/i.test(modePaiement) : !!facture.especes;

  const taxeDecidee = (p) => Math.abs(nombre(p.taxe_admin) - nombre(orig(p).taxe_calculee ?? orig(p).taxe_admin)) > 0.005;
  const totalDecide = (p) => totalTouche[p.id] && String(p.total_cours ?? "").trim() !== ""
    && (orig(p).total_cours_manuel || Math.abs(nombre(p.total_cours) - nombre(p.total_cours_calcule)) > 0.005);
  const prorataDecide = (p) => (prorataTouche[p.id]
    ? String(p.prorata ?? "").trim() !== "" : !!p.prorata_manuel);

  /* Ce que « Enregistrer » envoie pour une inscription — l'aperçu envoie EXACTEMENT la
     même chose. Tout champ non touché renvoie la valeur de la ligne. */
  const champsDuPoste = (p) => {
    const o = orig(p);
    return {
      total_cours: totalDecide(p) ? nombre(p.total_cours) : nombre(o.total_cours),
      // Calculées par la console d'après les Réglages : jamais réécrites d'ici.
      reduction_accumulation: nombre(o.reduction_accumulation),
      reduction_famille: nombre(o.reduction_famille),
      taxe_admin: taxeDecidee(p) ? nombre(p.taxe_admin) : nombre(o.taxe_admin),
      ...(taxeDecidee(p) ? { taxe_admin_manuel: true } : {}),
      ...(prorataTouche[p.id] ? (
        String(p.prorata ?? "").trim() === ""
          ? { prorata: "" } : { prorata: nombre(p.prorata), prorata_manuel: true }) : {}),
      essais_regles: nombre(o.essais_regles),
      ajustement: nombre(p.ajustement), motif_ajustement: p.motif_ajustement || "",
      rabais: nombre(p.rabais), motif_rabais: p.motif_rabais || "",
      montant_recu: nombre(p.montant_recu), date_paiement: p.date_paiement || "",
      mode_paiement: p.mode_paiement || "", notes: p.notes || "",
    };
  };
  const montantsChanges = postes.some((p) => {
    const o = orig(p);
    const c = champsDuPoste(p);
    return ["total_cours", "taxe_admin", "ajustement", "rabais"]
      .some((k) => Math.abs(nombre(c[k]) - nombre(o[k])) > 0.005)
      || (c.taxe_admin_manuel && !o.taxe_admin_manuel)
      || ("prorata" in c && Math.abs(nombre(c.prorata) - nombre(o.prorata)) > 0.005)
      || prorataDecide(p) !== !!o.prorata_manuel;
  });
  const autreChose = postes.some((p) => {
    const o = orig(p);
    return ["motif_ajustement", "motif_rabais", "mode_paiement", "notes", "date_paiement"]
      .some((k) => String(p[k] || "") !== String(o[k] || ""))
      || Math.abs(nombre(p.montant_recu) - nombre(o.montant_recu)) > 0.005;
  });

  /* Le décompte recalculé par la console (même calcul qu'après l'enregistrement). */
  const [apercu, setApercu] = useState(null);
  const signature = JSON.stringify(postes.map((p) => [p.id, champsDuPoste(p)]));
  useEffect(() => {
    if (!montantsChanges) { setApercu(null); return undefined; }
    let fini = false;
    const minuterie = setTimeout(async () => {
      try {
        const r = await api("/facturation/montants/apercu", {
          method: "POST",
          body: JSON.stringify({ postes: Object.fromEntries(postes.map((p) => [p.id, champsDuPoste(p)])) }),
        });
        if (!fini) setApercu(r);
      } catch (e) {
        if (!fini) setApercu({ ok: false, erreur: e.message });
      }
    }, 400);
    return () => { fini = true; clearTimeout(minuterie); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, montantsChanges]);

  /* Ouverte depuis « À corriger » (la facture porte sa correction) : enregistrer VALIDE la
     correction, et la facture refaite part dans « Générées, à envoyer » (09.10.2026). Rien à
     changer ? « Valider la correction » le dit, sans rien réécrire. */
  const depuisACorriger = !!facture.correction;
  const rienAChanger = !montantsChanges && !autreChose && !Object.keys(correction).length;
  const enregistrer = async () => {
    const sansMotif = postes.find((p) => correction[p.id] && (motifs[p.id] || "").trim().length < 3);
    if (sansMotif) {
      setErr("Explique pourquoi tu corriges l'encaissement à la main : la raison reste dans l'historique.");
      return;
    }
    setEnvoi(true);
    setErr("");
    try {
      if (!rienAChanger) {
        for (const p of postes) {
          await api(`/facturation/poste/${encodeURIComponent(p.id)}`, {
            method: "PATCH",
            body: JSON.stringify({ motif: correction[p.id] ? (motifs[p.id] || "").trim() : "",
              champs: champsDuPoste(p) }),
          });
        }
      }
      onOk(montantsChanges, { valider: depuisACorriger });
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  /* La facture lue comme le récapitulatif du site (école, 09.10.2026 : « pour que ce soit
     clair qui apporte quoi ») : élève par élève, puis la famille. Tous les chiffres viennent
     du serveur (`recap`, recalculé par l'aperçu) — rien n'est additionné ici. */
  const decompte = (montantsChanges && apercu?.ok && apercu.decompte) || facture.decompte_lignes || [];
  const recap = (montantsChanges && apercu?.ok && apercu.recap) || facture.recap
    || { eleves: [], famille: decompte.map((l) => ({ ...l, cle: "autre" })) };
  const totalAffiche = montantsChanges && apercu?.ok ? apercu.total : facture.total;
  const parEleve = [...new Set((facture.detail || []).map((d) => d.eleve))];
  const cartes = facture.cartes_10 || [];
  const bonsDeduits = facture.parrainages || [];
  const bonsALaMain = facture.parrainages_neutralises || [];
  const bonsReportes = facture.parrainages_reportes || [];
  const signe = (v) => (v > 0.005 ? `+ ${argent(v)}` : v < -0.005 ? `− ${argent(-v)}` : argent(0));
  const titreBloc = "text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]";

  return (
    <Modale large titre={`Facture — ${facture.client}`} onFermer={onFermer} testid="modale-montants"
      sous_titre="En haut, la facture telle qu'elle partira. En dessous, ce que tu peux décider : le total se recalcule tout seul.">
      {envoi && <BarreAvancement texte="Enregistrement, puis fabrication de la facture…" />}

      {tousQuittes.length > 0 && (
        <div className="rounded-xl px-4 py-3 mb-4 text-xs" data-testid="modale-cours-quittes"
          style={{ background: "#FEF3C7", border: "1px solid #FDE68A", color: "#92400E" }}>
          <strong>Cours annulés / non facturés</strong> — déjà sortis du total : il n'y a rien à
          déduire à la main (un ajustement négatif les déduirait deux fois).
          <ul className="mt-1.5 space-y-0.5">
            {tousQuittes.map((q, k) => (
              <li key={k}>{q.eleve} — {q.cours} · {q.statut || "annulé"}{q.montant ? ` · ${argent(q.montant)} déjà retiré` : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {postes.some((p) => p.prorata_a_retirer) && (
        <div className="rounded-xl px-4 py-3 mb-4 text-xs" data-testid="modale-prorata-a-retirer"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
          <strong>Prorata retiré :</strong> un cours remplace un cours quitté ce semestre, l'élève
          était déjà là et ne perd aucun mois. Le prorata a été vidé pour toi (« Cas rares »
          ci-dessous) — clique « Enregistrer » pour valider, la facture sortira de « À corriger ».
        </div>
      )}

      {/* ── 1. La facture ───────────────────────────────────────────────────── */}
      <div className="rounded-xl p-4 mb-4" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}
        data-testid="modale-facture">
        <div className={titreBloc}>1 · La facture</div>
        {recap.eleves.length === 0 && (
          <p className="text-sm text-[var(--muted)] mt-2">Aucun cours facturé sur cette facture.</p>
        )}
        {/* Élève par élève : ses cours (un forfait ou un pass sur UNE ligne), son prorata, sa
            réduction d'accumulation et son sous-total. */}
        {recap.eleves.map((b) => (
          <div key={b.eleve_id} className="mt-3 pb-2 border-b" style={{ borderColor: "#E2E8F0" }}
            data-testid="modale-eleve">
            {recap.eleves.length > 1 && <div className="text-xs font-semibold mb-1">{b.eleve}</div>}
            {b.lignes.map((l, k) => (
              <div key={k} className="py-1" data-testid="modale-ligne-cours">
                <div className="flex justify-between gap-3 text-sm">
                  <span>{l.libelle}{l.detail ? <span className="text-[var(--muted)]"> · {l.detail}</span> : null}</span>
                  <span className="whitespace-nowrap">{argent(l.montant)}</span>
                </div>
                {l.suite && <div className="text-[11px]" style={{ color: "#065F46" }}>{l.suite}</div>}
              </div>
            ))}
            {Math.abs(b.prorata) > 0.005 && (
              <div className="flex justify-between gap-3 text-sm py-1 text-[var(--muted)]" data-testid="modale-ligne-prorata">
                <span>Prorata — mois non suivis
                  {b.prorata_note && <span className="block text-[11px]">{b.prorata_note}</span>}</span>
                <span className="whitespace-nowrap">{signe(b.prorata)}</span>
              </div>
            )}
            {Math.abs(b.accumulation) > 0.005 && (
              <div className="flex justify-between gap-3 text-sm py-1" style={{ color: "#065F46" }}
                data-testid="modale-ligne-accumulation">
                <span>{b.accumulation_libelle}
                  {b.accumulation_detail && <span className="block text-[11px] opacity-80">{b.accumulation_detail}</span>}</span>
                <span className="whitespace-nowrap">{signe(b.accumulation)}</span>
              </div>
            )}
            {Math.abs(b.costume) > 0.005 && (
              <div className="flex justify-between gap-3 text-sm py-1 text-[var(--muted)]">
                <span>Costume de spectacle</span>
                <span className="whitespace-nowrap">{signe(b.costume)}</span>
              </div>
            )}
            <div className="flex justify-between gap-3 text-sm font-medium pt-1" data-testid="modale-sous-total-eleve">
              <span>Sous-total {b.eleve}</span>
              <span className="whitespace-nowrap">{argent(b.sous_total)}</span>
            </div>
          </div>
        ))}
        {cartes.length > 0 && (
          <div className="mt-3 rounded-lg px-3 py-2 text-xs" style={{ background: "#FEF3C7", color: "#92400E" }}
            data-testid="modale-cartes10">
            <strong>Hors facture — à encaisser au comptoir</strong> (Cash / espèces › Cartes 10 cours) :{" "}
            {cartes.map((c) => `${c.designation}${parEleve.length > 1 || c.eleve ? ` (${c.eleve})` : ""} — ${argent(c.montant)}${c.taxe ? ` dont ${argent(c.taxe)} de taxe` : ""}`).join(" · ")}
          </div>
        )}
        {/* Puis la famille : total des cours, réductions (qui fait la réduction famille, et
            pourquoi les autres n'y comptent pas), taxe, prorata, rabais, bons. */}
        {recap.famille.length > 0 && (
          <div className="mt-4 pt-1" data-testid="modale-decompte">
            {recap.famille.map((l, k) => (
              <div key={k} className="py-0.5" data-testid={`modale-famille-${l.cle}`}>
                <div className="flex justify-between gap-3 text-sm">
                  <span className={l.cle === "cours" ? "" : "text-[var(--muted)]"}>{l.libelle}</span>
                  <span className="whitespace-nowrap">{l.cle === "cours" ? argent(l.montant) : signe(l.montant)}</span>
                </div>
                {l.note && <div className="text-[11px] text-[var(--muted)]">Ne compte pas — {l.note}</div>}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between items-baseline gap-3 mt-3 pt-3 border-t font-semibold"
          style={{ borderColor: "var(--border)" }} data-testid="modale-total-poste">
          <span>{montantsChanges ? "Nouveau total" : "Total de la facture"}</span>
          <span className="text-lg">
            {montantsChanges && !apercu && "calcul…"}
            {montantsChanges && apercu && !apercu.ok && <span style={{ color: "#B91C1C" }} className="text-sm">impossible — {apercu.erreur}</span>}
            {(!montantsChanges || apercu?.ok) && argent(totalAffiche)}
          </span>
        </div>
        {montantsChanges && apercu?.ok && Math.abs((apercu.total || 0) - (facture.total || 0)) > 0.005 && (
          <div className="text-xs text-right text-[var(--muted)]">au lieu de {argent(facture.total)}</div>
        )}
        {!montantsChanges && facture.gelee && Math.abs((recap.total ?? facture.total) - (facture.total || 0)) > 0.005 && (
          <div className="text-xs text-right text-[var(--muted)]" data-testid="modale-total-gele">
            montant de la facture envoyée — le calcul du jour ci-dessus donne {argent(recap.total)}
          </div>
        )}
        {(facture.dette_saisons_passees || 0) > 0.02 && (
          <div className="rounded-lg px-3 py-2 mt-2 text-xs" data-testid="modale-dette-saisons-passees"
            style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
            <strong>Encore dû d'une saison passée : {argent(facture.dette_saisons_passees)}</strong>
            {facture.dette_saisons_passees_detail ? ` (${facture.dette_saisons_passees_detail})` : ""}.
            {" "}Ce montant reste sur sa facture d'origine (À traiter › Impayés des saisons
            précédentes) : il n'est pas ajouté à celle-ci.
          </div>
        )}
        <p className="text-[11px] text-[var(--muted)] mt-2">
          Prix des cours, prorata, réductions accumulation et famille, taxe et costume sont
          calculés par la console d'après les Réglages. Pour une autre réduction : « Rabais » ci-dessous.
        </p>
        {(bonsDeduits.length + bonsALaMain.length + bonsReportes.length) > 0 && (
          <div className="rounded-lg px-3 py-2 mt-2 text-xs" data-testid="modale-parrainages"
            style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46" }}>
            <strong>Bons de parrainage</strong> — déduits par la console, rien à saisir (surtout pas
            dans « Ajustement » : la déduction serait comptée deux fois).
            <ul className="mt-1 space-y-0.5">
              {bonsDeduits.map((b, k) => <li key={`d${k}`}>Parrainage de {b.filleul} · − {argent(Number(b.montant || 0))} — déduit de cette facture</li>)}
              {bonsALaMain.map((b, k) => <li key={`m${k}`}>Parrainage de {b.filleul} · − {argent(Number(b.montant || 0))} — déjà déduit à la main dans « Ajustement »</li>)}
              {bonsReportes.map((b, k) => <li key={`r${k}`} style={{ color: "#B45309" }}>Parrainage de {b.filleul} · − {argent(Number(b.montant || 0))} — facture déjà partie : à créditer au semestre suivant</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ── 2. Ce que l'école peut décider ──────────────────────────────────── */}
      <div className="rounded-xl p-4 mb-4" style={{ background: "#fff", border: "1px solid var(--border)" }}
        data-testid="modale-decisions">
        <div className={`${titreBloc} mb-3`}>2 · Ce que tu peux décider — pour toute la facture</div>

        {ecartPrix > 0 && (
          <p className="text-xs mb-3 rounded-lg px-3 py-2" data-testid="modale-prix-special"
            style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
            Prix spécial convenu : la famille a versé {argent(facture.recu)} sur {argent(facture.total)}.
            Le rabais proposé (−{argent(ecartPrix)}) ramène la facture à ce montant — ajuste-le si le
            prix convenu est différent, puis enregistre.
          </p>
        )}
        {porteurRabais && (
          <div className="grid sm:grid-cols-2 gap-x-4" data-testid="modale-taxes-reductions">
            <ChampTexte label="Rabais (geste commercial)" valeur={porteurRabais.rabais || ""} testid="champ-rabais"
              onChange={(v) => maj(porteurRabais.id, { rabais: v })}
              aide="en négatif (−20) — sa propre ligne sur la facture" />
            <ChampTexte label="Motif du rabais" valeur={porteurRabais.motif_rabais || ""} testid="champ-motif-rabais"
              onChange={(v) => maj(porteurRabais.id, { motif_rabais: v })} aide="imprimé sur la facture" />
          </div>
        )}
        {porteurAjustement && (
          <div className="grid sm:grid-cols-2 gap-x-4">
            <ChampTexte label="Ajustement" valeur={porteurAjustement.ajustement || ""} testid="champ-ajustement"
              onChange={(v) => maj(porteurAjustement.id, { ajustement: v })}
              aide={toutAnnule
                ? "tout est annulé mais la famille doit quelque chose ? le montant convenu, en positif"
                : "+ pour faire payer un montant convenu, − pour corriger une erreur"} />
            <ChampTexte label="Motif de l'ajustement" valeur={porteurAjustement.motif_ajustement || ""} testid="champ-motif-ajustement"
              onChange={(v) => maj(porteurAjustement.id, { motif_ajustement: v })} aide="imprimé sur la facture" />
          </div>
        )}
        {!porteurAjustement && (
          <p className="text-xs mb-3" style={{ color: "#B45309" }}>
            Cette facture porte des ajustements sur plusieurs inscriptions : ils se modifient
            chacun dans « Cas rares » ci-dessous.
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-x-4">
          {facturees.map((p) => (
            <ChampChoix key={p.id}
              label={facturees.length > 1 ? `Taxe administrative — ${elevesDuPoste(p)}` : "Taxe administrative"}
              valeur={String(nombre(p.taxe_admin))} testid="champ-taxe"
              options={[[0, "Non — pas de taxe"], [20, "Oui — 20 CHF"],
                ...([0, 20].includes(nombre(p.taxe_admin)) ? [] : [[nombre(p.taxe_admin), `${nombre(p.taxe_admin)} CHF`]])]
                .map(([v, l]) => ({ valeur: String(v), label: l }))}
              onChange={(v) => maj(p.id, { taxe_admin: v })}
              aide={taxeDecidee(p) ? `décision de l'école (calculée : ${argent(nombre(orig(p).taxe_calculee ?? orig(p).taxe_admin))})`
                : nombre(orig(p).taxe_calculee ?? orig(p).taxe_admin) > 0
                  ? "calculée : nouvel élève, une seule fois dans sa vie"
                  : "calculée : aucune (déjà inscrit·e, ou payée avec la carte 10 cours)"} />
          ))}
          {/* Deux modes (20.10.2026, « en gros on a cash ou facture ») : « TWINT » n'a
              jamais servi sur une facture et « vide » voulait dire la même chose que
              « Virement ». Une famille habituée du comptoir (mode vide repris d'un autre
              semestre) s'affiche « Cash ». */}
          <ChampChoix label="Mode de paiement" valeur={cash ? "Cash" : "Virement"}
            testid="champ-mode-paiement"
            options={[{ valeur: "Virement", label: "Facture QR (virement)" },
                      { valeur: "Cash", label: "Cash — au comptoir, sans facture" }]}
            onChange={(v) => setPostes((liste) => liste.map((p) => ({ ...p, mode_paiement: v })))}
            aide={cash && depuisACorriger
              ? "au comptoir : le montant est validé sans facture QR et s'encaisse dans Cash / espèces. « Facture QR » : la console refait la facture QR, rien ne part sans toi"
              : cash
              ? "au comptoir : aucune facture envoyée. « Facture QR » : la facture repart dans « À générer » (ce qui est reçu reste compté)"
              : "« Cash » : la facture quitte le circuit (plus d'envoi ni de rappel) et passe dans Cash / espèces"} />
        </div>

        {/* Encaissement : en espèces, saisi ici ; par banque, rapproché depuis le relevé. */}
        {postes.filter((p) => facturees.includes(p) || nombre(orig(p).montant_recu)).map((p) => (
          (cash || correction[p.id]) ? (
            <div key={p.id} className="grid sm:grid-cols-2 gap-x-4">
              <ChampTexte label={`Montant reçu${postes.length > 1 ? ` — ${elevesDuPoste(p)}` : ""}`}
                valeur={p.montant_recu} onChange={(v) => maj(p.id, { montant_recu: v })} testid="champ-montant-recu"
                aide="un montant reçu passe la facture en payée" />
              <ChampTexte label="Date du paiement" type="date" valeur={p.date_paiement}
                onChange={(v) => maj(p.id, { date_paiement: v })} testid="champ-date-paiement" />
              {correction[p.id] && (
                <div className="sm:col-span-2">
                  <ChampTexte label="Motif de la correction" valeur={motifs[p.id] || ""}
                    onChange={(v) => setMotifs({ ...motifs, [p.id]: v })} testid="champ-motif-paiement"
                    aide="obligatoire — conservé dans l'historique de la facture" />
                  {err && (motifs[p.id] || "").trim().length < 3 && (
                    <p className="text-sm -mt-2 mb-3" role="alert" style={{ color: "#B91C1C" }} data-testid="erreur-motif">{err}</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div key={p.id} className="rounded-xl px-4 py-3 mb-4" style={{ background: "#F1F5F9", border: "1px solid var(--border)" }}
              data-testid="paiement-verrouille">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] flex items-center gap-1.5">
                <Lock size={11} /> Encaissement{postes.length > 1 ? ` — ${elevesDuPoste(p)}` : ""} — depuis le relevé bancaire
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
                <span className="text-sm">
                  {p.montant_recu ? `${argent(nombre(p.montant_recu))} reçus` : "rien reçu pour l'instant"}
                  {p.date_paiement ? ` · le ${jourFr(p.date_paiement)}` : ""}
                </span>
                <button className="btn-ghost" data-testid="paiement-corriger" style={{ padding: "6px 10px", fontSize: 12 }}
                  onClick={() => setCorrection({ ...correction, [p.id]: true })}>
                  <Pencil size={12} /> Corriger manuellement
                </button>
              </div>
            </div>
          )
        ))}

        {porteurNotes ? (
          <ChampTexte label="Notes internes" valeur={porteurNotes.notes || ""} testid="champ-notes-facturation"
            onChange={(v) => maj(porteurNotes.id, { notes: v })}
            aide="pas sur la facture · « BV Noémie » = bulletin de Noémie · « à facturer séparément » = sort du programme" />
        ) : null}
      </div>

      {/* ── 3. Cas rares (replié) ───────────────────────────────────────────── */}
      <div className="rounded-xl mb-4" style={{ border: "1px dashed var(--border)" }} data-testid="modale-cas-rares">
        <button type="button" onClick={() => setAvance((o) => !o)} aria-expanded={avance}
          className="w-full flex items-center gap-2 px-4 py-3 text-left text-xs font-semibold text-[var(--muted)]"
          data-testid="modale-cas-rares-ouvrir">
          3 · Cas rares — forcer le prix des cours ou le prorata d'une inscription
          <span className="ml-auto">{avance ? "▴" : "▾"}</span>
        </button>
        {avance && (
          <div className="px-4 pb-4 space-y-4">
            <p className="text-xs text-[var(--muted)]">
              Laisse vide pour garder le calcul de la console. Un montant écrit ici est une décision
              de l'école : la console ne le recalcule plus.
            </p>
            {facturees.map((p) => (
              <div key={p.id} className="rounded-lg p-3" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}
                data-testid="modale-poste">
                <div className="text-sm font-semibold mb-0.5" data-testid="modale-poste-titre">{elevesDuPoste(p)}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--muted)] mb-2">demande {p.demande}</div>
                <div className="grid sm:grid-cols-2 gap-x-4">
                  <ChampTexte label="Prix des cours" valeur={p.total_cours} testid="champ-total-cours"
                    onChange={(v) => { maj(p.id, { total_cours: v }); setTotalTouche({ ...totalTouche, [p.id]: true }); }}
                    aide={`calculé : ${argent(p.total_cours_calcule || 0)}${orig(p).total_cours_manuel ? ` · décidé à la main : ${argent(nombre(orig(p).total_cours))}` : ""}`} />
                  <ChampTexte label="Prorata" valeur={p.prorata} testid="champ-prorata"
                    onChange={(v) => { maj(p.id, { prorata: v }); setProrataTouche({ ...prorataTouche, [p.id]: true }); }}
                    aide={`calculé : ${signe(nombre(p.prorata_calcule))}${p.prorata_note ? ` — ${p.prorata_note}` : ""} · 0 = pas de prorata`} />
                  {!porteurAjustement && (
                    <>
                      <ChampTexte label="Ajustement" valeur={p.ajustement || ""} testid="champ-ajustement"
                        onChange={(v) => maj(p.id, { ajustement: v })} />
                      <ChampTexte label="Motif de l'ajustement" valeur={p.motif_ajustement || ""} testid="champ-motif-ajustement"
                        onChange={(v) => maj(p.id, { motif_ajustement: v })} aide="imprimé sur la facture" />
                    </>
                  )}
                  {!porteurRabais && (
                    <>
                      <ChampTexte label="Rabais" valeur={p.rabais || ""} testid="champ-rabais"
                        onChange={(v) => maj(p.id, { rabais: v })} />
                      <ChampTexte label="Motif du rabais" valeur={p.motif_rabais || ""} testid="champ-motif-rabais"
                        onChange={(v) => maj(p.id, { motif_rabais: v })} />
                    </>
                  )}
                  {!porteurNotes && (
                    <div className="sm:col-span-2">
                      <ChampTexte label="Notes internes" valeur={p.notes || ""} testid="champ-notes-facturation"
                        onChange={(v) => maj(p.id, { notes: v })} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {err && !postes.some((p) => correction[p.id]) && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{err}</p>}
      <CasParticuliers facture={facture} onOk={onOk} />
      {depuisACorriger && (
        <p className="text-xs text-[var(--muted)] mt-3" data-testid="modale-montants-validation">
          Facture à corriger : en validant, la console refait la facture corrigée (même numéro,
          nouveau QR-code) et la range dans « Générées, à envoyer ». Rien ne part tout seul : tu
          la relis, puis tu cliques « Envoyer ».
        </p>
      )}

      {/* Enregistrer / Annuler : en bas de la fenêtre, avec le total (pas collés à l'écran,
          ils passaient par-dessus les champs). */}
      <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <BoutonSombre onClick={enregistrer} disabled={envoi || (rienAChanger && !depuisACorriger)}
          testid="modale-montants-enregistrer">
          {envoi ? "Enregistrement…"
            : !depuisACorriger ? "Enregistrer"
            : rienAChanger ? "Valider la correction" : "Enregistrer et valider la correction"}
        </BoutonSombre>
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        <span className="ml-auto text-sm font-semibold" data-testid="modale-total-pied">
          {montantsChanges ? (apercu?.ok ? `Nouveau total : ${argent(apercu.total)}` : "Nouveau total : calcul…")
            : `Total : ${argent(facture.total)}`}
        </span>
      </div>
    </Modale>
  );
}

/* Une facture bloquée, expliquée : ce qui a été facturé, pourquoi c'est faux, et la
   correction chiffrée que l'école valide d'un clic. */
/* Les actions d'une ligne de facture : la plus utile en clair, le reste replié.
   Avant, les huit boutons empilés élargissaient le tableau et obligeaient à faire
   défiler vers la droite pour les atteindre. */
const ActionsFacture = ({ actions }) => (
  /* Tous les boutons visibles, comme l'école le demande. La largeur reste bornée pour que
     le tableau ne parte pas hors de l'écran : les boutons passent à la ligne. */
  <div className="flex flex-wrap gap-1 justify-end">
    {actions.map((a) => (
      <button key={a.id} className="btn-ghost" data-testid={a.id} title={a.title}
        style={{ padding: "6px 10px", fontSize: 12, gap: 5 }}
        disabled={a.disabled} onClick={a.onClick}>
        <a.Icon size={12} /> {a.label}
        {a.alerte && <AlertTriangle size={12} style={{ color: "#B45309" }} />}
      </button>
    ))}
  </div>
);

/* Les montants d'une facture à corriger, TOUS sur la même base : après les bons de
   parrainage que la console déduit en fabriquant la facture. L'en-tête montrait le total
   AVANT ces bons (870) pendant que la phrase rouge et le décompte parlaient du total
   APRÈS (810 reçus, 820 aujourd'hui) : trois chiffres pour une seule facture (école,
   09.10.2026). « avant » = ce que la famille a entre les mains si une version est partie,
   sinon le total actuel de la facture. */
function totauxCorrection(f) {
  const c = f.correction;
  const arrondi = (x) => Math.round(x * 100) / 100;
  const bons = (f.parrainages || []).filter((b) => Math.abs(b.montant || 0) > 0.001);
  const totalBons = -bons.reduce((t, b) => t + Math.abs(b.montant || 0), 0);
  const recu = (f.total_version_envoyee || 0) > 0.001 && f.version_perimee;
  const nouveau = c ? arrondi(c.total_apres + totalBons) : arrondi(f.total || 0);
  const avant = recu ? arrondi(f.total_version_envoyee)
    : c ? arrondi(c.total_avant + totalBons) : arrondi(f.total || 0);
  return { bons, totalBons, recu, avant, nouveau, ecart: arrondi(nouveau - avant) };
}

// Un des trois chiffres en tête d'une facture à corriger.
const ChiffreCorrection = ({ titre, montant, couleur, barre, testid, children }) => (
  <div className="rounded-xl px-4 py-3 flex-1 min-w-[150px]"
    style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
    <div className="text-xs text-[var(--muted)]">{titre}</div>
    <div className="font-display text-2xl" data-testid={testid}
      style={{ color: couleur, textDecoration: barre ? "line-through" : "none", opacity: barre ? 0.7 : 1 }}>
      {montant}
    </div>
    {children}
  </div>
);

function CarteCorrection({ facture: f, occupe, onAccepter, onModifier, onRefaire, onChoisir }) {
  const c = f.correction;
  const [passe, setPasse] = useState([]);

  useEffect(() => {
    if (f.genre === "stage") return;
    api(`/facturation/corrections/historique?cle=${encodeURIComponent(f.cle)}`
      + `&semestre=${f.semestre || ""}`)
      .then((d) => setPasse(d.items || [])).catch(() => setPasse([]));
  }, [f.cle, f.semestre, f.genre]);

  const quand = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-CH",
      { day: "2-digit", month: "2-digit", year: "2-digit" });
  };

  // Une réduction (accumulation, famille, bon de parrainage, prorata) se comprend au
  // niveau de la famille : on additionne celles de tous les postes, libellé par libellé.
  // Une facture peut être à corriger SANS proposition chiffrée (corrigée à la main après
  // son envoi) : la carte doit tenir debout sans `correction`, sinon la liste entière
  // disparaissait de l'écran.
  const reductions = Object.entries((c?.postes || []).flatMap((p) => p.autres || [])
    .reduce((acc, a) => ({ ...acc, [a.libelle]: (acc[a.libelle] || 0) + a.montant }), {}))
    .map(([libelle, montant]) => ({ libelle, montant: Math.round(montant * 100) / 100 }))
    .filter((a) => a.montant !== 0);
  const totalCours = (c?.postes || []).reduce((t, p) => t + (p.sous_total_apres || 0), 0);
  const totalCoursAvant = (c?.postes || []).reduce((t, p) => t + (p.sous_total_avant || 0), 0);
  /* Bons de parrainage réellement déduits sur cette facture. Un bon venu d'un semestre
     précédent (`reporte`) est déduit lui aussi : la console le reprend sur la facture
     suivante, il ne doit pas manquer du total affiché ici. */
  const { bons, totalBons, recu, avant, nouveau, ecart } = totauxCorrection(f);
  const totalReductions = c ? Math.round((c.total_apres - totalCours) * 100) / 100 : 0;
  const lignesReductions = [...reductions,
    ...bons.map((b) => ({ libelle: `Bon de parrainage${b.filleul ? ` — ${b.filleul}` : ""}`,
      montant: -Math.abs(b.montant || 0), bon: true }))];

  // UN geste pour clore la correction. « Accepter la correction » (montants proposés) et
  // « Confirmer la correction » (refaire la facture) faisaient la même chose du point de
  // vue de l'école : la facture refaite rejoint « Générées, à envoyer », rien ne part seul.
  const proposition = c && Math.abs(c.ecart || 0) > 0.001;
  const peutRefaire = f.genre !== "stage" && f.statut !== "bloquee";
  const valider = proposition ? onAccepter : peutRefaire ? onRefaire : null;
  /* Une famille au comptoir (mode « Cash ») : « Valider » refaisait une facture QR que la
     console refuse, et la facture restait « À corriger » (F-2026S1-0186, 02.11.2026). La
     carte dit ce qui va se passer et propose les deux vrais choix, chacun en un geste. */
  const auComptoir = f.genre !== "stage" && !!f.especes && !!valider && !!onChoisir;
  const resteComptoir = Math.max(Math.round((nouveau - (f.recu || 0)) * 100) / 100, 0);
  // Une facture QR existe déjà (reçue ou fabriquée) : elle sera refaite, même numéro.
  const qrDeja = !!(recu || f.renvoi || f.date_envoi_precedent || f.pdf || f.copie_numero);
  const enCours = auComptoir ? occupe === "correction"
    : occupe === (proposition ? "correction" : "generation");

  return (
    <article className="bg-white rounded-2xl border p-5" data-testid="carte-correction"
      style={{ borderColor: "#FECACA" }}>
      <div className="min-w-0">
        <div className="font-semibold">{f.client}</div>
        <div className="text-xs text-[var(--muted)]">
          {f.email} · n° {f.numero || "à créer"}
          {f.envoyee_le ? ` · envoyée le ${jourFr(f.envoyee_le)}`
            : f.copie_date ? ` · une version est partie le ${jourFr(f.copie_date)}`
              : " · pas encore envoyée"}
        </div>
      </div>

      {/* Les trois chiffres qui comptent, dans l'ordre où on les lit : ce que la famille
          a (ou ce qui est facturé), la facture corrigée, la différence. */}
      <div className="flex flex-wrap items-stretch gap-2 mt-4" data-testid="correction-chiffres">
        <ChiffreCorrection titre={recu ? "Reçue par la famille" : "Facture actuelle"} montant={argent(avant)}
          barre={Math.abs(ecart) > 0.001} testid="correction-avant">
          {/* La copie reçue par la famille : le dernier PDF réellement parti, qu'il vienne
              de la console, d'une version corrigée ou de l'archive importée. */}
          {f.copie_numero && (
            <button className="text-xs underline text-[var(--muted)] mt-1 flex items-center gap-1"
              data-testid="correction-pdf-envoye"
              title={`Le PDF réellement reçu par la famille — ${f.copie_total} CHF`}
              onClick={() => ouvrirPdf(
                f.copie_source === "console"
                  ? `/facturation/pdf/${encodeURIComponent(f.copie_numero)}`
                  : f.copie_source === "version"
                    ? `/facturation/version/${encodeURIComponent(f.copie_numero)}/pdf`
                    : `/factures/archive/pdf?numero=${encodeURIComponent(f.copie_numero)}`,
                `Facture envoyée ${f.copie_numero} — ${f.client}`)}>
              <Eye size={12} /> Voir la facture reçue{f.copie_date ? ` (${jourFr(f.copie_date)})` : ""}
            </button>
          )}
        </ChiffreCorrection>
        <ChiffreCorrection titre="Facture corrigée" montant={argent(nouveau)} couleur="#15803D"
          testid="correction-total">
          {/* Le PDF recalculé À L'INSTANT : le dernier PDF enregistré porte encore
              l'ancien montant tant qu'on n'a pas régénéré. */}
          <button className="text-xs underline text-[var(--muted)] mt-1 flex items-center gap-1"
            data-testid="correction-pdf-actuel"
            onClick={() => ouvrirPdf(
              `/facturation/apercu-pdf?cle=${encodeURIComponent(f.cle)}&semestre=${f.semestre || 1}&saison=${encodeURIComponent(f.saison || "")}`,
              `Facture à jour — ${f.client}`)}>
            <Eye size={12} /> Voir la facture corrigée
          </button>
        </ChiffreCorrection>
        <ChiffreCorrection titre="Différence" testid="correction-ecart"
          couleur={Math.abs(ecart) < 0.001 ? "var(--muted)" : ecart > 0 ? "#B45309" : "#15803D"}
          montant={Math.abs(ecart) < 0.001 ? "aucune" : `${ecart > 0 ? "+" : "−"} ${argent(Math.abs(ecart))}`}>
          <div className="text-xs text-[var(--muted)] mt-1">
            {Math.abs(ecart) < 0.001 ? "même montant, facture à refaire"
              : ecart > 0 ? "la famille doit en plus" : "en moins pour la famille"}
          </div>
        </ChiffreCorrection>
      </div>

      <div className="rounded-xl px-4 py-3 mt-3 text-sm" style={{ background: "#FEF2F2", color: "#991B1B" }}
        data-testid="correction-probleme">
        <strong>Pourquoi :</strong>
        <ul className="mt-1 space-y-1" data-testid="correction-causes">
          {(c?.causes || []).length > 0
            ? c.causes.map((cause, k) => <li key={k}>· {cause}</li>)
            : <li>· {f.a_corriger_pourquoi || f.motif || "montants à revoir"}</li>}
        </ul>
        {/* Au comptoir, le motif n'est que « réglée en espèces » : le bloc des choix le dit
            déjà, mieux. Un vrai contrôle (statut « bloquee ») reste affiché. */}
        {f.motif && !(auComptoir && f.statut !== "bloquee") && (
          <div className="text-xs mt-2" style={{ opacity: 0.8 }}>Contrôle de la console : {f.motif}</div>
        )}
      </div>

      {/* La facture corrigée, ligne par ligne, dans l'ordre de la facture : les cours, puis
          la taxe et les réductions (elles valent pour toute la famille), puis le total —
          le même que « Facture corrigée » en haut. */}
      {c && (
        <div className="mt-3 rounded-xl border p-4" style={{ borderColor: "var(--border)" }}
          data-testid="correction-reductions">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)] mb-2">
            La facture corrigée, ligne par ligne
          </div>
          <ul className="text-sm space-y-1" data-testid="correction-cours">
            {c.postes.flatMap((p) => p.cours).map((l, k) => (
              <li key={k} className="flex gap-3">
                <span>{l.eleve} — {l.cours}
                  {l.origine && (
                    <span className="text-xs text-[var(--muted)]" data-testid="correction-origine">
                      {" · "}{l.origine}{l.origine_le ? ` le ${l.origine_le.split("-").reverse().join(".")}` : ""}
                    </span>
                  )}
                  {/* Pourquoi ce cours commencé en octobre n'a pas de prorata. */}
                  {l.suite && (
                    <span className="block text-xs" style={{ color: "#0F766E" }}
                      data-testid="correction-suite">{l.suite}</span>
                  )}
                </span>
                <span className="ml-auto font-semibold whitespace-nowrap">{argent(l.montant)}</span>
              </li>
            ))}
            {c.postes.every((p) => p.cours.length === 0) && (
              <li className="text-[var(--muted)]">Aucun cours actif.</li>
            )}
            {/* Un cours retiré est DÉJÀ hors du total recalculé : on l'affiche comme une
                information, avec le nom de son élève (cas Messina). */}
            {c.postes.flatMap((p) => p.retires).map((r, k) => (
              <li key={`r${k}`} className="flex gap-3" style={{ color: "#B45309" }}>
                <span>Quitté : {r.eleve ? `${r.eleve} — ` : ""}{r.cours} — {r.motif}</span>
                <span className="ml-auto whitespace-nowrap">{argent(r.montant)} déjà hors du total</span>
              </li>
            ))}
          </ul>
          <div className="text-sm mt-2 pt-2 border-t flex gap-3" style={{ borderColor: "var(--border)" }}>
            <span className="text-[var(--muted)]">Total des cours</span>
            <span className="ml-auto font-semibold whitespace-nowrap">
              {Math.abs(totalCoursAvant - totalCours) > 0.001 && (
                <span className="text-xs text-[var(--muted)] font-normal mr-2"
                  style={{ textDecoration: "line-through" }}>{argent(totalCoursAvant)}</span>
              )}
              {argent(totalCours)}
            </span>
          </div>

          {/* Les bons de parrainage sont déduits par la console au moment de fabriquer la
              facture : sans leur ligne, le total d'ici dépassait celui de la facture. */}
          <ul className="text-sm mt-3 space-y-1">
            {lignesReductions.map((a, k) => (
              <li key={k} className="flex gap-3" data-testid={a.bon ? "correction-bon" : undefined}>
                <span>{a.libelle}</span>
                <span className="ml-auto whitespace-nowrap">{argent(a.montant)}</span>
              </li>
            ))}
            {lignesReductions.length === 0 && (
              <li className="text-[var(--muted)]">Aucune taxe, réduction ni bon.</li>
            )}
          </ul>
          {lignesReductions.length > 0 && (
            <div className="text-sm mt-2 pt-2 border-t flex gap-3" style={{ borderColor: "var(--border)" }}>
              {/* Additionne des CHARGES (taxe) et des DÉDUCTIONS (réductions, bons) : son
                  nom dit les deux. */}
              <span className="text-[var(--muted)]">Total réductions / taxe</span>
              <span className="ml-auto font-semibold whitespace-nowrap">{argent(totalReductions + totalBons)}</span>
            </div>
          )}
          <div className="text-base mt-2 pt-2 border-t flex gap-3" style={{ borderColor: "var(--border)" }}>
            <strong>Total à facturer</strong>
            <strong className="ml-auto whitespace-nowrap" style={{ color: "#15803D" }}>{argent(nouveau)}</strong>
          </div>
        </div>
      )}

      {passe.length > 0 && (
        <details className="mt-3 text-sm" data-testid="correction-historique">
          <summary className="cursor-pointer text-[var(--muted)]">
            Déjà fait sur cette facture ({passe.length})
          </summary>
          <ul className="mt-1 space-y-0.5 text-[var(--muted)]">
            {passe.slice(0, 6).map((p, k) => (
              <li key={k}>
                {quand(p.le)} · {p.par} — {p.quoi}
                {p.avant || p.apres ? ` : ${argent(p.avant)} → ${argent(p.apres)}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}

      {auComptoir && (
        <div className="mt-4 rounded-xl px-4 py-3" data-testid="correction-comptoir"
          style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <div className="text-sm font-semibold" style={{ color: "#92400E" }}>
            Cette famille paie au comptoir (mode « {f.especes_annoncees || "Cash"} »).
            Comment règle-t-elle {argent(nouveau)} ?
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div className="rounded-xl bg-white border p-3 flex flex-col gap-2" style={{ borderColor: "var(--border)" }}>
              <div className="text-sm font-semibold">Elle paie au comptoir</div>
              <p className="text-xs text-[var(--muted)] flex-1">
                {resteComptoir > 0.02
                  ? `Sans facture QR : elle passe dans Cash / espèces, ${argent(resteComptoir)} à encaisser.`
                  : "Sans facture QR : déjà réglée, elle passe dans « Payées »."}
                {recu ? ` La facture de ${argent(avant)} reçue${f.copie_date ? ` le ${jourFr(f.copie_date)}` : ""} reste dans l'historique.` : ""}
              </p>
              <BoutonSombre onClick={() => onChoisir("especes")} disabled={enCours}
                testid="correction-comptoir-valider">
                {enCours ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                {resteComptoir > 0.02 ? `Valider ${argent(nouveau)} — à encaisser au comptoir`
                  : `Valider ${argent(nouveau)} — déjà réglée au comptoir`}
              </BoutonSombre>
            </div>
            <div className="rounded-xl bg-white border p-3 flex flex-col gap-2" style={{ borderColor: "var(--border)" }}>
              <div className="text-sm font-semibold">Elle paie par virement</div>
              <p className="text-xs text-[var(--muted)] flex-1">
                Elle passe en « Facture QR (virement) »
                {qrDeja ? " : la facture est refaite (même numéro, nouvelle version) et attend dans « Générées, à envoyer »."
                  : " : sa facture attend dans « À générer »."}
                {" "}Rien ne part sans toi.
              </p>
              <button className="btn-ghost justify-center" onClick={() => onChoisir("qr")} disabled={enCours}
                data-testid="correction-comptoir-qr">
                <Receipt size={13} />
                {qrDeja ? `Passer en facture QR et refaire la facture (${argent(nouveau)})`
                  : `Passer en facture QR (${argent(nouveau)})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        {valider && !auComptoir && (
          <BoutonSombre onClick={valider} disabled={enCours}
            testid={proposition ? "correction-accepter" : "correction-refaire"}>
            {enCours ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Valider la facture corrigée ({argent(nouveau)})
          </BoutonSombre>
        )}
        <button className="btn-ghost" onClick={onModifier} data-testid="correction-modifier">
          <Pencil size={13} /> Corriger à la main
        </button>
      </div>
      <p className="text-xs text-[var(--muted)] mt-2">
        {valider && !auComptoir ? "« Valider » refait la facture à ce montant et la range dans « Générées, à envoyer » : "
          + "rien ne part sans toi, l'ancienne version reste dans l'historique. " : ""}
        {auComptoir ? "« Corriger à la main » : tu changes toi-même un montant (rabais, ajustement…) ou le mode de paiement."
          : "« Corriger à la main » : tu changes toi-même un montant (rabais, ajustement…)."}
      </p>
    </article>
  );
}

/* L'extrait comptable : un client par ligne, à classer avec la déclaration d'impôts.
   Les paiements en espèces peuvent être écartés pour ne garder que la banque. */
function ModaleExtrait({ onFermer }) {
  const [periode, setPeriode] = useState("annee");
  const [sansEspeces, setSansEspeces] = useState(false);
  const [seulementEncaissees, setSeulementEncaissees] = useState(false);
  const [detail, setDetail] = useState(true);
  const [avecStages, setAvecStages] = useState(true);
  const [occupe, setOccupe] = useState("");
  const [err, setErr] = useState("");

  const sortir = async (format) => {
    setOccupe(format);
    setErr("");
    try {
      const p = new URLSearchParams({
        format, periode, exclure_especes: String(sansEspeces),
        seulement_encaissees: String(seulementEncaissees), detail: String(detail),
        stages: String(avecStages),
      });
      await telecharger(`/facturation/export?${p}`,
        `1000ARTS_extrait_${periode}.${format === "xlsx" ? "xlsx" : "pdf"}`);
    } catch (e) {
      setErr(e.message);
    }
    setOccupe("");
  };

  return (
    <Modale titre="Extrait pour la déclaration d'impôts"
      sous_titre="Une facture par ligne (cours et stages) : total facturé, encaissé, solde et date d'encaissement."
      onFermer={onFermer} testid="modale-extrait">
      <ChampChoix label="Période" valeur={periode} testid="extrait-periode"
        options={[{ valeur: "annee", label: "Année complète (semestres 1 et 2)" },
          { valeur: "1", label: "Semestre 1" }, { valeur: "2", label: "Semestre 2" }]}
        onChange={setPeriode} />
      <div className="space-y-3 mb-5">
        {[["extrait-sans-especes", sansEspeces, setSansEspeces,
           "Écarter les paiements en espèces", "Ne garder que ce qui est passé en banque."],
          ["extrait-encaissees", seulementEncaissees, setSeulementEncaissees,
           "Uniquement les factures encaissées", "Les factures encore impayées sont laissées de côté."],
          ["extrait-stages", avecStages, setAvecStages,
           "Inclure les stages", "Chaque stage compte dans le semestre où il a lieu (automne : S1 ; Pâques, été : S2)."],
          ["extrait-detail", detail, setDetail,
           "Inclure le détail des cours par élève", "Une colonne de plus, avec le cours et son montant."],
        ].map(([testid, valeur, setter, titre, aide]) => (
          <label key={testid} className="flex items-start gap-3 cursor-pointer"
            onClick={() => setter(!valeur)}>
            <Case cochee={valeur} onChange={() => setter(!valeur)} testid={testid} />
            <span className="text-sm leading-tight">
              {titre}
              <span className="block text-xs text-[var(--muted)]">{aide}</span>
            </span>
          </label>
        ))}
      </div>
      {err && <p className="text-sm mb-3" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex flex-wrap gap-2">
        <BoutonSombre onClick={() => sortir("xlsx")} disabled={!!occupe} testid="extrait-excel">
          {occupe === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
        </BoutonSombre>
        <button className="btn-ghost" onClick={() => sortir("pdf")} disabled={!!occupe} data-testid="extrait-pdf">
          {occupe === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
        </button>
      </div>
    </Modale>
  );
}
